import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx/xlsx.mjs";
import { importExcelAsTemplate, exportReportAsExcelClone, exportReportAsExcelRaw, exportReportAsExcelPDF, buildExcelPreviewHtml, buildExcelEditableHtml, evalFormulasWithReport } from "./ExcelImporter";
import { parseChartsForSheet, ExcelChartPanel, chartConfigToDataUrl } from "./ExcelChartRenderer";
import { buildProctorTemplate, exportProctorToExcel, exportProctorToPDF, PROCTOR_FIELDS, computeTrials, ProctorPreview } from "./ProctorTemplate";
import ProctorChart, { generateProctorChartImage } from "./ProctorChart";
import { importPdfAsTemplate, exportFilledPdf, getFilledPdfBlobUrl } from "./PdfImporter";
import PdfFormEditor from "./PdfFormEditor";
import RateCatalog from "./RateCatalog";
import {
  supabase, signIn, signOut, getProfile,
  getUserProjects, getAllProjects, createProject, deleteProject,
  getProjectMembers, createUser, removeMember, changeMemberRole, getAllUsers, setSuperAdmin,
  getFolders, createFolder as dbCreateFolder, renameFolder as dbRenameFolder, deleteFolder as dbDeleteFolder,
  getTemplates, upsertTemplate, deleteTemplate as dbDeleteTemplate, uploadTemplateFile, downloadTemplateFile,
  getReports, upsertReport, deleteReport as dbDeleteReport,
  getSheets, uploadSheet, downloadSheet, deleteSheetRecord, updateSheetMeta,
  getSpecs, uploadSpec, downloadSpec, deleteSpecRecord, updateSpecMeta,
  getJob, getJobsChecked, upsertJob, getRateCatalogItemsOrThrow,
  uploadProposalPdf, downloadProposalPdf, removeProposalPdf,
  updateProjectConfig,
} from "./supabase";
import { patchRateCellsInWorkbook, planUnitTypeUpdate, isEstimatingTemplate, buildSampleReport, requirePositiveSampleSubtotal, buildRateCatalog, checkRatesCurrent } from "./rateCellPatch";
import { MODULE_KEYS, MODULE_META } from "./moduleKeys";
import { label } from "./label";
import { HOUSE_BRAND, applyTheme } from "./theme";
import JobsList from "./JobsList";
import JobScreen from "./JobScreen";
import { resolveLineStatus, computeLineSubtotal, resolveJobRollup, resolveMainSheet, readSubtotalFromResults, findQuantityField, parseLineQuantity, parseMarkupPercent, buildQuantityChange, buildNewLine, applyJobHeaderField, quantityJobSaveFailureMessage, isJobFrozen, buildSentJob, buildRevisedJobCopies, collectSentTakeoffIds, reviseFailureMessage, findExistingRevision, jobWriteBlockReason, findSentJobUsingTemplate, sentElsewhereMessage, hasUnsavedJobEdits, buildProposal, sendJobWithProposal, markSentFailureMessage, syncCustomLineDescription, applyLineDescription, lineDescriptionProblem, proposalBaseId, quantityDivergenceProblem, syncUnitLineQuantity, lineProblemMessage, projectHasEstimatingTemplates, isUnitTypeTemplate, isCustomLineTemplate, defaultMarkupPercent, reviseLostResponseOutcome, jobsUnknownMessage } from "./jobMath";
import { createSerialQueue, createScopedOpRunner } from "./serialQueue";
import { generateProposalPdf, proposalFileName, interimProposalBrand } from "./proposalPdf";

// Task 6: refusals for edits to a sent (frozen) job or a takeoff behind one.
const SENT_JOB_LOCKED = "This job has been sent and can no longer be edited. Use Revise to make an editable copy.";
const SENT_TAKEOFF_LOCKED = "This takeoff is part of a sent bid and can no longer be changed. Revise the job to get an editable copy.";
// Task 7 fix round 1 (ruling R24): until the client layer supplies a brand,
// proposals print no company name (never the product's), no contact and no
// terms, so the "Terms not set" notice shows.
const PROPOSAL_BRAND = interimProposalBrand(HOUSE_BRAND);

// In-memory stores — survive within the browser session but not page refresh
const WB_STORE = {};   // parsed SheetJS workbooks (used for non-Proctor templates)
const RAW_STORE = {};  // raw xlsx bytes (used for Proctor export to preserve chart)

const FIELD_TYPES = [
  { value: "text",     label: "Short Text",    icon: "T"  },
  { value: "textarea", label: "Long Text",     icon: "¶"  },
  { value: "number",   label: "Number",        icon: "#"  },
  { value: "date",     label: "Date",          icon: "📅" },
  { value: "email",    label: "Email",         icon: "@"  },
  { value: "url",      label: "URL",           icon: "🔗" },
  { value: "select",   label: "Dropdown",      icon: "▾"  },
  { value: "image",    label: "Photo / Image", icon: "🖼" },
];

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2);

// ─── Embedded chart helpers ────────────────────────────────────────────────────
// Which worksheet names a template covers. Multi-sheet templates list them
// explicitly; a single-sheet import is named after its sheet, with the workbook's
// first sheet as the fallback — the same resolution exportReportAsExcelPDF uses.
function sheetNamesForTemplate(tpl, wb) {
  if (!tpl) return [];
  if (tpl.isMultiSheet) return (tpl.sheets || []).map(s => s.name);
  const names = wb?.SheetNames || [];
  const name = names.includes(tpl.name) ? tpl.name : names[0];
  return name ? [name] : [];
}

// sheetName → [PNG data URL, ...] for a template's embedded charts.
// Used by the export paths that have no access to the chartsBySheet state,
// so a report exported from the reports list carries the same charts as one
// exported from the open report.
async function buildChartImagesForTemplate(tpl, rawBytes, wb) {
  const out = {};
  if (!tpl?.isExcelImport || !rawBytes || !wb) return out;
  for (const name of sheetNamesForTemplate(tpl, wb)) {
    const cfgs = await parseChartsForSheet(name, rawBytes, wb);
    if (cfgs.length) {
      out[name] = await Promise.all(
        cfgs.map(cfg => chartConfigToDataUrl(cfg, cfg.options?.plugins?.title?.text || ""))
      );
    }
  }
  return out;
}

// ─── Excel Export ──────────────────────────────────────────────────────────────
function exportToExcel(report, template) {
  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Report Summary ──
  const summaryRows = [
    ["REPORT SUMMARY"],
    [],
    ["Report Title", report.reportTitle || "Untitled Report"],
    ["Template",     template?.name || "—"],
    ["Author",       report._author || "—"],
    ["Date",         report._date || "—"],
    [],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet["!cols"] = [{ wch: 20 }, { wch: 50 }];
  summarySheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

  // ── Sheet 2: Full Data (one row per report, one col per field) ──
  const headers = ["Report Title", "Author", "Date"];
  const values  = [report.reportTitle || "", report._author || "", report._date || ""];

  (template?.sections || []).forEach(sec => {
    (sec.fields || []).forEach(f => {
      headers.push(`[${sec.name}] ${f.label}`);
      values.push(f.type === "image" ? (report[f.id] ? "[Image]" : "") : (report[f.id] || ""));
    });
  });

  const dataSheet = XLSX.utils.aoa_to_sheet([headers, values]);
  dataSheet["!cols"] = headers.map(() => ({ wch: 24 }));
  XLSX.utils.book_append_sheet(wb, dataSheet, "Data");

  // ── Sheet 3: Field-by-Field Detail ──
  const detailRows = [["Section", "Field", "Type", "Value"]];
  (template?.sections || []).forEach(sec => {
    (sec.fields || []).forEach(f => {
      detailRows.push([sec.name, f.label, f.type, f.type === "image" ? (report[f.id] ? "[Image]" : "") : (report[f.id] || "")]);
    });
  });
  const detailSheet = XLSX.utils.aoa_to_sheet(detailRows);
  detailSheet["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, detailSheet, "Field Detail");

  const filename = `${(report.reportTitle || "report").replace(/\s+/g, "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ─── Bulk Excel Export (all reports for a template) ───────────────────────────
function exportAllToExcel(reports, templates) {
  if (!reports.length) return;
  const wb = XLSX.utils.book_new();

  // Group by template
  const byTemplate = {};
  reports.forEach(r => {
    const key = r._templateId || "no-template";
    if (!byTemplate[key]) byTemplate[key] = [];
    byTemplate[key].push(r);
  });

  Object.entries(byTemplate).forEach(([tplId, reps]) => {
    const tpl = templates.find(t => t.id === tplId);
    const sheetName = (tpl?.name || "Reports").slice(0, 31);
    const headers = ["Report Title", "Author", "Date"];
    (tpl?.sections || []).forEach(sec => {
      (sec.fields || []).forEach(f => headers.push(`[${sec.name}] ${f.label}`));
    });

    const rows = [headers];
    reps.forEach(r => {
      const row = [r.reportTitle || "", r._author || "", r._date || ""];
      (tpl?.sections || []).forEach(sec => {
        (sec.fields || []).forEach(f => row.push(f.type === "image" ? (r[f.id] ? "Image" : "") : (r[f.id] || "")));
      });
      rows.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!cols"] = headers.map(() => ({ wch: 24 }));
    XLSX.utils.book_append_sheet(wb, sheet, sheetName);
  });

  XLSX.writeFile(wb, "all_reports.xlsx");
}

// ─── Folder helpers ───────────────────────────────────────────────────────────
// Returns a Set of folderId + all descendant folder IDs
function getDescendantIds(folders, folderId) {
  const ids = new Set([folderId]);
  const queue = [folderId];
  while (queue.length) {
    const cur = queue.shift();
    folders.filter(f => f.parentId === cur).forEach(f => { ids.add(f.id); queue.push(f.id); });
  }
  return ids;
}

// Flattens a folder list into [{...folder, depth}] in tree order (for move dropdowns)
function flattenFolders(folders, parentId = null, depth = 0) {
  return folders
    .filter(f => (f.parentId || null) === parentId)
    .flatMap(f => [{ ...f, depth }, ...flattenFolders(folders, f.id, depth + 1)]);
}

// Builds display path "2026 / January" for a folder
function folderPath(folders, folderId) {
  const parts = [];
  let cur = folders.find(f => f.id === folderId);
  while (cur) { parts.unshift(cur.name); cur = cur.parentId ? folders.find(f => f.id === cur.parentId) : null; }
  return parts.join(" / ");
}

// ─── FolderSidebar Component ──────────────────────────────────────────────────
// Self-contained nested folder sidebar with expand/collapse, inline rename,
// inline create (root or subfolder via the + button on each row).
function FolderSidebar({ kind, folders, items, activeFolder, setActiveFolder, onCreate, onRename, onDelete, canEdit, allLabel }) {
  const [expanded, setExpanded] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [creatingParentId, setCreatingParentId] = useState(null);
  const [newName, setNewName] = useState("");

  const tree = useMemo(() => {
    const map = {};
    folders.forEach(f => { map[f.id] = { ...f, children: [] }; });
    const roots = [];
    folders.forEach(f => {
      if (f.parentId && map[f.parentId]) map[f.parentId].children.push(map[f.id]);
      else roots.push(map[f.id]);
    });
    return roots;
  }, [folders]);

  const descendantsMap = useMemo(() => {
    const result = {};
    folders.forEach(f => { result[f.id] = getDescendantIds(folders, f.id); });
    return result;
  }, [folders]);

  const startCreate = (parentId) => {
    setIsCreating(true);
    setCreatingParentId(parentId);
    setNewName("");
    if (parentId) setExpanded(prev => new Set([...prev, parentId]));
  };

  const commitCreate = async () => {
    if (!newName.trim()) { setIsCreating(false); setNewName(""); return; }
    await onCreate(kind, newName.trim(), creatingParentId);
    setIsCreating(false);
    setNewName("");
  };

  const commitRename = async () => {
    if (editingName.trim()) await onRename(editingId, editingName.trim());
    setEditingId(null);
    setEditingName("");
  };

  const toggle = (e, id) => {
    e.stopPropagation();
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const renderNodes = (nodes, depth = 0) => nodes.map(node => {
    const hasKids = node.children.length > 0;
    const isOpen = expanded.has(node.id);
    const count = items.filter(i => descendantsMap[node.id]?.has(i.folderId)).length;
    const isActive = activeFolder === node.id;
    const pl = 8 + depth * 14;
    const childPl = 8 + (depth + 1) * 14;

    return (
      <div key={node.id}>
        <div
          style={{ display:"flex", alignItems:"center", gap:4, padding:`7px 8px 7px ${pl}px`, borderRadius:6, cursor:"pointer", fontSize:13, color: isActive ? "var(--rf-accent)" : "var(--rf-muted)", background: isActive ? "var(--rf-surface)" : "transparent", userSelect:"none" }}
          onClick={() => setActiveFolder(node.id)}
        >
          <span style={{ width:12, fontSize:9, color:"var(--rf-text-dim)", flexShrink:0, cursor:"pointer" }} onClick={e => toggle(e, node.id)}>
            {hasKids ? (isOpen ? "▼" : "▶") : ""}
          </span>
          {editingId === node.id ? (
            <input
              style={{ flex:1, background:"var(--rf-bg)", border:"1px solid var(--rf-primary)", borderRadius:5, color:"var(--rf-text-heading)", fontSize:12, padding:"3px 6px", outline:"none" }}
              value={editingName} autoFocus
              onChange={e => setEditingName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setEditingId(null); } }}
              onBlur={commitRename}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <>
              <span style={{ fontSize:14, flexShrink:0 }}>📁</span>
              <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{node.name}</span>
              <span style={{ marginLeft:"auto", fontSize:10, color:"var(--rf-text-faint)", background:"var(--rf-surface)", borderRadius:10, padding:"1px 6px", flexShrink:0 }}>{count}</span>
              {canEdit && <>
                <span style={{ fontSize:11, cursor:"pointer", opacity:0.6, padding:"0 2px", flexShrink:0 }} title="Rename" onClick={e => { e.stopPropagation(); setEditingId(node.id); setEditingName(node.name); }}>✏️</span>
                <span style={{ fontSize:11, cursor:"pointer", opacity:0.7, padding:"0 2px", flexShrink:0, fontWeight:"bold" }} title="New subfolder" onClick={e => { e.stopPropagation(); startCreate(node.id); }}>＋</span>
                <span style={{ fontSize:11, cursor:"pointer", opacity:0.6, padding:"0 2px", flexShrink:0 }} title="Delete" onClick={e => { e.stopPropagation(); if (window.confirm(`Delete folder "${node.name}" and all its subfolders?`)) onDelete(kind, node.id); }}>🗑</span>
              </>}
            </>
          )}
        </div>
        {isCreating && creatingParentId === node.id && (
          <div style={{ paddingLeft: childPl, paddingRight:8, paddingTop:3, paddingBottom:3 }}>
            <input
              style={{ width:"100%", background:"var(--rf-bg)", border:"1px solid var(--rf-primary)", borderRadius:5, color:"var(--rf-text-heading)", fontSize:12, padding:"4px 7px", outline:"none", boxSizing:"border-box" }}
              placeholder="Subfolder name…" value={newName} autoFocus
              onChange={e => setNewName(e.target.value)}
              onKeyDown={async e => { if (e.key === "Enter") await commitCreate(); if (e.key === "Escape") { setIsCreating(false); setNewName(""); } }}
            />
          </div>
        )}
        {hasKids && isOpen && renderNodes(node.children, depth + 1)}
      </div>
    );
  });

  return (
    <div style={{ width:200, flexShrink:0, background:"var(--rf-sidebar)", border:"1px solid var(--rf-surface)", borderRadius:10, padding:"12px 8px", display:"flex", flexDirection:"column", gap:2 }} onClick={e => e.stopPropagation()}>
      <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:1, color:"var(--rf-text-faint)", padding:"0 8px 8px" }}>Folders</div>
      <div
        style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 8px", borderRadius:6, cursor:"pointer", fontSize:13, userSelect:"none", color: activeFolder === null ? "var(--rf-accent)" : "var(--rf-muted)", background: activeFolder === null ? "var(--rf-surface)" : "transparent" }}
        onClick={() => setActiveFolder(null)}
      >
        <span style={{ fontSize:14 }}>📂</span> {allLabel}
        <span style={{ marginLeft:"auto", fontSize:10, color:"var(--rf-text-faint)", background:"var(--rf-surface)", borderRadius:10, padding:"1px 6px" }}>{items.length}</span>
      </div>
      {renderNodes(tree)}
      {canEdit && (isCreating && creatingParentId === null ? (
        <div style={{ padding:"6px 8px" }}>
          <input
            style={{ width:"100%", background:"var(--rf-bg)", border:"1px solid var(--rf-primary)", borderRadius:5, color:"var(--rf-text-heading)", fontSize:12, padding:"4px 7px", outline:"none", boxSizing:"border-box" }}
            placeholder="Folder name…" value={newName} autoFocus
            onChange={e => setNewName(e.target.value)}
            onKeyDown={async e => { if (e.key === "Enter") await commitCreate(); if (e.key === "Escape") { setIsCreating(false); setNewName(""); } }}
          />
        </div>
      ) : !isCreating && (
        <button style={{ margin:"6px 8px 2px", background:"transparent", border:"1px dashed var(--rf-border)", borderRadius:6, color:"var(--rf-primary)", fontSize:12, cursor:"pointer", padding:"5px 10px", textAlign:"left" }}
          onClick={() => startCreate(null)}>+ New Folder</button>
      ))}
    </div>
  );
}

// ─── Excel Sheet Preview Component ────────────────────────────────────────────
// Renders the full Excel sheet as an HTML table inside the preview tab.
// Re-renders whenever report or template changes.
function ExcelSheetPreview({ report, template, getRawBytes }) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve(getRawBytes(template.id)).then(rawBytes => {
      if (cancelled) return;
      if (!rawBytes) { setLoading(false); setHtml(null); return; }
      buildExcelPreviewHtml(report, template, rawBytes).then(result => {
        if (!cancelled) { setHtml(result); setLoading(false); }
      });
    });
    return () => { cancelled = true; };
  }, [report, template]); // eslint-disable-line

  if (loading) return <div style={{ padding: "40px 24px", color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>Loading preview…</div>;
  if (!html) return <div style={{ padding: "40px 24px", color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>Preview unavailable.</div>;
  return (
    <div style={{ overflowX: "auto", padding: "24px 24px 40px", background: "var(--rf-sidebar)" }}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

// ─── Excel Sheet Editor Component ─────────────────────────────────────────────
// Renders the Excel sheet as an HTML table with live <input> fields embedded
// in the cells that map to template fields. Uses event delegation for changes.
function ExcelSheetEditor({ report, template, rawBytes, onChange, cellOverrides }) {
  const [html, setHtml] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const containerRef = useRef(null);
  const formulaTimerRef = useRef(null);

  // Build the table HTML once (template/rawBytes don't change while editing)
  useEffect(() => {
    if (!rawBytes) return;
    setLoadErr(null);
    buildExcelEditableHtml(template, rawBytes)
      .then(result => { if (result) setHtml(result); else setLoadErr("Sheet not found in template file."); })
      .catch(err => { console.error("[ExcelSheetEditor]", err); setLoadErr(err?.message || "Failed to render sheet."); });
  }, [template, rawBytes]); // eslint-disable-line

  // Sync report values into input elements whenever report changes
  useEffect(() => {
    if (!containerRef.current || !html) return;
    const inputs = containerRef.current.querySelectorAll("input[data-field-id]");
    inputs.forEach(inp => {
      const val = report[inp.getAttribute("data-field-id")] || "";
      if (document.activeElement !== inp) inp.value = val;
    });
  }, [report, html]);

  // Debounced formula evaluation — updates formula cell display values ~400ms after typing
  useEffect(() => {
    if (!containerRef.current || !html || !rawBytes) return;
    clearTimeout(formulaTimerRef.current);
    formulaTimerRef.current = setTimeout(() => {
      const values = { ...evalFormulasWithReport(template, rawBytes, report), ...(cellOverrides || {}) };
      const cells = containerRef.current?.querySelectorAll("[data-formula-addr]");
      cells?.forEach(td => {
        const addr = td.getAttribute("data-formula-addr");
        if (addr && values[addr] !== undefined) td.textContent = values[addr];
      });
    }, 400);
    return () => clearTimeout(formulaTimerRef.current);
  }, [report, html, rawBytes, cellOverrides]); // eslint-disable-line

  // Event delegation — one listener for all inputs
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !html) return;
    const handle = e => {
      const fieldId = e.target.getAttribute?.("data-field-id");
      if (fieldId) onChange(fieldId, e.target.value);
    };
    el.addEventListener("input", handle);
    return () => el.removeEventListener("input", handle);
  }, [html, onChange]);

  // Focus ring on active input
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !html) return;
    const onFocus = e => { if (e.target.dataset?.fieldId) { e.target.parentElement.style.boxShadow = "inset 0 0 0 2px var(--rf-primary)"; e.target.parentElement.style.background = "#eef2ff"; } };
    const onBlur  = e => { if (e.target.dataset?.fieldId) { e.target.parentElement.style.boxShadow = "inset 0 0 0 1px #e0e7ff"; e.target.parentElement.style.background = "#fffbcc"; } };
    el.addEventListener("focusin",  onFocus);
    el.addEventListener("focusout", onBlur);
    return () => { el.removeEventListener("focusin", onFocus); el.removeEventListener("focusout", onBlur); };
  }, [html]);

  if (loadErr) return <div style={{ padding: "40px 24px", color: "var(--rf-danger)", fontSize: 13 }}>Error: {loadErr} — Check the browser console for details.</div>;
  if (!rawBytes) return <div style={{ padding: "40px 24px", color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>Loading template file…</div>;
  if (!html) return <div style={{ padding: "40px 24px", color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>Loading form…</div>;
  return (
    <div style={{ overflowX: "auto", background: "#fff", padding: "12px 0 40px" }}>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}


// ─── PDF Export ────────────────────────────────────────────────────────────────
function generatePDF(report, template, chartImgDataURL, returnBlob = false) {
  if (template?.isProctor) {
    return exportProctorToPDF(report, template, chartImgDataURL, returnBlob);
  } else if (template?.isExcelImport) {
    return generateExcelTemplatePDF(report, template, returnBlob);
  } else {
    return generateGenericPDF(report, template, returnBlob);
  }
}

// Render PDF that mirrors the Proctor/Excel sheet layout exactly
function generateExcelTemplatePDF(report, template, returnBlob = false) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const mL = 36, mR = 36, mT = 36;
  const contentW = pageW - mL - mR;
  let y = mT;

  // ── Value lookup: field.id → filled value (or defaultValue) ─────────────
  const val = (fieldId, defaultVal = "") => {
    const v = report[fieldId];
    return (v !== undefined && v !== null && v !== "") ? String(v) : String(defaultVal || "");
  };

  // ── Field lookup by label text ────────────────────────────────────────────
  const fieldByLabel = {};
  (template.sections || []).forEach(sec => {
    (sec.fields || []).forEach(f => {
      fieldByLabel[f.label.trim()] = val(f.id, f.defaultValue);
    });
  });

  const checkPage = (needed = 20) => {
    if (y + needed > pageH - 36) { doc.addPage(); y = mT; }
  };

  // ── Cell renderer ─────────────────────────────────────────────────────────
  // Properly vertically and horizontally centers text in cells
  const drawCell = (x, cy, w, h, txt, opts = {}) => {
    const { bg, bold = false, fontSize = 8, align = "left", textColor = [20, 20, 20], italic = false } = opts;
    // Background
    if (bg) { doc.setFillColor(...bg); doc.rect(x, cy, w, h, "F"); }
    // Border
    doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.25); doc.rect(x, cy, w, h, "S");
    // Text
    if (!txt && txt !== 0) return;
    const str = String(txt);
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", italic ? "italic" : bold ? "bold" : "normal");
    doc.setTextColor(...textColor);
    const pad = 3;
    const maxW = w - pad * 2;
    const lines = doc.splitTextToSize(str, maxW);
    const lineH = fontSize * 1.25;
    const totalH = lines.length * lineH;
    // Vertical center
    let ty = cy + (h - totalH) / 2 + fontSize * 0.72;
    lines.forEach(line => {
      if (align === "center") doc.text(line, x + w / 2, ty, { align: "center" });
      else doc.text(line, x + pad, ty);
      ty += lineH;
    });
  };

  // ── Section header bar ────────────────────────────────────────────────────
  const drawSectionHeader = (label, fy, fw) => {
    doc.setFillColor(55, 55, 55); doc.rect(mL, fy, fw, 14, "F");
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text(label, mL + fw / 2, fy + 9.5, { align: "center" });
    return fy + 14;
  };

  // ── Identify sections by type ─────────────────────────────────────────────
  const allSecs = template.sections || [];
  // General info = section with colon-label fields (no _tableRow)
  const infoSec = allSecs.find(s => s.fields.some(f => f._tableRow === undefined && !f._tableHeaderRow));
  // Oversize = section whose name contains "OVERSIZE" or has ≤5 non-table fields
  const oversizeSec = allSecs.find(s =>
    s.name.toUpperCase().includes("OVERSIZE") ||
    (s !== infoSec && s.fields.every(f => f._tableRow === undefined) && s.fields.length <= 6)
  );
  // Table sections = have _tableRow fields
  const tableSecs = allSecs.filter(s => s.fields.some(f => f._tableRow !== undefined));

  // ── TITLE BAR ─────────────────────────────────────────────────────────────
  const titleText = report.reportTitle || template.name || template.description || "Report";
  doc.setFillColor(40, 40, 40); doc.rect(mL, y, contentW, 20, "F");
  doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
  doc.text(titleText, mL + 6, y + 13);
  y += 22;

  // ── HEADER INFO (left) + OVERSIZE TABLE (right) side by side ──────────────
  const headerStartY = y;
  const leftW = oversizeSec ? contentW * 0.60 : contentW;
  const rightW = contentW - leftW - 4;
  const rightX = mL + leftW + 4;
  const rowH = 17;

  if (infoSec) {
    // Build pairs: [label, fieldId] — each row shows 2 pairs
    const plainFields = infoSec.fields.filter(f => f._tableRow === undefined);
    // Pair them up into rows of 2
    const pairs = [];
    for (let i = 0; i < plainFields.length; i += 2) {
      pairs.push([plainFields[i], plainFields[i + 1] || null]);
    }
    const lw = leftW * 0.30, vw = leftW * 0.20, lw2 = leftW * 0.30, vw2 = leftW * 0.20;

    pairs.forEach(([f1, f2]) => {
      checkPage(rowH);
      // Left label
      drawCell(mL, y, lw, rowH, f1.label + ":", { bold: true, bg: [240, 240, 240], fontSize: 7.5 });
      // Left value
      drawCell(mL + lw, y, vw, rowH, val(f1.id, f1.defaultValue), { fontSize: 8 });
      if (f2) {
        // Right label
        drawCell(mL + lw + vw, y, lw2, rowH, f2.label + ":", { bold: true, bg: [240, 240, 240], fontSize: 7.5 });
        // Right value
        drawCell(mL + lw + vw + lw2, y, vw2, rowH, val(f2.id, f2.defaultValue), { fontSize: 8 });
      } else {
        drawCell(mL + lw + vw, y, lw2 + vw2, rowH, "", { bg: [255, 255, 255] });
      }
      y += rowH;
    });
  }

  // ── OVERSIZE TABLE (right side, drawn alongside header) ───────────────────
  if (oversizeSec) {
    let oy = headerStartY;
    const ovH = 13;
    // Title bar
    doc.setFillColor(185, 185, 185); doc.rect(rightX, oy, rightW, ovH, "F");
    doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.25); doc.rect(rightX, oy, rightW, ovH, "S");
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(20, 20, 20);
    doc.text(oversizeSec.name || "OVERSIZE CORRECTION", rightX + rightW / 2, oy + 8.5, { align: "center" });
    oy += ovH;

    // Header row: Input | Value
    const iW = rightW * 0.60, vW = rightW * 0.40;
    drawCell(rightX, oy, iW, ovH, "Input", { bold: true, bg: [210, 210, 210], align: "center", fontSize: 7.5 });
    drawCell(rightX + iW, oy, vW, ovH, "Value", { bold: true, bg: [210, 210, 210], align: "center", fontSize: 7.5 });
    oy += ovH;

    // Data rows
    oversizeSec.fields.forEach(f => {
      const cellH = 15;
      drawCell(rightX, oy, iW, cellH, f.label, { fontSize: 7, bg: [250, 250, 250] });
      drawCell(rightX + iW, oy, vW, cellH, val(f.id, f.defaultValue), { fontSize: 8, align: "center" });
      oy += cellH;
    });
  }

  y = Math.max(y, (oversizeSec ? (headerStartY + 13 * 2 + oversizeSec.fields.length * 15) : y)) + 6;

  // ── DATA TABLE SECTIONS ───────────────────────────────────────────────────
  tableSecs.forEach(sec => {
    checkPage(40);
    y = drawSectionHeader(sec.name, y, contentW);

    // Get all unique column labels and trial numbers
    const colLabels = [];
    const trialSet = new Set();
    sec.fields.forEach(f => {
      const m = f.label.match(/^(.+) — Trial (\d+)$/);
      if (m) {
        if (!colLabels.includes(m[1])) colLabels.push(m[1]);
        trialSet.add(parseInt(m[2]));
      }
    });
    const trials = [...trialSet].sort((a, b) => a - b);

    // Build lookup: "colLabel — Trial N" → value
    const tval = (colLabel, trial) => {
      const f = sec.fields.find(f2 => f2.label === `${colLabel} — Trial ${trial}`);
      return f ? val(f.id, f.defaultValue) : "";
    };

    // Column widths — Trial col narrow, rest equal
    const trialColW = 30;
    const dataColW = (contentW - trialColW) / colLabels.length;
    const hdrH = 26;

    // Header row
    checkPage(hdrH);
    drawCell(mL, y, trialColW, hdrH, "Trial", { bold: true, bg: [215, 215, 215], align: "center", fontSize: 7 });
    colLabels.forEach((lbl, i) => {
      drawCell(mL + trialColW + i * dataColW, y, dataColW, hdrH, lbl, {
        bold: true, bg: [215, 215, 215], align: "center", fontSize: 6.5
      });
    });
    y += hdrH;

    // Data rows
    trials.forEach(trial => {
      checkPage(14);
      const rH = 13;
      const bg = trial % 2 === 0 ? [248, 248, 248] : [255, 255, 255];
      drawCell(mL, y, trialColW, rH, String(trial), { align: "center", fontSize: 8, bg });
      colLabels.forEach((lbl, i) => {
        drawCell(mL + trialColW + i * dataColW, y, dataColW, rH, tval(lbl, trial), {
          align: "center", fontSize: 8, bg
        });
      });
      y += rH;
    });
    y += 4;
  });

  // ── OTHER SECTIONS (Results, Field Density etc — non-table, non-info, non-oversize) ──
  const otherSecs = allSecs.filter(s => s !== infoSec && s !== oversizeSec && !tableSecs.includes(s));
  otherSecs.forEach(sec => {
    checkPage(40);
    y = drawSectionHeader(sec.name, y, contentW);
    const plainFields = sec.fields.filter(f => f._tableRow === undefined);
    // Pair into rows of 2
    for (let i = 0; i < plainFields.length; i += 2) {
      checkPage(17);
      const f1 = plainFields[i], f2 = plainFields[i + 1] || null;
      const hw = contentW / 2;
      const lw = hw * 0.55, vw2 = hw * 0.45;
      const v1 = val(f1.id, f1.defaultValue);
      const isComputed1 = !v1;
      drawCell(mL, y, lw, rowH, f1.label, { bold: true, bg: [240, 240, 240], fontSize: 7.5 });
      drawCell(mL + lw, y, vw2, rowH, isComputed1 ? "(calculated)" : v1, {
        fontSize: 7.5, align: "center",
        bg: isComputed1 ? [215, 215, 215] : [255, 255, 255],
        textColor: isComputed1 ? [130, 130, 130] : [20, 20, 20],
        italic: isComputed1,
      });
      if (f2) {
        const v2 = val(f2.id, f2.defaultValue);
        const isComputed2 = !v2;
        drawCell(mL + hw, y, lw, rowH, f2.label, { bold: true, bg: [240, 240, 240], fontSize: 7.5 });
        drawCell(mL + hw + lw, y, vw2, rowH, isComputed2 ? "(calculated)" : v2, {
          fontSize: 7.5, align: "center",
          bg: isComputed2 ? [215, 215, 215] : [255, 255, 255],
          textColor: isComputed2 ? [130, 130, 130] : [20, 20, 20],
          italic: isComputed2,
        });
      }
      y += rowH;
    }
    y += 4;
  });

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
    doc.setDrawColor(190, 190, 190); doc.setLineWidth(0.3);
    doc.line(mL, pageH - 22, pageW - mR, pageH - 22);
    doc.text(titleText, mL, pageH - 13);
    doc.text(`Page ${p} of ${pages}`, pageW - mR, pageH - 13, { align: "right" });
  }

  if (returnBlob) return doc.output("blob");
  doc.save(`${titleText.replace(/\s+/g, "_")}.pdf`);
}

function generateGenericPDF(report, template, returnBlob = false) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 60;
  const contentW = pageW - margin * 2;
  let y = 60;
  const safe = (v) => (v === undefined || v === null ? "" : String(v));
  const checkPage = () => { if (y > 720) { doc.addPage(); y = 60; } };

  doc.setFillColor(17, 24, 39); doc.rect(0, 0, pageW, 8, "F");
  doc.setFillColor(99, 102, 241); doc.rect(0, 0, 4, 800, "F");

  y = 72;
  doc.setFontSize(28); doc.setFont("helvetica", "bold"); doc.setTextColor(17, 24, 39);
  doc.splitTextToSize(safe(report.reportTitle) || "Untitled Report", contentW).forEach(l => { doc.text(l, margin, y); y += 34; });

  if (template?.name) { doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(99, 102, 241); doc.text(template.name.toUpperCase(), margin, y); y += 16; }
  const meta = [report._author && `By ${report._author}`, report._date].filter(Boolean).join("  ·  ");
  if (meta) { doc.setFontSize(10); doc.setTextColor(107, 114, 128); doc.text(meta, margin, y); y += 20; }
  y += 6; doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.75); doc.line(margin, y, pageW - margin, y); y += 22;

  (template?.sections || []).forEach((sec, si) => {
    checkPage();
    doc.setFillColor(243, 244, 246); doc.roundedRect(margin, y - 13, contentW, 22, 3, 3, "F");
    doc.setFillColor(99, 102, 241); doc.rect(margin, y - 13, 3, 22, "F");
    doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(17, 24, 39);
    doc.text(safe(sec.name) || `Section ${si + 1}`, margin + 12, y + 2); y += 26;
    (sec.fields || []).forEach((field) => {
      const val = report[field.id];
      if (!val && val !== 0) return;
      checkPage();
      doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(107, 114, 128);
      doc.text((safe(field.label) || "Field").toUpperCase(), margin, y); y += 13;
      if (field.type === "image" && typeof val === "string" && val.startsWith("data:image")) {
        const imgH = 120;
        const imgW = Math.min(contentW, 200);
        if (y + imgH > 740) { doc.addPage(); y = 60; }
        try { doc.addImage(val, margin, y, imgW, imgH); } catch(e) { /* skip if format unsupported */ }
        y += imgH + 8;
      } else {
        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(31, 41, 55);
        doc.splitTextToSize(safe(val), contentW).forEach(l => { checkPage(); doc.text(l, margin, y); y += 15; });
        y += 8;
      }
    });
    y += 10;
  });

  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p); doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(156, 163, 175);
    doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.5); doc.line(margin, 770, pageW - margin, 770);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, 782, { align: "right" });
    doc.text(safe(report.reportTitle) || "Report", margin, 782);
  }
  if (returnBlob) return doc.output("blob");
  doc.save(`${(safe(report.reportTitle) || "report").replace(/\s+/g, "_")}.pdf`);
}

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  // Sets the --rf-* colour variables the styles below read. Layout effect so
  // they exist before the first paint. House brand only for now; a later task
  // swaps in the per-hostname brand.
  useLayoutEffect(() => { applyTheme(HOUSE_BRAND); }, []);
  const [screen, setScreen] = useState("home");
  const [homeTab, setHomeTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  // Final fix wave, item 11: "loading" | "ok" | "error" for the current
  // project's jobs, so an error is never mistaken for "no jobs" (see
  // jobMath.jobsUnknownMessage).
  const [jobsLoadState, setJobsLoadState] = useState("loading");
  const [activeJob, setActiveJob] = useState(null);
  const [jobLineDetails, setJobLineDetails] = useState({ lines: [], rollup: { incomplete: true, subtotal: null, markup: null, total: null } });
  const [jobTotals, setJobTotals] = useState({});
  const [jobMarkupError, setJobMarkupError] = useState(null);
  const [cameFromJobId, setCameFromJobId] = useState(null);
  const [ratesDirty, setRatesDirty] = useState(false);
  const [updatingUnitTypes, setUpdatingUnitTypes] = useState(false);
  // Ref mirrors of activeJob/reports/templates/jobMarkupError, kept current
  // by the effects right below AND, inside every job-mutating handler
  // further down, updated directly the moment a save resolves (see
  // jobQueueRef right after this block). An async job handler reads
  // *Ref.current, never the state variable and never a value "peeked" out
  // of a setState updater callback — React 18 only runs an updater
  // synchronously when the fiber has no pending lanes, so a preceding
  // setState call earlier in the very same handler (e.g. setReports right
  // before a setActiveJob "peek") defers the peek's callback, leaving the
  // outer variable it assigns to still null/stale on the next line. That
  // was fix round 1's bug: addJobLine threw on every call because its
  // `latestJob.lines` read ran against a `latestJob` the deferred updater
  // had never actually set.
  //
  // IMPORTANT — what these refs do NOT fix, on their own: reading a fresh
  // value solves a STALE READ, but two overlapping async job-mutating
  // operations are a genuine RACE between two in-flight writes, and no
  // amount of ref-freshness prevents whichever one's `upsertJob` resolves
  // LAST from overwriting whatever the other one just saved (fix round 3's
  // two Important findings: a second quantity edit committed while the
  // first is still saving, or a markup edit typed while a quantity save is
  // in flight, could each silently revert the other). That half of the
  // problem is solved by jobQueueRef below, not by these refs — every
  // job-mutating handler reads *Ref.current only from INSIDE its own
  // queued turn, once every earlier-queued operation has fully settled.
  const activeJobRef = useRef(null);
  const reportsRef = useRef([]);
  const templatesRef = useRef([]);
  const jobMarkupErrorRef = useRef(null);
  // Task 6 fix round 1: reviseJob reads the jobs list inside its turn (to
  // refuse a second revision), so jobs gets a ref mirror too. Job turns
  // update it the moment a save resolves, through updateJobs below.
  const jobsRef = useRef([]);
  const screenRef = useRef("home");
  const homeTabRef = useRef("templates");
  // Fix round 4: useLayoutEffect, not useEffect. A passive effect from an
  // OLDER render can flush after a handler has already written a NEWER value
  // straight into the ref (every job turn does, the moment its save
  // resolves), silently rolling the ref back to the older value until the
  // next render. A layout effect runs synchronously inside the commit, so
  // the ref can only ever be set to state that already includes every
  // update queued before that render.
  useLayoutEffect(() => { activeJobRef.current = activeJob; }, [activeJob]);
  useLayoutEffect(() => { reportsRef.current = reports; }, [reports]);
  useLayoutEffect(() => { jobsRef.current = jobs; }, [jobs]);
  useLayoutEffect(() => { templatesRef.current = templates; }, [templates]);
  useLayoutEffect(() => { jobMarkupErrorRef.current = jobMarkupError; }, [jobMarkupError]);
  useLayoutEffect(() => { screenRef.current = screen; }, [screen]);
  useLayoutEffect(() => { homeTabRef.current = homeTab; }, [homeTab]);
  // Fix round 3: every handler that reads "the current job" and later
  // writes an updated job back to the database now hands its actual work
  // to this one queue instead of running immediately. See
  // src/serialQueue.js for the queue itself (and its own tests) and the
  // comment above for why ref freshness alone isn't enough. Lazy-inited
  // (not `useRef(createSerialQueue())`, which would construct a new queue
  // on every render just to throw it away) so exactly one queue lives for
  // this component's whole lifetime.
  const jobQueueRef = useRef(null);
  if (jobQueueRef.current === null) jobQueueRef.current = createSerialQueue();
  // Fix round 4: every job turn goes through enqueueJobOp, never
  // jobQueueRef.current.enqueue directly. It scopes the turn to the job that
  // was active when it was enqueued (a turn whose job is no longer active by
  // the time its turn arrives never runs; a turn that sees ctx.isCurrent()
  // go false must skip every ref/state write and navigation except setJobs
  // for its own job id; since fix round 5 openJob's switch is itself a
  // queued turn, so every turn queued before it still runs as current),
  // and it catches
  // a thrown turn (e.g. a supabase-js network TypeError) and alerts instead
  // of leaving an unhandled rejection nobody sees. See
  // src/serialQueue.js's createScopedOpRunner and its tests.
  const jobOpRunnerRef = useRef(null);
  if (jobOpRunnerRef.current === null) {
    jobOpRunnerRef.current = createScopedOpRunner({
      queue: jobQueueRef.current,
      getActiveScopeId: () => activeJobRef.current?.id ?? null,
      onError: (label, err) => {
        console.error(`Job operation failed (${label}):`, err);
        alert(`Failed to ${label}.` + (err?.message ? ` (${err.message})` : ""));
      },
    });
  }
  const enqueueJobOp = (label, fn, jobId = activeJobRef.current?.id ?? null) => jobOpRunnerRef.current(label, jobId, fn);
  // activeJob only ever changes through here: a queued turn (while its job
  // is current) or openJob's queued switch turn. Ref first, so the very
  // next turn reads it.
  const commitActiveJob = (job) => {
    activeJobRef.current = job;
    setActiveJob(job);
  };
  // Task 6 fix round 1: every job turn changes the jobs list through here, so
  // jobsRef is current for the next turn without waiting for a render. `fn`
  // is a pure list transform applied to the ref and handed to setJobs; no
  // value is ever read back out of the updater.
  const updateJobs = (fn) => {
    jobsRef.current = fn(jobsRef.current);
    setJobs(fn);
  };
  // Task 6 fix round 1 (ruling R18): the freeze is re-checked against the
  // stored row just before a turn writes, so a bid marked Sent in another
  // tab or by another user is never overwritten. Returns { reason,
  // storedJob }: reason is the refusal message or null. A failed check
  // refuses too (fail closed).
  const CHECK_FAILED = "Could not check whether this bid was marked Sent elsewhere, so nothing was saved.";
  const storedJobBlockReason = async (jobId, intent) => {
    let result;
    try {
      result = await getJob(jobId);
    } catch (err) {
      result = { data: null, error: err };
    }
    if (result.error) return { reason: CHECK_FAILED + (result.error.message ? ` (${result.error.message})` : ""), storedJob: null, intent };
    return { reason: jobWriteBlockReason({ storedJob: result.data, intent }), storedJob: result.data, intent };
  };
  // As above, for a quantity change that writes through to a takeoff: also
  // refuses when any stored sent job points at that takeoff.
  const storedTakeoffBlockReason = async (jobId, reportId) => {
    let result;
    try {
      result = await getJobsChecked(currentProject.id);
    } catch (err) {
      result = { data: null, error: err };
    }
    if (result.error || !result.data) return { reason: CHECK_FAILED + (result.error?.message ? ` (${result.error.message})` : ""), storedJob: null, intent: "quantity" };
    const storedJob = result.data.find((j) => j.id === jobId) || null;
    return {
      reason: jobWriteBlockReason({ storedJob, intent: "quantity", takeoffReportId: reportId, sentTakeoffIds: collectSentTakeoffIds(result.data) }),
      storedJob,
      intent: "quantity",
    };
  };
  // When a check finds this job already sent in the database, the stored
  // (sent) version replaces the stale local copy, in the jobs list always
  // and on screen while this job is current. Without this, reopening the
  // active job would keep showing the stale in-memory draft.
  //
  // Fix round 2: returns { shown, discarded }: whether the sent version is
  // now on screen, and whether that replaced header/markup edits that were
  // never saved (compared against the last saved copy in jobsRef, read
  // before it is replaced).
  const adoptStoredSentJob = async (ctx, storedJob) => {
    if (!storedJob || !isJobFrozen(storedJob)) return { shown: false, discarded: false };
    const lastSaved = jobsRef.current.find((j) => j.id === storedJob.id) || null;
    updateJobs((prev) => prev.map((j) => (j.id === storedJob.id ? storedJob : j)));
    if (!ctx.isCurrent()) return { shown: false, discarded: false };
    const discarded = hasUnsavedJobEdits(activeJobRef.current, lastSaved);
    commitActiveJob(storedJob);
    setJobMarkupError(null);
    jobMarkupErrorRef.current = null;
    const details = await computeJobLineDetails(storedJob, reportsRef.current, templatesRef.current);
    if (ctx.isCurrent()) setJobLineDetails(details);
    return { shown: true, discarded };
  };
  // The message for a refused check, adopting the stored sent version first
  // when that is why it was refused. Any other refusal (the check failed, or
  // a takeoff belongs to some other sent job) is returned as is.
  const storedBlockMessage = async (ctx, check) => {
    if (!check.storedJob || !isJobFrozen(check.storedJob)) return check.reason;
    const { shown, discarded } = await adoptStoredSentJob(ctx, check.storedJob);
    return sentElsewhereMessage({ intent: check.intent, sentVersionShown: shown, discardedUnsavedEdits: discarded });
  };
  const refuseIfStoredBlocks = async (ctx, check) => {
    if (!check.reason) return false;
    alert(await storedBlockMessage(ctx, check));
    return true;
  };
  // Task 6 fix round 1, Important 1: Mark Sent / Revise are disabled while
  // their turn is queued or running, so a double click can't queue a second.
  // The ref is the real guard (two clicks can land before a re-render).
  const [jobActionBusy, setJobActionBusyState] = useState(null);
  const jobActionBusyRef = useRef(null);
  const setJobActionBusy = (v) => {
    jobActionBusyRef.current = v;
    setJobActionBusyState(v);
  };
  // Fix round 5 (ruling R10): the job openJob has asked to switch to whose
  // switch turn hasn't run yet (null when none). Written directly together
  // with its state twin, never mirrored by an effect. While it is set, the
  // Job screen shows a loading placeholder instead of the still-active
  // previous job. openSeqRef counts openJob calls so createNewJob can tell
  // whether any job was opened while its create was saving.
  const [openingJobId, setOpeningJobIdState] = useState(null);
  const openingJobIdRef = useRef(null);
  const setOpeningJobId = (id) => {
    openingJobIdRef.current = id;
    setOpeningJobIdState(id);
  };
  const openSeqRef = useRef(0);
  // Final fix wave, item 2: on sign-out and whenever the project changes,
  // nothing of the previous project's job screen may survive, or a Save (or
  // any other job turn) could write that job, and its takeoffs, into the
  // new project. Clears the active job through commitActiveJob, so every
  // turn still queued or in flight for it sees ctx.isCurrent() go false and
  // stops touching the screen; any write such a turn still makes goes to
  // the job's own project (upsertJob refuses any other). A pending openJob
  // switch is cancelled by clearing openingJobId. Also clears the line
  // details, the markup error, the Back-to-job target, and goes home.
  const resetJobScreen = () => {
    setOpeningJobId(null);
    commitActiveJob(null);
    setJobLineDetails({ lines: [], rollup: { incomplete: true, subtotal: null, markup: null, total: null } });
    setJobMarkupError(null);
    jobMarkupErrorRef.current = null;
    setCameFromJobId(null);
    setScreen("home");
    screenRef.current = "home";
  };
  const [activeTpl, setActiveTpl] = useState(null);
  const [activeRep, setActiveRep] = useState(null);
  const [repTpl, setRepTpl] = useState(null);
  const [flash, setFlash] = useState(false);
  const [tab, setTab] = useState("edit");
  const [exportMenu, setExportMenu] = useState(null);
  const [importModal, setImportModal] = useState(null); // { templates, workbook, sheetNames }
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const chartRef = useRef(null);

  // ── Filing system state ──────────────────────────────────────────────────────
  const [tplFolders, setTplFolders] = useState([]);   // [{ id, name }]
  const [repFolders, setRepFolders] = useState([]);   // [{ id, name }]
  const [repSearch, setRepSearch] = useState({ text: "", dateFrom: "", dateTo: "", templateId: "" });
  const [activeTplFolder, setActiveTplFolder] = useState(null);  // null = All
  const [activeRepFolder, setActiveRepFolder] = useState(null);  // null = All
  const [moveMenu, setMoveMenu] = useState(null); // item id with open move menu

  // ── Sheets state ─────────────────────────────────────────────────────────────
  const [sheets, setSheets] = useState([]);       // [{ id, name, folderId, uploadedAt, size }]
  const [sheetFolders, setSheetFolders] = useState([]);
  const [activeSheetFolder, setActiveSheetFolder] = useState(null);
  const [viewingSheet, setViewingSheet] = useState(null); // { id, name } of open PDF
  const [sheetBlobUrl, setSheetBlobUrl] = useState(null);
  const sheetFileRef = useRef(null);

  // ── Specs state ───────────────────────────────────────────────────────────────
  const [specs, setSpecs] = useState([]);
  const [specFolders, setSpecFolders] = useState([]);
  const [activeSpecFolder, setActiveSpecFolder] = useState(null);
  const [viewingSpec, setViewingSpec] = useState(null);
  const [specBlobUrl, setSpecBlobUrl] = useState(null);
  const specFileRef = useRef(null);
  const [repTplRawBytes, setRepTplRawBytes] = useState(null); // raw bytes for active repTpl
  const [chartsBySheet, setChartsBySheet] = useState({});    // sheetName → [chartConfig, ...]
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);   // active tab in multi-sheet edit/preview

  // ── Auth state ───────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", password: "", fullName: "", role: "manager" });
  const [userMgmtError, setUserMgmtError] = useState("");
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [superAdminProjects, setSuperAdminProjects] = useState([]);
  const [superAdminUsers, setSuperAdminUsers] = useState([]);
  const [newProjectName, setNewProjectName] = useState("");
  const [showLogin, setShowLogin] = useState(false);

  // ── Email state ───────────────────────────────────────────────────────────
  const [gmailEmail, setGmailEmail]       = useState(null); // connected address or null
  const [sendEmailModal, setSendEmailModal] = useState(null); // null | { to, cc, subject, message, sending, error }

  // Fast profile fetch using raw REST API — bypasses slow Supabase JS client
  const fetchProfileFast = async (userId, accessToken) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=*&limit=1`,
        { headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` } }
      );
      const rows = await res.json();
      return rows?.[0] || null;
    } catch { return null; }
  };

  // Auth state listener
  useEffect(() => {
    const timeout = setTimeout(() => setAuthLoading(false), 8000);
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        clearTimeout(timeout);
        setAuthLoading(false);
        // Fast raw fetch for profile so is_super_admin is known immediately
        const profile = await fetchProfileFast(session.user.id, session.access_token);
        setCurrentUser({
          ...session.user,
          ...(profile || {}),
          username: profile?.full_name || session.user.email,
        });
      } else {
        setCurrentUser(null);
        setCurrentProject(null);
        // Final fix wave, item 2: no stale job screen survives a sign-out.
        resetJobScreen();
        setTemplates([]); setReports([]); setJobs([]);
        setTplFolders([]); setRepFolders([]);
        setSheets([]); setSheetFolders([]);
        setSpecs([]); setSpecFolders([]);
        clearTimeout(timeout);
        setAuthLoading(false);
      }
    });
    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []); // eslint-disable-line

  // ── Gmail: check redirect params and load existing connection ────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail_connected") === "true") {
      const email = params.get("gmail_email");
      if (email) setGmailEmail(decodeURIComponent(email));
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gmail_error")) {
      alert("Gmail connection failed: " + params.get("gmail_error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!currentUser) { setGmailEmail(null); return; }
    supabase.from("email_connections").select("email")
      .eq("user_id", currentUser.id).eq("provider", "gmail").maybeSingle()
      .then(({ data }) => { if (data) setGmailEmail(data.email); });
  }, [currentUser]); // eslint-disable-line

  // Load projects when user logs in — wait until profile loaded so is_super_admin is known
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.is_super_admin === undefined) return; // profile not loaded yet
    const load = async () => {
      const list = currentUser.is_super_admin ? await getAllProjects() : await getUserProjects();
      setProjects(list);
      if (list.length === 1) setCurrentProject(list[0]);
    };
    load();
  }, [currentUser]);

  // The rates-out-of-date banner belongs to the project whose rates changed.
  // Keyed on the id, not the project object, so re-setting the same project
  // doesn't wipe a real warning.
  //
  // Final fix wave, item 3 (ruling R28): the flag is persisted per project
  // in localStorage (rb_rates_dirty_<project id>), so a reload no longer
  // loses it, and it is cleared only by a fully successful Update unit
  // types. It is a warning for this browser only; what actually stops an
  // old-rate bid, for every session, is the computed check before Mark Sent
  // and the preview (checkRatesCurrentForJob). Storage failures are ignored:
  // the banner is advisory, the check is the guard.
  const currentProjectId = currentProject?.id;
  const currentProjectIdRef = useRef(null);
  useLayoutEffect(() => { currentProjectIdRef.current = currentProjectId ?? null; }, [currentProjectId]);
  const ratesDirtyKey = (pid) => `rb_rates_dirty_${pid}`;
  const readRatesDirty = (pid) => {
    if (!pid) return false;
    try { return localStorage.getItem(ratesDirtyKey(pid)) === "1"; } catch { return false; }
  };
  // Writes the flag for project pid, and shows it only if pid is still the
  // project on screen (an update can finish after a project change).
  const markRatesDirty = (pid, dirty) => {
    if (!pid) return;
    try {
      if (dirty) localStorage.setItem(ratesDirtyKey(pid), "1");
      else localStorage.removeItem(ratesDirtyKey(pid));
    } catch {}
    if (currentProjectIdRef.current === pid) setRatesDirty(dirty);
  };
  useEffect(() => { setRatesDirty(readRatesDirty(currentProjectId)); }, [currentProjectId]); // eslint-disable-line
  // Final fix wave, item 2: a project change (sign-out and sign-in to
  // another project, since there is no in-app switcher) closes whatever job
  // screen the previous project left behind. Keyed on the id, like above.
  // resetJobScreen only touches setters and refs, so the effect needs no
  // other dependency.
  useEffect(() => { resetJobScreen(); }, [currentProjectId]); // eslint-disable-line

  // Load project data when project is selected
  useEffect(() => {
    if (!currentProject) return;
    const pid = currentProject.id;
    const cacheKey = `rb_project_${pid}`;

    // Show cached data instantly if available
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const c = JSON.parse(cached);
        setTemplates(c.tpls || []); setReports(c.reps || []); setJobs(c.jbs || []);
        setTplFolders(c.tFolders || []); setRepFolders(c.rFolders || []);
        setSheetFolders(c.sFolders || []); setSpecFolders(c.spFolders || []);
        setSheets(c.shts || []); setSpecs(c.sps || []);
        setProjectMembers(c.members || []);
      }
    } catch {}

    // Always fetch fresh data in background. Final fix wave, item 2: a
    // load that resolves after the project has changed again is dropped, so
    // one project's jobs, reports and templates never land in another's.
    let cancelled = false;
    setJobsLoadState("loading");
    const load = async () => {
      const [tpls, reps, jobsResult, tFolders, rFolders, sFolders, spFolders, shts, sps, members] = await Promise.all([
        // Final fix wave, item 11: the checked read, so a failed load is
        // known as one (jobsLoadState "error"), never shown as "no jobs".
        getTemplates(pid), getReports(pid), getJobsChecked(pid).catch((err) => ({ data: null, error: err })),
        getFolders(pid, "tpl"), getFolders(pid, "rep"),
        getFolders(pid, "sheet"), getFolders(pid, "spec"),
        getSheets(pid), getSpecs(pid),
        getProjectMembers(pid),
      ]);
      if (cancelled) return;
      const jobsLoaded = !jobsResult?.error && Array.isArray(jobsResult?.data);
      const jbs = jobsLoaded ? jobsResult.data : [];
      setJobsLoadState(jobsLoaded ? "ok" : "error");
      setTemplates(tpls); setReports(reps); setJobs(jbs);
      setTplFolders(tFolders); setRepFolders(rFolders);
      setSheetFolders(sFolders); setSpecFolders(spFolders);
      setSheets(shts); setSpecs(sps);
      setProjectMembers(members);
      // Update cache
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ tpls, reps, jbs, tFolders, rFolders, sFolders, spFolders, shts, sps, members }));
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [currentProject]); // eslint-disable-line

  // Jobs list totals (spec section 2: the list shows each job's total). A
  // frozen (Sent) job's total is read straight off job.frozen — never
  // recomputed — matching the freeze guarantee added in the Sent-freeze task.
  useEffect(() => {
    if (homeTab !== "jobs" || !currentProject) return;
    let cancelled = false;
    (async () => {
      const totals = {};
      for (const j of jobs) {
        if (isJobFrozen(j)) { const r = resolveJobRollup(j, []); totals[j.id] = { incomplete: r.incomplete, total: r.total }; continue; }
        const details = await computeJobLineDetails(j, reports, templates);
        totals[j.id] = { incomplete: details.rollup.incomplete, total: details.rollup.total };
      }
      if (!cancelled) setJobTotals(totals);
    })();
    return () => { cancelled = true; };
  }, [homeTab, jobs, reports, templates, currentProject]); // eslint-disable-line

  // Load raw bytes for active report template (needed by ExcelSheetEditor and PDF fill)
  useEffect(() => {
    if (!repTpl?.isExcelImport && !repTpl?.isPdf) { setRepTplRawBytes(null); return; }
    getRawBytesForTemplate(repTpl.id).then(bytes => setRepTplRawBytes(bytes || null));
    setActiveSheetIdx(0);
  }, [repTpl]); // eslint-disable-line

  // Parse embedded charts for an Excel-import template.
  // Single-sheet templates are included. The Template Guide recommends one sheet
  // per report type, and gating this on isMultiSheet silently dropped their charts
  // from the preview, the exported PDF and the emailed PDF.
  //
  // The chart is parsed out of the TEMPLATE file, so without the report's own
  // evaluated values it can only ever plot the template's numbers. That failed two
  // ways: a template carrying a stale numCache drew those cached points on every
  // report regardless of what was entered, and a freshly authored template with no
  // cache drew nothing at all. The report's values are computed here and handed to
  // the parser, which prefers them over the cache.
  //
  // Debounced at the same 400ms the editor uses for formula evaluation, so typing
  // does not re-read the workbook on every keystroke.
  useEffect(() => {
    if (!repTpl?.isExcelImport || !repTplRawBytes) { setChartsBySheet({}); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      getWorkbookForTemplate(repTpl.id).then(async wb => {
        if (!wb || cancelled) return;
        let values = {};
        try {
          values = evalFormulasWithReport(repTpl, repTplRawBytes, activeRep || {}) || {};
        } catch (err) {
          // A chart drawn from the template is wrong, but it is better than the
          // preview failing outright, so fall back rather than throw.
          console.warn("[charts] could not evaluate report values:", err);
        }
        const result = {};
        await Promise.all(sheetNamesForTemplate(repTpl, wb).map(async name => {
          const configs = await parseChartsForSheet(name, repTplRawBytes, wb, { values, sheet: name });
          if (configs.length) result[name] = configs;
        }));
        if (!cancelled) setChartsBySheet(result);
      });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [repTpl?.id, repTplRawBytes, activeRep]); // eslint-disable-line

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();

      // ── PDF import ───────────────────────────────────────────────────────────
      if (file.name.toLowerCase().endsWith(".pdf")) {
        const tpl = await importPdfAsTemplate(file, arrayBuffer);
        const rawBytes = new Uint8Array(arrayBuffer);
        RAW_STORE[tpl.id] = rawBytes;
        uploadTemplateFile(currentProject.id, tpl.id, rawBytes, "application/pdf").catch(console.warn);
        const saved = await upsertTemplate({ ...tpl, file_path: `${currentProject.id}/${tpl.id}` }, currentProject.id);
        if (!saved.data) { alert("Failed to save template to database. Please try again."); setImporting(false); return; }
        setTemplates(prev => [...prev, saved.data]);
        alert(`✓ PDF template "${tpl.name}" imported with ${tpl.sections.reduce((n, s) => n + s.fields.length, 0)} fields!`);
        setImporting(false);
        e.target.value = "";
        return;
      }

      // Detect if this is the Proctor template by checking for known sheet names
      const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: "array", cellFormula: true, cellStyles: true });
      const isProctor = wb.SheetNames.some(n => /^proctor/i.test(n)) && wb.SheetNames.includes("Formulas");

      {
        // Use the regular Excel parser for all templates including Proctor
        const result = await importExcelAsTemplate(file, arrayBuffer);
        // Auto-import only when there's exactly 1 visible sheet total
        if ((result.visibleNames || result.templates).length === 1 && result.templates.length === 1) {
          const { _workbook, ...tplClean } = result.templates[0];
          if (isProctor) tplClean.isProctor = true;
          if (result.workbook) WB_STORE[tplClean.id] = result.workbook;
          RAW_STORE[tplClean.id] = new Uint8Array(arrayBuffer);
          uploadTemplateFile(currentProject.id, tplClean.id, new Uint8Array(arrayBuffer)).catch(console.warn);
          const saved = await upsertTemplate({ ...tplClean, file_path: `${currentProject.id}/${tplClean.id}` }, currentProject.id);
          if (!saved.data) { alert("Failed to save template to database. Please try again."); setImporting(false); return; }
          setTemplates(prev => [...prev, saved.data]);
          alert(`✓ Template "${tplClean.name}" imported!`);
        } else {
          setImportModal({
            templates: result.templates.map(t => isProctor ? { ...t, isProctor: true } : t),
            visibleNames: result.visibleNames,
            workbook: result.workbook,
            rawBytes: arrayBuffer,
            sheetNames: result.sheetNames,
            fileName: file.name,
          });
        }
      }
    } catch (err) {
      alert("Could not parse Excel file: " + err.message);
    }
    setImporting(false);
    e.target.value = "";
  };

  const confirmImport = async (tpl) => {
    const { _workbook, ...tplClean } = tpl;
    if (importModal.workbook) WB_STORE[tplClean.id] = importModal.workbook;
    if (importModal.rawBytes) {
      RAW_STORE[tplClean.id] = new Uint8Array(importModal.rawBytes);
      uploadTemplateFile(currentProject.id, tplClean.id, new Uint8Array(importModal.rawBytes)).catch(console.warn);
    }
    const saved = await upsertTemplate({ ...tplClean, file_path: `${currentProject.id}/${tplClean.id}` }, currentProject.id);
    if (!saved.data) { alert("Failed to save template to database. Please try again."); return; }
    setTemplates(prev => [...prev, saved.data]);
    setImportModal(null);
  };

  const confirmImportAll = async () => {
    const results = await Promise.all(importModal.templates.map(async ({ _workbook, ...t }) => {
      if (importModal.workbook) WB_STORE[t.id] = importModal.workbook;
      if (importModal.rawBytes) {
        RAW_STORE[t.id] = new Uint8Array(importModal.rawBytes);
        uploadTemplateFile(currentProject.id, t.id, new Uint8Array(importModal.rawBytes)).catch(console.warn);
      }
      const saved = await upsertTemplate({ ...t, file_path: `${currentProject.id}/${t.id}` }, currentProject.id);
      return saved.data || null;
    }));
    const saved = results.filter(Boolean);
    if (saved.length === 0) { alert("Failed to save templates to database. Please try again."); return; }
    if (saved.length < importModal.templates.length) alert(`${importModal.templates.length - saved.length} template(s) failed to save.`);
    setTemplates(prev => [...prev, ...saved]);
    setImportModal(null);
  };

  const confirmImportMultiSheet = async () => {
    const defaultName = importModal.fileName?.replace(/\.[^.]+$/, "") || "Multi-Sheet Template";
    const name = window.prompt("Name for this multi-sheet template:", defaultName);
    if (!name?.trim()) return;
    const id = crypto.randomUUID();
    // Include only sheets that have input fields (others are formula/helper sheets)
    const sheets = importModal.templates.map(({ _workbook, ...t }) => ({
      name: t.name,
      sections: t.sections,
    }));
    const tplClean = {
      id,
      name: name.trim(),
      description: `Multi-sheet import — ${importModal.fileName || ""}`,
      isExcelImport: true,
      isMultiSheet: true,
      sheets,
      sections: [],
    };
    if (importModal.workbook) WB_STORE[id] = importModal.workbook;
    if (importModal.rawBytes) {
      RAW_STORE[id] = new Uint8Array(importModal.rawBytes);
      uploadTemplateFile(currentProject.id, id, new Uint8Array(importModal.rawBytes)).catch(console.warn);
    }
    const saved = await upsertTemplate({ ...tplClean, file_path: `${currentProject.id}/${id}` }, currentProject.id);
    if (!saved.data) { alert("Failed to save template to database. Please try again."); return; }
    setTemplates(prev => [...prev, saved.data]);
    setImportModal(null);
  };

  // Download the ReportForge template guide
  const downloadStarterTemplate = () => {
    const wb = XLSX.utils.book_new();
    const guide = XLSX.utils.aoa_to_sheet([
      ["REPORTFORGE TEMPLATE GUIDE"],
      [],
      ["════════════════════════════════════════════════════════════"],
      ["CELL COLOR SYSTEM — how fill colors control the preview layout"],
      ["════════════════════════════════════════════════════════════"],
      [],
      ["1. OPTIONAL INPUT CELLS (yellow fill = #FFFBCC)"],
      ["   - Fill the cell background with yellow (#FFFBCC)."],
      ["   - These become the editable fields users fill in when creating a report."],
      ["   - Do NOT put formulas in yellow cells — user input overwrites them."],
      ["   - In preview and PDF, yellow is stripped and the cell shows the entered value."],
      ["   - IMPORTANT: A data row is only shown in the preview if at least one of its"],
      ["     input cells (yellow or red) has been filled in. Empty rows are hidden."],
      [],
      ["2. REQUIRED INPUT CELLS (red fill — any shade of red)"],
      ["   - Fill the cell background with red or any red shade."],
      ["     Examples: #FF0000, #CC0000, #FF9999, #FFC7CE (light red), #8B0000 (dark red)"],
      ["   - Behaves exactly like a yellow input cell, BUT is marked as required."],
      ["   - In the report editor, required cells show with a pink/red background."],
      ["   - When saving: if any required fields are empty, the app shows a warning"],
      ["     popup listing the missing fields by name. The user can still save anyway."],
      ["   - Reports saved with missing required fields show an ACTION NEEDED badge"],
      ["     in the reports list. Once all required fields are filled and re-saved,"],
      ["     the badge disappears."],
      ["   - In preview and PDF, red fill is stripped just like yellow."],
      ["   - Use red for fields that MUST be filled before a report is considered complete."],
      [],
      ["3. SECTION HEADER ROWS (BLACK fill only) — THE KEY TO SECTION SEPARATION"],
      ["   - Fill EVERY cell in the row with BLACK or near-black (very dark, close to"],
      ["     #000000). Only truly black fills trigger section breaks."],
      ["   - IMPORTANT: Dark grey and dark blue fills do NOT trigger section bars —"],
      ["     they stay as normal rows. This lets you freely use grey/blue for column"],
      ["     headers without accidentally splitting the layout."],
      ["   - IMPORTANT: ALL cells in the row must be black. If even one cell is light,"],
      ["     the row is treated as a data row, not a section break."],
      ["   - The preview pulls this row OUT of the table and renders it as a full-width"],
      ["     dark bar above the next section's table — exactly like the Proctor report."],
      ["   - Each dark bar row starts a NEW section, visually separated by whitespace."],
      ["   - Use for titles like 'COMPACTION TEST DATA', 'RESULTS', 'FIELD DENSITY'."],
      ["   - Example hex codes: #000000  #111111  #1a1a1a  #0d0d0d  #222222"],
      ["   - Tip: merge the cells across the full row width so it looks like one bar."],
      ["   - Tip: any rows BEFORE the first black bar row form an unlabeled top section."],
      [],
      ["4. COLUMN HEADER ROWS (medium / non-white fill)"],
      ["   - Fill with any medium color: grey, light blue, tan, olive, etc."],
      ["   - As long as the fill is not dark, not yellow, and not white, it reads as a header."],
      ["   - The preview renders these cells as bold and centered with black text."],
      ["   - Use for column labels like 'Section', 'Item #', 'Inspection Point', 'Status'."],
      ["   - Example hex codes: #d2d2d2  #c0c0c0  #bfdbfe  #d1fae5  #fef3c7"],
      [],
      ["5. FORMULA / CALCULATED CELLS (white or no fill)"],
      ["   - Leave formula cells white or with no fill."],
      ["   - The app recalculates them automatically when the user fills in input cells."],
      ["   - Supported functions: SUM, AVERAGE, ROUND, IFERROR, IF, COUNT, MAX, MIN"],
      ["   - Cell references (A1), ranges (A1:A10), and absolute refs ($A$1) all work."],
      ["   - Number format set in Excel (decimal places, %) is preserved in the preview."],
      ["   - Error values like #VALUE! and #DIV/0! are automatically hidden in preview."],
      [],
      ["6. LABEL / STATIC TEXT CELLS (white or no fill, bold)"],
      ["   - Static labels like 'Project:', 'Date:', 'Test Method:' use no fill."],
      ["   - Bold text with no fill. Place directly to the LEFT of the yellow input cell."],
      ["   - The app reads the label text to name each input field in the edit form."],
      [],
      ["════════════════════════════════════════════════════════════"],
      ["PREVIEW LAYOUT"],
      ["════════════════════════════════════════════════════════════"],
      [],
      ["   - All previews use a clean white paper-card layout with a dark title bar."],
      ["   - The report title (entered by the user) appears in the dark bar at the top."],
      ["   - The sheet is split into SECTIONS separated by whitespace:"],
      ["       * Each BLACK-fill row becomes a section header bar (full width, white text)."],
      ["         IMPORTANT: Only near-black fills (very dark, close to #000000) trigger"],
      ["         section bars. Dark grey or dark blue fills do NOT — they stay as normal"],
      ["         column header rows. Use BLACK fills for section bars only."],
      ["       * The data rows between section bars form a table below that bar."],
      ["       * Sections are spaced apart — no content bleeds into the next section."],
      ["   - Data rows alternate between white (#ffffff) and light grey (#f8f8f8)."],
      ["   - Alternating colors RESET at the start of each new section."],
      ["   - Blank/unfilled data rows are automatically hidden (no extra whitespace)."],
      [],
      ["════════════════════════════════════════════════════════════"],
      ["SHEET SETUP"],
      ["════════════════════════════════════════════════════════════"],
      [],
      ["   - Keep the sheet tab name under 31 characters."],
      ["   - One sheet per report type is recommended."],
      ["   - The sheet name becomes the template name in the app."],
      ["   - Column widths and row heights set in Excel are preserved in the preview."],
      [],
      ["════════════════════════════════════════════════════════════"],
      ["WORKFLOW"],
      ["════════════════════════════════════════════════════════════"],
      [],
      ["   1. Design your report layout in Excel using the color rules above."],
      ["      - Yellow cells = optional input fields"],
      ["      - Red cells = required input fields (must be filled before saving)"],
      ["      - Black rows = section header bars"],
      ["      - Medium fills = column headers"],
      ["   2. Save as .xlsx and import it into ReportForge as a Template."],
      ["   3. Create a Report from that Template — fill in the input fields."],
      ["      - Yellow fields: optional. Red fields: required (pink/red background)."],
      ["   4. Formula cells recalculate automatically in the preview as you type."],
      ["   5. Empty rows (no inputs filled in) are hidden automatically."],
      ["   6. Save the report. If any required (red) fields are empty:"],
      ["      - A warning popup lists the missing fields by name."],
      ["      - You can go back and fill them, or save anyway."],
      ["      - If saved with missing required fields, the report shows ACTION NEEDED."],
      ["      - Fill the missing fields and re-save to clear the ACTION NEEDED badge."],
      ["   7. Export as PDF or Excel when done."],
      [],
      ["════════════════════════════════════════════════════════════"],
      ["TIPS"],
      ["════════════════════════════════════════════════════════════"],
      [],
      ["   - Test your formulas in Excel before importing — make sure they calculate"],
      ["     correctly when the input cells are empty AND when filled."],
      ["   - Set number formats in Excel (e.g. 2 decimal places) — they carry over"],
      ["     to the preview and PDF automatically."],
      ["   - Merge dark header cells across the full table width for the cleanest look."],
      ["   - Rows that have ONLY formula cells (no input cells) and all formulas"],
      ["     evaluate to zero are also hidden — useful for conditional rows."],
      ["   - If a row you expect to see is hidden, check that at least one of its"],
      ["     input cells (yellow or red) has a value entered."],
      ["   - Each section in the preview is automatically trimmed to the width of"],
      ["     its own content — sparse sections (like summary rows) won't stretch"],
      ["     to the width of wider sections above them."],
      ["   - Use red sparingly — mark only the fields that are truly mandatory."],
      ["     Too many red fields can make the form feel overwhelming."],
    ]);
    guide["!cols"] = [{ wch: 100 }];
    XLSX.utils.book_append_sheet(wb, guide, "Template Guide");
    XLSX.writeFile(wb, "ReportForge-TemplateGuide.xlsx");
  };

  // Get raw xlsx bytes — used by Proctor export to preserve native chart
  const getRawBytesForTemplate = async (tplId) => {
    if (RAW_STORE[tplId]) return RAW_STORE[tplId];
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl?.file_path) return null;
    const bytes = await downloadTemplateFile(tpl.file_path);
    if (bytes) RAW_STORE[tplId] = bytes;
    return bytes;
  };

  // Reconstruct workbook from Supabase storage if not in memory
  const getWorkbookForTemplate = async (tplId) => {
    if (WB_STORE[tplId]) return WB_STORE[tplId];
    const bytes = await getRawBytesForTemplate(tplId);
    if (!bytes) return null;
    const wb = XLSX.read(bytes, { type: "array", cellFormula: true });
    WB_STORE[tplId] = wb;
    return wb;
  };

  const doFlash = () => { setFlash(true); setTimeout(() => setFlash(false), 1800); };

  // ── Folder helpers ────────────────────────────────────────────────────────────
  const createFolder = async (kind, name, parentId = null) => {
    if (!name) return;
    const { data } = await dbCreateFolder(currentProject.id, kind, name, parentId);
    if (!data) return;
    if (kind === "tpl") setTplFolders(f => [...f, data]);
    else if (kind === "rep") setRepFolders(f => [...f, data]);
    else if (kind === "sheet") setSheetFolders(f => [...f, data]);
    else if (kind === "spec") setSpecFolders(f => [...f, data]);
  };

  const renameFolder = async (id, name) => {
    if (!name) return;
    await dbRenameFolder(id, name);
    const update = arr => arr.map(f => f.id === id ? { ...f, name } : f);
    setTplFolders(update); setRepFolders(update); setSheetFolders(update); setSpecFolders(update);
  };

  const deleteFolder = async (kind, id) => {
    // Gather all descendant IDs so we can cascade state cleanup client-side
    const allFolders = kind === "tpl" ? tplFolders : kind === "rep" ? repFolders : kind === "sheet" ? sheetFolders : specFolders;
    const toDelete = getDescendantIds(allFolders, id);
    await dbDeleteFolder(id); // DB cascades child folder rows; items.folder_id set null by FK
    if (kind === "tpl") {
      setTplFolders(f => f.filter(x => !toDelete.has(x.id)));
      setTemplates(t => t.map(x => toDelete.has(x.folderId) ? { ...x, folderId: null } : x));
      if (toDelete.has(activeTplFolder)) setActiveTplFolder(null);
    } else if (kind === "rep") {
      setRepFolders(f => f.filter(x => !toDelete.has(x.id)));
      setReports(r => r.map(x => toDelete.has(x.folderId) ? { ...x, folderId: null } : x));
      if (toDelete.has(activeRepFolder)) setActiveRepFolder(null);
    } else if (kind === "sheet") {
      setSheetFolders(f => f.filter(x => !toDelete.has(x.id)));
      setSheets(s => s.map(x => toDelete.has(x.folderId) ? { ...x, folderId: null } : x));
      if (toDelete.has(activeSheetFolder)) setActiveSheetFolder(null);
    } else {
      setSpecFolders(f => f.filter(x => !toDelete.has(x.id)));
      setSpecs(s => s.map(x => toDelete.has(x.folderId) ? { ...x, folderId: null } : x));
      if (toDelete.has(activeSpecFolder)) setActiveSpecFolder(null);
    }
  };

  const moveToFolder = async (kind, itemId, folderId) => {
    if (kind === "tpl") {
      const tpl = templates.find(t => t.id === itemId);
      await upsertTemplate({ ...tpl, folderId }, currentProject.id);
      setTemplates(t => t.map(x => x.id === itemId ? { ...x, folderId: folderId || null } : x));
    } else {
      const rep = reports.find(r => r.id === itemId);
      await upsertReport({ ...rep, folderId }, currentProject.id);
      setReports(r => r.map(x => x.id === itemId ? { ...x, folderId: folderId || null } : x));
    }
    setMoveMenu(null);
  };

  // ── Sheets helpers ────────────────────────────────────────────────────────────
  const importSheet = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.type !== "application/pdf") continue;
      const buf = await file.arrayBuffer();
      const id = crypto.randomUUID();
      const name = file.name.replace(/\.pdf$/i, "");
      const { data, error } = await uploadSheet(currentProject.id, id, name, activeSheetFolder, buf);
      if (!data) { alert(`Failed to upload "${file.name}". Please try again.` + (error ? ` (${error.message})` : "")); continue; }
      setSheets(prev => [...prev, { id, name, folderId: activeSheetFolder || null, file_path: `${currentProject.id}/${id}.pdf`, size: file.size, uploadedAt: new Date().toISOString() }]);
    }
    e.target.value = "";
  };

  const deleteSheet = async (id) => {
    const sheet = sheets.find(s => s.id === id);
    await deleteSheetRecord(id, sheet?.file_path);
    setSheets(prev => prev.filter(s => s.id !== id));
    if (viewingSheet?.id === id) closeSheetViewer();
  };

  const openSheetViewer = async (sheet) => {
    const buf = await downloadSheet(sheet.file_path);
    if (!buf) return;
    const blob = new Blob([buf], { type: "application/pdf" });
    setSheetBlobUrl(URL.createObjectURL(blob));
    setViewingSheet(sheet);
  };

  const closeSheetViewer = () => {
    if (sheetBlobUrl) URL.revokeObjectURL(sheetBlobUrl);
    setSheetBlobUrl(null); setViewingSheet(null);
  };

  const moveSheetToFolder = async (sheetId, folderId) => {
    await updateSheetMeta(sheetId, { folder_id: folderId || null });
    setSheets(s => s.map(x => x.id === sheetId ? { ...x, folderId: folderId || null } : x));
    setMoveMenu(null);
  };


  // ── Specs helpers ─────────────────────────────────────────────────────────────
  const importSpec = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.type !== "application/pdf") continue;
      const buf = await file.arrayBuffer();
      const id = crypto.randomUUID();
      const name = file.name.replace(/\.pdf$/i, "");
      const { data, error } = await uploadSpec(currentProject.id, id, name, activeSpecFolder, buf);
      if (!data) { alert(`Failed to upload "${file.name}". Please try again.` + (error ? ` (${error.message})` : "")); continue; }
      setSpecs(prev => [...prev, { id, name, folderId: activeSpecFolder || null, file_path: `${currentProject.id}/${id}.pdf`, size: file.size, uploadedAt: new Date().toISOString() }]);
    }
    e.target.value = "";
  };

  const deleteSpec = async (id) => {
    const spec = specs.find(s => s.id === id);
    await deleteSpecRecord(id, spec?.file_path);
    setSpecs(prev => prev.filter(s => s.id !== id));
    if (viewingSpec?.id === id) closeSpecViewer();
  };

  const openSpecViewer = async (spec) => {
    const buf = await downloadSpec(spec.file_path);
    if (!buf) return;
    const blob = new Blob([buf], { type: "application/pdf" });
    setSpecBlobUrl(URL.createObjectURL(blob));
    setViewingSpec(spec);
  };

  const closeSpecViewer = () => {
    if (specBlobUrl) URL.revokeObjectURL(specBlobUrl);
    setSpecBlobUrl(null); setViewingSpec(null);
  };

  const moveSpecToFolder = async (specId, folderId) => {
    await updateSpecMeta(specId, { folder_id: folderId || null });
    setSpecs(s => s.map(x => x.id === specId ? { ...x, folderId: folderId || null } : x));
    setMoveMenu(null);
  };


  // ── Auth helpers ──────────────────────────────────────────────────────────────
  const login = async () => {
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await Promise.race([
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/token?grant_type=password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ email: loginForm.email, password: loginForm.password }),
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out — check your internet connection")), 15000)),
      ]);
      const data = await res.json();
      if (!res.ok || data.error) {
        setLoginError(data.error_description || data.msg || data.error || "Login failed. Check your email and password.");
        return;
      }
      // Write session to localStorage in Supabase's expected format, then reload
      // This bypasses a known hang in supabase-js v2 signInWithPassword/setSession
      const projectRef = (process.env.REACT_APP_SUPABASE_URL || "").match(/\/\/([^.]+)\./)?.[1];
      if (projectRef) {
        localStorage.setItem(`sb-${projectRef}-auth-token`, JSON.stringify({
          access_token: data.access_token,
          token_type: data.token_type || "bearer",
          expires_in: data.expires_in,
          expires_at: data.expires_at,
          refresh_token: data.refresh_token,
          user: data.user,
        }));
      }
      window.location.reload();
    } catch (err) {
      setLoginError("Connection error: " + err.message);
      setLoginLoading(false);
    }
  };
  const logout = async () => {
    await signOut();
    setScreen("home"); setHomeTab("reports");
  };

  // ── Gmail connect / send ──────────────────────────────────────────────────
  const connectGmail = () => {
    if (!currentUser) return;
    const state = btoa(JSON.stringify({
      user_id:    currentUser.id,
      return_url: window.location.origin + window.location.pathname,
    })).replace(/=/g, "");
    const params = new URLSearchParams({
      client_id:     "990714388163-sa5uu76sn2mimej5ii50aamqa0nlds5o.apps.googleusercontent.com",
      redirect_uri:  "https://reportforge.tech/api/gmail-auth",
      response_type: "code",
      scope:         "https://www.googleapis.com/auth/gmail.send email profile",
      access_type:   "offline",
      prompt:        "consent",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/auth?${params}`;
  };

  const disconnectGmail = async () => {
    if (!currentUser) return;
    await supabase.from("email_connections").delete().eq("user_id", currentUser.id).eq("provider", "gmail");
    setGmailEmail(null);
  };

  const blobToBase64 = (blob) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload  = () => res(reader.result.split(",")[1]);
    reader.onerror = rej;
    reader.readAsDataURL(blob);
  });

  const sendEmailReport = async ({ to, cc, subject, message }) => {
    let pdfBase64 = null;
    const filename = `${activeRep?.name || activeRep?.reportTitle || "report"}.pdf`;
    try {
      if (repTpl?.isPdf && repTplRawBytes) {
        // Native PDF template — use filled PDF blob
        const blobUrl = await getFilledPdfBlobUrl(repTplRawBytes, activeRep, repTpl);
        const blob    = await fetch(blobUrl).then(r => r.blob());
        URL.revokeObjectURL(blobUrl);
        pdfBase64 = await blobToBase64(blob);
      } else if (repTpl?.isProctor) {
        // Proctor template
        const chartImg = await generateProctorChartImage(computeTrials(activeRep, repTpl));
        const blob = exportProctorToPDF(activeRep, repTpl, chartImg, true);
        pdfBase64 = await blobToBase64(blob);
      } else if (repTpl?.isExcelImport) {
        // Excel import template — try ExcelPDF first, fall back to jsPDF
        const rawBytes = await getRawBytesForTemplate(repTpl.id);
        const chartImagesBySheet = {};
        for (const [sheetName, cfgs] of Object.entries(chartsBySheet)) {
          chartImagesBySheet[sheetName] = await Promise.all(
            cfgs.map(cfg => chartConfigToDataUrl(cfg, cfg.options?.plugins?.title?.text || ""))
          );
        }
        const excelBlob = rawBytes ? await exportReportAsExcelPDF(activeRep, repTpl, rawBytes, jsPDF, chartImagesBySheet, true) : null;
        const blob = excelBlob || generateExcelTemplatePDF(activeRep, repTpl, true);
        pdfBase64 = await blobToBase64(blob);
      } else {
        // Standard custom-form template
        const blob = generateGenericPDF(activeRep, repTpl, true);
        pdfBase64 = await blobToBase64(blob);
      }
    } catch (err) {
      console.warn("PDF generation for email failed:", err);
    }
    const { data: { session } } = await supabase.auth.getSession();
    const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${session.access_token}`,
        apikey:          process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ to, cc: cc || undefined, subject, message, pdf_base64: pdfBase64, filename }),
    });
    const result = await resp.json();
    if (!resp.ok) throw new Error(result.error || "Failed to send email");
    return result;
  };
  const [creatingUser, setCreatingUser] = useState(false);
  const createUserHandler = async () => {
    const { email, password, fullName, role } = newUserForm;
    if (!email.trim() || !password.trim()) { setUserMgmtError("Email and password are required."); return; }
    setCreatingUser(true);
    setUserMgmtError("");
    const result = await createUser(email.trim(), password, fullName.trim(), role, currentProject?.id);
    setCreatingUser(false);
    if (result.error) { setUserMgmtError(result.error); return; }
    // Refresh members
    const members = await getProjectMembers(currentProject.id);
    setProjectMembers(members);
    setNewUserForm({ email: "", password: "", fullName: "", role: "manager" });
  };
  const deleteUserHandler = async (userId) => {
    await removeMember(currentProject.id, userId);
    setProjectMembers(prev => prev.filter(m => m.user_id !== userId));
  };
  const changeRoleHandler = async (userId, role) => {
    await changeMemberRole(currentProject.id, userId, role);
    setProjectMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
  };

  const myRole = currentUser?.is_super_admin ? "admin" :
    (projectMembers.find(m => m.user_id === currentUser?.id)?.role || currentUser?.role || "base");
  const isAdmin = myRole === "admin" || currentUser?.is_super_admin;
  const isManager = myRole === "manager";
  const isBase = myRole === "base";
  const isSuperAdmin = currentUser?.is_super_admin;
  const canEditReports = isAdmin || isManager;
  // Task 8: the per-client vocabulary override (clients.config.vocabulary),
  // always {} today since no clients table exists on this branch yet. One
  // object, threaded to every label() call and every component below that
  // takes a vocabulary prop, so a real value only ever needs to be supplied
  // here once.
  const vocabulary = {};
  // Takeoffs behind a sent bid (Task 6, ruling R17): opened read-only, never
  // saved or deleted, because changing one would silently change the
  // evidence behind a bid that has already gone out. activeJob is included
  // so the job on screen counts even before its jobs-list entry refreshes.
  // Final fix wave, item 4 (ruling R27): the Jobs module only exists in a
  // project that has an estimating template (jobMath.projectHasEstimatingTemplates).
  // Everywhere else it is hidden from the sidebar AND blocked at its routes:
  // the Jobs tab and the Job screen do not render, and the effect below
  // sends anyone who is on either one back to Reports (the client-sites
  // spec's rule for a disabled module).
  const jobsEnabled = projectHasEstimatingTemplates(templates);
  useEffect(() => {
    if (jobsEnabled) return;
    if (screen === "jobScreen") { resetJobScreen(); setHomeTab("reports"); return; }
    if (homeTab === "jobs") setHomeTab("reports");
  }, [jobsEnabled, screen, homeTab]); // eslint-disable-line

  // Tab manager: which modules are enabled for this project.
  // null means all enabled (default, backward compatible).
  const enabledModules = currentProject?.config?.enabledModules ?? null;
  const [showTabManager, setShowTabManager] = useState(false);
  const isModuleVisible = (key) =>
    (!MODULE_META[key].adminOnly || isAdmin) &&
    (key !== "jobs" || jobsEnabled) &&
    (enabledModules == null || enabledModules.includes(key));
  // Redirect off a tab that was just disabled.
  useEffect(() => {
    if (!currentProject) return;
    if (homeTab && !isModuleVisible(homeTab)) setHomeTab("reports");
  }, [enabledModules, homeTab, isAdmin, jobsEnabled]); // eslint-disable-line
  const sentTakeoffIds = useMemo(() => collectSentTakeoffIds(activeJob ? [...jobs, activeJob] : jobs), [jobs, activeJob]);
  const activeRepLocked = !!activeRep && sentTakeoffIds.has(activeRep.id);
  // Final fix wave, item 11: sentTakeoffIds is only trustworthy once the
  // jobs are loaded. Until then (or when the load failed) a destructive
  // action on a takeoff or template refuses instead of assuming no sent
  // jobs (jobMath.jobsUnknownMessage). Returns true when it refused.
  const refuseIfJobsUnknown = () => {
    const msg = jobsUnknownMessage(jobsLoadState, jobsEnabled);
    if (msg) alert(msg);
    return !!msg;
  };

  const startNewTemplate = () => { setActiveTpl({ id: crypto.randomUUID(), name: "", description: "", folderId: activeTplFolder || null, sections: [{ id: crypto.randomUUID(), name: "Section 1", fields: [] }] }); setScreen("buildTemplate"); };
  const editTemplate = (t) => { setActiveTpl(JSON.parse(JSON.stringify(t))); setScreen("buildTemplate"); };
  const saveTemplate = async () => { const { data } = await upsertTemplate(activeTpl, currentProject.id); const saved = data || activeTpl; setTemplates(prev => prev.some(t => t.id === saved.id) ? prev.map(t => t.id === saved.id ? saved : t) : [...prev, saved]); doFlash(); };
  const deleteTpl = async (id) => {
    if (refuseIfJobsUnknown()) return;
    // Task 6 fix round 1: a template behind a sent bid's takeoff stays.
    const sentJob = findSentJobUsingTemplate(jobs, reports, id);
    if (sentJob) { alert(`This template is used by a takeoff in the sent bid "${sentJob.name || "Untitled Job"}", so it cannot be deleted.`); return; }
    await dbDeleteTemplate(id); setTemplates(prev => prev.filter(t => t.id !== id)); setReports(prev => prev.filter(r => r._templateId !== id));
  };

  const setTF = (k, v) => setActiveTpl(t => ({ ...t, [k]: v }));
  const addSec = () => setActiveTpl(t => ({ ...t, sections: [...t.sections, { id: crypto.randomUUID(), name: `Section ${t.sections.length + 1}`, fields: [] }] }));
  const updSec = (sid, k, v) => setActiveTpl(t => ({ ...t, sections: t.sections.map(s => s.id === sid ? { ...s, [k]: v } : s) }));
  const remSec = (sid) => setActiveTpl(t => ({ ...t, sections: t.sections.filter(s => s.id !== sid) }));
  const addFld = (sid) => setActiveTpl(t => ({ ...t, sections: t.sections.map(s => s.id === sid ? { ...s, fields: [...s.fields, { id: crypto.randomUUID(), label: "", type: "text", options: "", defaultValue: "", required: false }] } : s) }));
  const updFld = (sid, fid, k, v) => setActiveTpl(t => ({ ...t, sections: t.sections.map(s => s.id === sid ? { ...s, fields: s.fields.map(f => f.id === fid ? { ...f, [k]: v } : f) } : s) }));
  const remFld = (sid, fid) => setActiveTpl(t => ({ ...t, sections: t.sections.map(s => s.id === sid ? { ...s, fields: s.fields.filter(f => f.id !== fid) } : s) }));

  const startReport = (tpl) => {
    const defaults = {};
    const allSections = tpl.isMultiSheet
      ? (tpl.sheets || []).flatMap(sh => sh.sections || [])
      : (tpl.sections || []);
    allSections.forEach(sec => sec.fields?.forEach(f => { if (f.defaultValue) defaults[f.id] = f.defaultValue; }));
    setRepTpl(tpl);
    setActiveRep({ id: crypto.randomUUID(), _templateId: tpl.id, _templateName: tpl.name, _date: new Date().toISOString().split("T")[0], _author: currentUser?.username || "", reportTitle: "", folderId: activeRepFolder || null, ...defaults });
    setTab("edit");
    setScreen("fillReport");
  };
  const openReport = (r) => { setRepTpl(templates.find(t => t.id === r._templateId) || null); setActiveRep(JSON.parse(JSON.stringify(r))); setTab("edit"); setScreen("fillReport"); };

  // Evaluates every line of a job against its takeoff report and rolls the
  // job up. A frozen (Sent) job short-circuits to its frozen figures and is
  // never recomputed.
  //
  // Fix round 1, finding #6 (second half): a job's stored markupPercent is
  // re-validated here, not trusted as-is — a non-numeric legacy value (or
  // any other bad data that slipped in before this fix existed) now marks
  // the whole job incomplete instead of computing straight through to a
  // "$NaN" total. `line.quantity ?? 1` (not `|| 1`) because by the time a
  // line reaches this function its quantity has already been through
  // parseLineQuantity at the one place it's ever written (changeJobLineQuantity)
  // — `?? 1` here only guards genuinely missing data (an old line saved
  // before quantity existed at all), it is never a stand-in for validation.
  const computeJobLineDetails = async (job, allReports, allTemplates) => {
    // A frozen (Sent) job never touches its takeoffs, templates or rates:
    // its lines and totals come straight off job.frozen (resolveJobRollup).
    // A job marked Sent with no frozen figures shows no lines and no total
    // (rollup.frozenError explains), never a live reprice.
    if (isJobFrozen(job)) {
      const rollup = resolveJobRollup(job, []);
      return {
        lines: (job.frozen?.lines || []).map((fl) => ({ line: fl, status: "ok", lineSubtotal: fl.lineSubtotal, markedUpAmount: fl.markedUpAmount })),
        rollup,
      };
    }
    const results = [];
    for (const line of job.lines || []) {
      const report = allReports.find((r) => r.id === line.reportId) || null;
      const template = report ? allTemplates.find((t) => t.id === report._templateId) || null : null;
      const status = resolveLineStatus(line, report, template);
      if (status.status !== "ok") {
        results.push({ line, status: status.status, lineSubtotal: null, missingFields: status.missingFields, submitted: !!report?._submitted });
        continue;
      }
      // Final fix wave, item 1: the takeoff's "Number of units..." field
      // and line.quantity must agree, or the line is incomplete and the job
      // cannot be priced, previewed or sent (fail closed, never an underbid).
      const qtyProblem = quantityDivergenceProblem(line, report, template);
      if (qtyProblem) {
        results.push({ line, status: "incomplete", lineSubtotal: null, error: qtyProblem, submitted: !!report?._submitted });
        continue;
      }
      try {
        const rawBytes = await getRawBytesForTemplate(template.id);
        const wb = XLSX.read(rawBytes, { type: "array", cellFormula: true, cellStyles: true });
        const ws = resolveMainSheet(wb, template);
        const evalResults = evalFormulasWithReport(template, rawBytes, report);
        const reportSubtotal = readSubtotalFromResults(ws, evalResults);
        const lineSubtotal = computeLineSubtotal(reportSubtotal, line.quantity ?? 1);
        results.push({ line, status: "ok", lineSubtotal, submitted: !!report?._submitted });
      } catch (err) {
        results.push({ line, status: "incomplete", lineSubtotal: null, error: err.message, submitted: !!report?._submitted });
      }
    }
    // resolveJobRollup (jobMath.js) owns the incomplete gate, including the
    // stored-markup re-validation described above.
    const rollup = resolveJobRollup(job, results.map((r) => ({ id: r.line.id, status: r.status, lineSubtotal: r.lineSubtotal })));
    const linesWithAmounts = rollup.incomplete
      ? results
      : results.map((r) => ({ ...r, markedUpAmount: rollup.lines.find((l) => l.id === r.line.id)?.markedUpAmount }));
    return { lines: linesWithAmounts, rollup };
  };

  // Final fix wave, item 3 (ruling R28): the rates check Mark Sent and the
  // draft preview run before anything else happens. Compares the current
  // catalog, without writing anything, with the rate cells baked into every
  // estimating template this job's lines use (rateCellPatch.checkRatesCurrent),
  // on the same stored bytes the line totals are computed from. Covers every
  // session, not just the browser that changed the rates.
  const checkRatesCurrentForJob = (job) => {
    const templateIds = new Set();
    for (const line of job.lines || []) {
      const report = reportsRef.current.find((r) => r.id === line.reportId);
      if (report?._templateId) templateIds.add(report._templateId);
    }
    const projectId = currentProject.id;
    return checkRatesCurrent({
      loadCatalogItems: () => getRateCatalogItemsOrThrow(projectId),
      templates: templatesRef.current
        .filter((t) => templateIds.has(t.id) && t.isExcelImport && !t.isMultiSheet)
        .map((t) => ({ name: t.name, loadBytes: () => getRawBytesForTemplate(t.id) })),
    });
  };
  // The refusal for a failed rates check. A stale result also raises the
  // banner for this project.
  const ratesCheckMessage = (check) => {
    if (check.reason === "stale") markRatesDirty(currentProject.id, true);
    const shown = (check.differences || []).slice(0, 5);
    return check.message + (shown.length ? `\n\n${shown.join("\n")}` : "");
  };

  // "Update unit types" on the Rates tab: bakes the current Rate Catalog
  // into every stored estimating template's rate cells (src/rateCellPatch.js).
  // Candidates are filtered down to actual estimating templates via
  // isEstimatingTemplate, so an unrelated template (an ASTM one, say) is
  // skipped and can never block the ones that qualify. Every candidate is
  // patched, re-imported and evaluated BEFORE anything is uploaded; one
  // failure uploads nothing (planUnitTypeUpdate). The uploads themselves are
  // separate storage writes, so true atomicity across them isn't possible:
  // if one fails partway, the alert says exactly which templates were and
  // weren't updated, and RAW_STORE only ever takes bytes that uploaded.
  const updateUnitTypes = async () => {
    if (!currentProject || updatingUnitTypes) return;
    const pid = currentProject.id;
    setUpdatingUnitTypes(true);
    try {
      let items;
      try {
        items = await getRateCatalogItemsOrThrow(currentProject.id);
      } catch (err) {
        alert(`Could not load the rate catalog, so nothing was updated.\n\n${err.message || String(err)}`);
        return;
      }
      const catalog = buildRateCatalog(items);
      const results = [];
      const patched = [];
      for (const t of templates.filter((tpl) => tpl.isExcelImport && !tpl.isMultiSheet)) {
        const rawBytes = await getRawBytesForTemplate(t.id);
        if (!rawBytes) {
          // No stored file at all: not a candidate. A stored file that
          // failed to download is a failure, never a silent skip, since it
          // may well be an estimating template that would stay stale.
          if (t.file_path) results.push({ templateId: t.id, templateName: t.name, ok: false, error: "Could not download the stored template file." });
          continue;
        }
        let ws;
        try {
          const wb = XLSX.read(rawBytes, { type: "array", cellStyles: true });
          ws = resolveMainSheet(wb, t);
        } catch (err) {
          results.push({ templateId: t.id, templateName: t.name, ok: false, error: `Could not read the stored template file: ${err.message}` });
          continue;
        }
        if (!isEstimatingTemplate(ws)) continue; // not one of the estimating templates. Never a failure, just not a candidate.
        try {
          const patchedBytes = await patchRateCellsInWorkbook(rawBytes, t.name, catalog);
          const { templates: reimported } = await importExcelAsTemplate({ name: t.name }, patchedBytes.buffer.slice(patchedBytes.byteOffset, patchedBytes.byteOffset + patchedBytes.byteLength));
          const patchedTpl = reimported[0];
          const origCount = t.sections.reduce((n, s) => n + s.fields.length, 0);
          const newCount = patchedTpl.sections.reduce((n, s) => n + s.fields.length, 0);
          if (newCount !== origCount) throw new Error(`Field count changed (${origCount} -> ${newCount}) after patching.`);
          const patchedWb = XLSX.read(patchedBytes, { type: "array", cellFormula: true, cellStyles: true });
          const patchedWs = resolveMainSheet(patchedWb, patchedTpl);
          // Fixed non-zero sample (100 per input, quantity 1), never an empty
          // report: with empty inputs every cost term is 0 x rate and the
          // subtotal is 0 whatever the rates are. Throws unless every rate
          // cell holds a valid number and the subtotal is finite and > 0.
          const evalResults = evalFormulasWithReport(patchedTpl, patchedBytes, buildSampleReport(patchedTpl));
          requirePositiveSampleSubtotal(patchedWs, evalResults);
          results.push({ templateId: t.id, templateName: t.name, ok: true, error: null });
          patched.push({ templateId: t.id, templateName: t.name, bytes: patchedBytes });
        } catch (err) {
          results.push({ templateId: t.id, templateName: t.name, ok: false, error: err.message });
        }
      }
      const plan = planUnitTypeUpdate(results);
      if (!plan.uploadAll) {
        alert(`Update unit types failed. Nothing was uploaded.\n\n${plan.failing.map((f) => `${f.templateName}: ${f.error}`).join("\n")}`);
        return;
      }
      if (patched.length === 0) {
        alert("No unit type templates were found in this project, so nothing was updated.");
        return;
      }
      const uploaded = [];
      for (let i = 0; i < patched.length; i++) {
        const { templateId, templateName, bytes } = patched[i];
        let uploadError;
        try {
          const { error } = await uploadTemplateFile(currentProject.id, templateId, bytes);
          uploadError = error ? (error.message || String(error)) : null;
        } catch (err) {
          uploadError = err.message || String(err);
        }
        if (uploadError) {
          const notUploaded = patched.slice(i + 1).map((p) => p.templateName);
          alert(
            `Update unit types stopped partway because an upload failed.\n\n` +
            `Failed: ${templateName}: ${uploadError}\n` +
            `Updated with the new rates: ${uploaded.length ? uploaded.join(", ") : "none"}\n` +
            `Not updated, still on the old rates: ${[templateName, ...notUploaded].join(", ")}\n\n` +
            `Click Update unit types again to retry.`
          );
          markRatesDirty(pid, true); // some stored templates are still on the old rates
          return;
        }
        RAW_STORE[templateId] = bytes;
        delete WB_STORE[templateId]; // re-parse from the new bytes next time, never the stale workbook
        uploaded.push(templateName);
      }
      markRatesDirty(pid, false); // the only place the flag is cleared: every template uploaded
      alert(`Updated ${uploaded.length} template(s): ${uploaded.join(", ")}.`);
    } catch (err) {
      alert(`Update unit types failed. Nothing was uploaded.\n\n${err.message || String(err)}`);
    } finally {
      setUpdatingUnitTypes(false);
    }
  };

  // Fix round 1, finding #8: the previous job's line details (and any stale
  // markup error) are reset as part of the switch, and the Job screen shows
  // a loading placeholder until then, so JobScreen never renders the
  // previous job's lines (and their "Open" buttons) under the new header.
  //
  // Fix round 4: the line computation is a queued turn scoped to the new job
  // reading the refs (not a bare await on render closures), so a slower
  // stale compute can never land on top of a newer job's lines.
  //
  // Fix round 5 (ruling R10): the switch itself is now a queued turn. openJob
  // only records the requested job and shows the Job screen's loading
  // placeholder; activeJobRef does not move until every turn already queued
  // for the previous job (a field commit, an explicit Save, an in-flight
  // quantity change) has run against that job as current. The switch turn
  // then resets line details and the markup error, commits the new job and
  // computes its lines. A switch superseded by a newer open (or cancelled
  // by Back on the placeholder) is skipped. Reopening the job that is
  // already active keeps activeJobRef's copy, which includes every turn
  // that landed after the jobs-list entry the user clicked was rendered.
  const openJob = (job) => {
    openSeqRef.current += 1;
    setOpeningJobId(job.id);
    setScreen("jobScreen");
    return jobOpRunnerRef.current.switchScope("load the job", () => {
      if (openingJobIdRef.current !== job.id) return null;
      const current = activeJobRef.current;
      commitActiveJob(current?.id === job.id ? current : job);
      setJobLineDetails({ lines: [], rollup: { incomplete: true, subtotal: null, markup: null, total: null } });
      setJobMarkupError(null);
      jobMarkupErrorRef.current = null;
      setOpeningJobId(null);
      return job.id;
    }, recomputeActiveJob);
  };

  // A queued turn body: recompute the active job's lines from the refs.
  const recomputeActiveJob = async (ctx) => {
    const job = activeJobRef.current;
    const details = await computeJobLineDetails(job, reportsRef.current, templatesRef.current);
    if (ctx.isCurrent()) setJobLineDetails(details);
  };

  // Queued (fix round 3) and job-scoped (fix round 4). Reads
  // activeJobRef.current at the start of its turn, so any header, markup or
  // quantity change committed before Save (a blur fires before the click)
  // is already in the job it saves.
  const saveJob = () => enqueueJobOp("save the job", async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return;
    if (isJobFrozen(job)) { alert(SENT_JOB_LOCKED); return; }
    // Fix round 1, finding #6: never persist while the markup input shows
    // an unresolved error.
    if (jobMarkupErrorRef.current) { alert("Fix the markup error before saving: " + jobMarkupErrorRef.current); return; }
    if (await refuseIfStoredBlocks(ctx, await storedJobBlockReason(job.id, "save"))) return;
    const { data, error } = await upsertJob(job, currentProject.id);
    if (!data) { alert("Failed to save job." + (error ? ` (${error.message})` : "")); return; }
    updateJobs((prev) => (prev.some((j) => j.id === data.id) ? prev.map((j) => (j.id === data.id ? data : j)) : [...prev, data]));
    if (!ctx.isCurrent()) return;
    commitActiveJob(data);
  });

  // Creates a brand-new job with a fresh id; nothing can be in flight
  // against a job that didn't exist a moment ago. Fix round 5, finding 4: it
  // is only opened if the user is still on the Jobs list and hasn't opened
  // any job since clicking New; otherwise it is just added to the list.
  //
  // Final fix wave, item 12: busy-guarded like Mark Sent and Revise, so a
  // double click on New creates one job, not two. The ref is the real guard
  // (two clicks can land before a re-render); the state disables the button.
  const [creatingJob, setCreatingJobState] = useState(false);
  const creatingJobRef = useRef(false);
  const setCreatingJob = (v) => {
    creatingJobRef.current = v;
    setCreatingJobState(v);
  };
  const createNewJob = async () => {
    if (creatingJobRef.current) return;
    setCreatingJob(true);
    const seqAtClick = openSeqRef.current;
    try {
      // Final fix wave, item 9: the markup starts at the catalog's default
      // markup entry (jobMath.defaultMarkupPercent), 20 only if there is
      // none. If the catalog cannot be read the job still starts at 20, and
      // the user is told so rather than left to assume it came from Rates.
      let markup;
      let catalogError = null;
      try {
        markup = defaultMarkupPercent(await getRateCatalogItemsOrThrow(currentProject.id));
      } catch (err) {
        catalogError = err;
        markup = defaultMarkupPercent([]);
      }
      const job = { id: crypto.randomUUID(), name: "Untitled Job", status: "draft", markupPercent: markup.percent, revision: 1, revisionOf: null, lines: [] };
      const { data, error } = await upsertJob(job, currentProject.id);
      if (!data) { alert("Failed to create job." + (error ? ` (${error.message})` : "")); return; }
      updateJobs((prev) => [...prev, data]);
      if (catalogError) {
        alert(`The job was created, but the rate catalog could not be read, so its markup starts at ${markup.percent}% instead of the catalog's default. Check Markup % before sending.` + (catalogError.message ? ` (${catalogError.message})` : ""));
      }
      if (openSeqRef.current === seqAtClick && screenRef.current === "home" && homeTabRef.current === "jobs") openJob(data);
    } catch (err) {
      console.error("Job operation failed (create the job):", err);
      alert("Failed to create the job." + (err?.message ? ` (${err.message})` : ""));
    } finally {
      setCreatingJob(false);
    }
  };

  // Queued and job-scoped. Creates the takeoff report, then adds the line to
  // the job it read at the start of its turn. If the user opened another job
  // while this was in flight, the line is still persisted on THIS job (so
  // the new report isn't orphaned) and the jobs list is updated, but the
  // active job/screen is left alone. It only navigates into the takeoff if
  // this job is still active AND the user is still on the Job screen, so it
  // can never pull someone off the Jobs list.
  const addJobLine = (kind, template) => enqueueJobOp("add the line", async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return;
    if (isJobFrozen(job)) { alert(SENT_JOB_LOCKED); return; }
    if (jobMarkupErrorRef.current) { alert("Fix the markup error before adding a line: " + jobMarkupErrorRef.current); return; }
    const defaults = {};
    (template.sections || []).forEach((sec) => sec.fields?.forEach((f) => { if (f.defaultValue) defaults[f.id] = f.defaultValue; }));
    // Write-through at creation: findQuantityField is the same lookup
    // buildQuantityChange uses on a later edit.
    const qtyField = kind === "unit_type" ? findQuantityField(template) : null;
    if (qtyField) defaults[qtyField.id] = "1";
    const newReport = { id: crypto.randomUUID(), _templateId: template.id, _templateName: template.name, _date: new Date().toISOString().split("T")[0], _author: currentUser?.username || "", reportTitle: `${job.name || "Job"} - ${template.name}`, ...defaults };
    // Checked before the first write (the takeoff), so a refused add never
    // leaves an orphan report behind.
    if (await refuseIfStoredBlocks(ctx, await storedJobBlockReason(job.id, "addLine"))) return;
    const { data: savedReport, error: reportError } = await upsertReport(newReport, currentProject.id);
    if (!savedReport) { alert("Failed to create the takeoff for this line." + (reportError ? ` (${reportError.message})` : "")); return; }
    // Reports are project-wide, not per-job, so this is applied regardless
    // of which job is active now.
    reportsRef.current = [...reportsRef.current, savedReport];
    setReports((prev) => [...prev, savedReport]);

    // Nothing else touching this job can have run since this turn started
    // (the queue), so `job` is still this job's latest in-memory state.
    const updatedJob = buildNewLine(job, template, kind, savedReport.id, () => crypto.randomUUID());
    let savedJob = null;
    let jobError = null;
    let thrown = null;
    // Fix round 2: checked again right before the job write. A refusal here
    // takes the same cleanup path as a failed job save below.
    const lateCheck = await storedJobBlockReason(job.id, "addLine");
    if (!lateCheck.reason) {
      try {
        ({ data: savedJob, error: jobError } = await upsertJob(updatedJob, currentProject.id));
      } catch (err) {
        thrown = err;
      }
    }
    if (!savedJob) {
      // Fix round 4: runs whether upsertJob returned an error OR threw. The
      // takeoff report already exists; delete it so a failed job save never
      // leaves an orphan pointing at no job line.
      let deleteError = null;
      try {
        ({ error: deleteError } = await deleteReport(savedReport.id));
      } catch (err) {
        deleteError = err;
      }
      // Fix round 5, finding 2: drop it from the ref AND the state (the
      // Reports tab and the next ref mirror both read state), but only when
      // the delete worked. If it didn't, the takeoff still exists and stays
      // listed, which is what the alert below tells the user to check.
      if (!deleteError) {
        reportsRef.current = reportsRef.current.filter((r) => r.id !== savedReport.id);
        setReports((prev) => prev.filter((r) => r.id !== savedReport.id));
      }
      const causeMessage = lateCheck.reason ? await storedBlockMessage(ctx, lateCheck) : (jobError || thrown)?.message;
      alert(
        "Failed to add the line to the job." + (causeMessage ? ` (${causeMessage})` : "") +
        (deleteError
          ? " The takeoff could not be removed either and may still exist; check the Reports tab."
          : " The takeoff was not kept.")
      );
      return;
    }
    updateJobs((prev) => prev.map((j) => (j.id === savedJob.id ? savedJob : j)));
    if (!ctx.isCurrent()) return;
    commitActiveJob(savedJob);
    if (screenRef.current !== "jobScreen" || openingJobIdRef.current != null) {
      // The user left the Job screen (or asked to open another job, whose
      // switch is queued behind this turn) while this was saving: the line
      // is added, but don't yank them into the takeoff editor.
      await recomputeActiveJob(ctx);
      return;
    }
    setCameFromJobId(savedJob.id);
    setRepTpl(template);
    setActiveRep(savedReport);
    setTab("edit");
    setScreen("fillReport");
  });

  // Fix round 1, finding #4: mirrors the Reports tab's own rule
  // (canEditReports && !r._submitted). Doesn't write the job, so it isn't
  // queued. Task 6: a sent job's takeoff opens on the Preview tab only
  // (sentTakeoffIds / activeRepLocked hide Edit and Save and saveReport
  // refuses it), for anyone who can view the job.
  const openJobLine = async (line) => {
    const report = reportsRef.current.find((r) => r.id === line.reportId);
    const template = report ? templatesRef.current.find((t) => t.id === report._templateId) : null;
    if (!report || !template) { alert("This line's takeoff could not be found."); return; }
    if (isJobFrozen(activeJobRef.current)) {
      setCameFromJobId(activeJobRef.current.id);
      setRepTpl(template);
      setActiveRep(JSON.parse(JSON.stringify(report)));
      setTab("preview");
      setScreen("fillReport");
      return;
    }
    if (!canEditReports) { alert("You do not have permission to edit this takeoff."); return; }
    if (report._submitted) { alert("This takeoff has been submitted and can no longer be edited."); return; }
    setCameFromJobId(activeJobRef.current?.id ?? null);
    setRepTpl(template);
    setActiveRep(JSON.parse(JSON.stringify(report)));
    setTab("edit");
    setScreen("fillReport");
  };

  // Queued (fix round 3) and job-scoped (fix round 4). The decision lives
  // in jobMath.js's buildQuantityChange. Saves the report first, then the
  // job. Once the report is written the job save always runs, even if the
  // user has opened another job meanwhile, so the report's quantity and
  // this job's line.quantity can't disagree in the database; only the
  // active-job/line-details writes are skipped when this job is no longer
  // current. Resolves true only when the change was saved, so
  // JobScreen's QuantityInput can reset its draft on anything else.
  const changeJobLineQuantity = (lineId, rawQuantity) => enqueueJobOp("change the quantity", async (ctx) => {
    if (isJobFrozen(activeJobRef.current)) { alert(SENT_JOB_LOCKED); return false; }
    if (jobMarkupErrorRef.current) { alert("Fix the markup error before changing quantities: " + jobMarkupErrorRef.current); return false; }

    const job = activeJobRef.current;
    const baseReports = reportsRef.current;
    const baseTemplates = templatesRef.current;
    const result = buildQuantityChange(job, baseReports, baseTemplates, lineId, rawQuantity);
    if (!result.ok) { alert(result.error); return false; }
    // Before the first write: the job row, and (when the quantity writes
    // through) whether any stored sent job points at that takeoff.
    const check = result.updatedReport
      ? await storedTakeoffBlockReason(job.id, result.updatedReport.id)
      : await storedJobBlockReason(job.id, "quantity");
    if (await refuseIfStoredBlocks(ctx, check)) return false;

    let nextReports = baseReports;
    if (result.updatedReport) {
      const { data: savedReport, error: reportError } = await upsertReport(result.updatedReport, currentProject.id);
      if (!savedReport) {
        alert("Failed to save this line's takeoff quantity." + (reportError ? ` (${reportError.message})` : "") + " The quantity was not changed.");
        return false;
      }
      // Reports are project-wide; applied whether or not this job is still active.
      reportsRef.current = reportsRef.current.map((r) => (r.id === savedReport.id ? savedReport : r));
      nextReports = reportsRef.current;
      setReports((prev) => prev.map((r) => (r.id === savedReport.id ? savedReport : r)));
    }

    let savedJob = null;
    let jobCause = null;
    // Fix round 2: checked again right before the job write. A refusal here
    // takes the same restore path as a failed job save below.
    const lateCheck = await storedJobBlockReason(job.id, "quantity");
    if (!lateCheck.reason) {
      try {
        const { data, error } = await upsertJob(result.updatedJob, currentProject.id);
        savedJob = data;
        jobCause = error;
      } catch (err) {
        jobCause = err;
      }
    }
    if (!savedJob) {
      // Fix round 5, finding 3: if the takeoff was already written, put it
      // back before claiming nothing changed. If that fails too, say the two
      // disagree and name the line (quantityJobSaveFailureMessage).
      let reportRestore = "not-needed";
      if (result.updatedReport) {
        let restored = null;
        try {
          ({ data: restored } = await upsertReport(result.previousReport, currentProject.id));
        } catch (err) {
          console.error("Restoring the takeoff quantity failed:", err);
        }
        if (restored) {
          reportsRef.current = reportsRef.current.map((r) => (r.id === restored.id ? restored : r));
          setReports((prev) => prev.map((r) => (r.id === restored.id ? restored : r)));
          reportRestore = "restored";
        } else {
          reportRestore = "failed";
        }
      }
      const line = job.lines.find((l) => l.id === lineId);
      const newQuantity = result.updatedJob.lines.find((l) => l.id === lineId)?.quantity;
      const causeMessage = lateCheck.reason ? await storedBlockMessage(ctx, lateCheck) : jobCause?.message;
      alert(quantityJobSaveFailureMessage({ line, newQuantity, causeMessage, reportRestore }));
      return false;
    }
    updateJobs((prev) => prev.map((j) => (j.id === savedJob.id ? savedJob : j)));
    if (!ctx.isCurrent()) return true;
    commitActiveJob(savedJob);
    const details = await computeJobLineDetails(savedJob, nextReports, baseTemplates);
    if (ctx.isCurrent()) setJobLineDetails(details);
    return true;
  });

  // Task 6: queued and job-scoped like every other job write. The totals are
  // recomputed inside the turn from the refs (never the rendered
  // jobLineDetails, which can lag a just-committed edit), and buildSentJob
  // refuses unless those details match the job this turn read. The confirm
  // shows the exact total being frozen. Unsaved header/markup edits are
  // saved with it, since their turns ran before this one.
  //
  // Task 7 (ruling R20): Mark Sent also stores the proposal as sent. The
  // proposal is built from the figures being frozen (buildProposal), then
  // jobMath's sendJobWithProposal generates the PDF, uploads it to
  // {project_id}/proposals/{job_id}-R{revision}.pdf and only then writes the
  // job, once, with status sent and frozen.proposalPath (the schema trigger
  // forbids changing frozen afterwards, so there is never a second write).
  // A failed upload means the job is not Sent; a failed job write removes
  // the uploaded PDF (see sendJobWithProposal for when it is kept instead).
  const markJobSent = () => {
    if (jobActionBusyRef.current) return Promise.resolve(undefined);
    setJobActionBusy("markSent");
    return enqueueJobOp("mark the job sent", markJobSentTurn).finally(() => setJobActionBusy(null));
  };
  const markJobSentTurn = async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return;
    if (isJobFrozen(job)) { alert("This job has already been sent."); return; }
    if (jobMarkupErrorRef.current) { alert("Fix the markup error before marking the job sent: " + jobMarkupErrorRef.current); return; }
    const details = await computeJobLineDetails(job, reportsRef.current, templatesRef.current);
    if (!ctx.isCurrent()) return;
    setJobLineDetails(details);
    const built = buildSentJob(job, details);
    if (!built.ok) {
      // Final fix wave, item 1: name the line and its problem when there is one.
      const lineProblem = details.rollup?.incomplete ? lineProblemMessage(details.lines) : null;
      alert(built.error + (lineProblem ? `\n\n${lineProblem}` : ""));
      return;
    }
    // Fix round 1 (R22): the customer reads these descriptions, so an empty
    // one or a raw template name refuses before anything happens.
    const descProblem = lineDescriptionProblem(job.lines, templatesRef.current.map((t) => t.name));
    if (descProblem) { alert(`Cannot mark this job Sent. ${descProblem}`); return; }
    // Built before the confirm, so a job whose proposal can't be built is
    // never offered for sending.
    const proposalBuilt = buildProposal(built.sentJob.frozen.lines, resolveJobRollup(built.sentJob, []));
    if (!proposalBuilt.ok) { alert(proposalBuilt.error); return; }
    // Final fix wave, item 3 (ruling R28): never send a bid priced on
    // templates whose baked rates no longer match the catalog.
    const ratesCheck = await checkRatesCurrentForJob(job);
    if (!ctx.isCurrent()) return;
    if (!ratesCheck.ok) { alert(ratesCheckMessage(ratesCheck)); return; }
    const baseId = proposalBaseId(job, jobsRef.current);
    const ok = window.confirm(`Mark this job Sent at a total of $${details.rollup.total.toFixed(2)}? Any edits on this screen are saved with it, and the proposal PDF is created and stored as sent. A sent job never reprices and can no longer be edited. Use Revise to create an editable copy.`);
    if (!ok) return;
    if (await refuseIfStoredBlocks(ctx, await storedJobBlockReason(job.id, "markSent"))) return;
    const projectId = currentProject.id;
    const result = await sendJobWithProposal({
      sentJob: built.sentJob,
      makePdf: () => generateProposalPdf(built.sentJob, proposalBuilt.proposal, PROPOSAL_BRAND, true, { baseId }),
      // Fix round 1 (R21): a fresh storage path per attempt (see uploadProposalPdf).
      uploadPdf: (blob) => uploadProposalPdf(projectId, job.id, job.revision || 1, blob),
      checkBeforeWrite: () => storedJobBlockReason(job.id, "markSent"),
      writeJob: (sentJob) => upsertJob(sentJob, projectId),
      readStoredJob: () => getJob(job.id),
      removePdf: (path) => removeProposalPdf(path),
    });
    if (!result.ok) {
      const causeMessage = result.stage === "blocked" ? await storedBlockMessage(ctx, result.check) : result.cause?.message;
      alert(markSentFailureMessage({ stage: result.stage, causeMessage, cleanup: result.cleanup, cleanupCauseMessage: result.cleanupCause?.message, path: result.path }));
      return;
    }
    const data = result.savedJob;
    updateJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
    if (!ctx.isCurrent()) return;
    commitActiveJob(data);
    const frozenDetails = await computeJobLineDetails(data, reportsRef.current, templatesRef.current);
    if (ctx.isCurrent()) setJobLineDetails(frozenDetails);
  };

  // Task 6 (ruling R17): copies a sent job into a new draft (revision + 1,
  // revisionOf set) whose lines point at brand-new copies of every takeoff
  // (buildRevisedJobCopies), so editing the revision can never touch the
  // sent job or the takeoffs behind it. The copies are saved first; if any
  // fails, or the new job itself fails to save, every copy already created
  // is deleted again and the alert names any that could not be, so no
  // revision is ever left pointing at takeoffs that don't exist. The sent
  // job is never written. Once saved, the revision is opened through
  // openJob's queued switch (not awaited: it runs after this turn), unless
  // the user has left the Job screen or asked to open another job meanwhile.
  //
  // Fix round 1, Important 1: never a second revision. The button is
  // disabled while this turn is queued or running, and the turn itself
  // refuses when a revision of this job already exists, locally (jobsRef)
  // or in the stored rows (another tab or user), and opens that one instead.
  const reviseJob = () => {
    if (jobActionBusyRef.current) return Promise.resolve(undefined);
    setJobActionBusy("revise");
    return enqueueJobOp("revise the job", reviseJobTurn).finally(() => setJobActionBusy(null));
  };
  const reviseJobTurn = async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return;
    if (!isJobFrozen(job)) { alert("Only a sent job can be revised."); return; }
    let stored;
    try {
      stored = await getJobsChecked(currentProject.id);
    } catch (err) {
      stored = { data: null, error: err };
    }
    if (stored.error || !stored.data) {
      alert("Could not check for an existing revision of this job, so nothing was created." + (stored.error?.message ? ` (${stored.error.message})` : ""));
      return;
    }
    const existing = findExistingRevision(stored.data, job.id) || findExistingRevision(jobsRef.current, job.id);
    if (existing) {
      if (!jobsRef.current.some((j) => j.id === existing.id)) updateJobs((prev) => [...prev, existing]);
      alert(`Rev ${existing.revision || 2} of this job already exists. Opening it instead of creating another.`);
      if (ctx.isCurrent() && screenRef.current === "jobScreen" && openingJobIdRef.current == null) openJob(existing);
      return;
    }
    const reportsById = Object.fromEntries(reportsRef.current.map((r) => [r.id, r]));
    const { revisedJob, newReports } = buildRevisedJobCopies(job, reportsById, () => crypto.randomUUID());

    const created = [];
    let stage = null;
    let cause = null;
    for (const r of newReports) {
      try {
        const { data, error } = await upsertReport(r, currentProject.id);
        if (!data) { stage = "takeoffs"; cause = error; break; }
        created.push(data);
      } catch (err) {
        stage = "takeoffs"; cause = err; break;
      }
    }
    let savedJob = null;
    if (!stage) {
      try {
        const { data, error } = await upsertJob(revisedJob, currentProject.id);
        savedJob = data;
        if (!data) { stage = "job"; cause = error; }
      } catch (err) {
        stage = "job"; cause = err;
      }
    }

    // Final fix wave, item 6: a job write that failed or threw may still
    // have landed (a lost response). Read the stored job back before
    // deleting any copy: if the revision is there, this Revise succeeded;
    // if it cannot be read, keep every copy and say so.
    if (stage === "job") {
      let storedRevision;
      try {
        storedRevision = await getJob(revisedJob.id);
      } catch (err) {
        storedRevision = { data: null, error: err };
      }
      const outcome = reviseLostResponseOutcome(storedRevision, revisedJob);
      if (outcome === "landed") {
        stage = null;
        cause = null;
        savedJob = storedRevision.data;
      } else if (outcome === "kept") {
        reportsRef.current = [...reportsRef.current, ...created];
        setReports((prev) => [...prev, ...created]);
        alert(reviseFailureMessage({ stage, causeMessage: cause?.message, leftoverTitles: created.map((r) => r.reportTitle), copiesKept: true }));
        return;
      }
    }

    if (stage) {
      const leftovers = [];
      for (const r of created) {
        let deleteError = null;
        try {
          ({ error: deleteError } = await dbDeleteReport(r.id));
        } catch (err) {
          deleteError = err;
        }
        if (deleteError) leftovers.push(r);
      }
      // Reports are project-wide: a copy that could not be deleted still
      // exists, so it is listed (the alert tells the user where to find it).
      if (leftovers.length > 0) {
        reportsRef.current = [...reportsRef.current, ...leftovers];
        setReports((prev) => [...prev, ...leftovers]);
      }
      alert(reviseFailureMessage({ stage, causeMessage: cause?.message, leftoverTitles: leftovers.map((r) => r.reportTitle) }));
      return;
    }

    reportsRef.current = [...reportsRef.current, ...created];
    setReports((prev) => [...prev, ...created]);
    updateJobs((prev) => [...prev, savedJob]);
    if (!ctx.isCurrent()) return;
    if (screenRef.current !== "jobScreen" || openingJobIdRef.current != null) return;
    openJob(savedJob);
  };

  // Task 7 (ruling R20): "Proposal PDF" on a draft job is a preview built
  // from the live figures and downloaded; it is never stored. Queued so a
  // header, markup or quantity commit made just before the click is in it,
  // with the figures recomputed inside the turn from the refs. A sent job's
  // proposal is always the stored file (downloadSentProposal), never a
  // fresh one.
  const generateJobProposal = () => enqueueJobOp("create the proposal PDF", async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return;
    if (isJobFrozen(job)) { alert("This job has been sent. Use Download sent proposal to get the proposal as it was sent."); return; }
    if (jobMarkupErrorRef.current) { alert("Fix the markup error before creating the proposal: " + jobMarkupErrorRef.current); return; }
    const details = await computeJobLineDetails(job, reportsRef.current, templatesRef.current);
    if (!ctx.isCurrent()) return;
    setJobLineDetails(details);
    const descProblem = lineDescriptionProblem(job.lines, templatesRef.current.map((t) => t.name));
    if (descProblem) { alert(`Cannot create the proposal. ${descProblem}`); return; }
    const built = buildProposal(job.lines, details.rollup);
    if (!built.ok) {
      const lineProblem = details.rollup?.incomplete ? lineProblemMessage(details.lines) : null;
      alert(built.error + (lineProblem ? `\n\n${lineProblem}` : ""));
      return;
    }
    // Final fix wave, item 3 (ruling R28): the preview refuses stale rates too.
    const ratesCheck = await checkRatesCurrentForJob(job);
    if (!ctx.isCurrent()) return;
    if (!ratesCheck.ok) { alert(ratesCheckMessage(ratesCheck)); return; }
    // Fix round 1 (R23): marked as a draft preview on every page and in the
    // file name, so it can't pass for the proposal that was sent.
    generateProposalPdf(job, built.proposal, PROPOSAL_BRAND, false, { preview: true, baseId: proposalBaseId(job, jobsRef.current) });
  });

  // Task 7: a sent job's proposal is the PDF stored when it was marked Sent
  // (frozen.proposalPath). Doesn't write anything, so it isn't queued. Fix
  // round 1: busy-guarded like Mark Sent/Revise, so a double click can't
  // start two downloads.
  const downloadSentProposal = async () => {
    if (jobActionBusyRef.current) return;
    const job = activeJobRef.current;
    const path = job?.frozen?.proposalPath;
    if (!path) { alert("No stored proposal PDF was found for this job."); return; }
    setJobActionBusy("download");
    try {
      let blob = null;
      try {
        blob = await downloadProposalPdf(path);
      } catch (err) {
        console.error("Downloading the sent proposal failed:", err);
      }
      if (!blob) { alert("Could not download the stored proposal PDF. Try again in a moment."); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = proposalFileName(job);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setJobActionBusy(null);
    }
  };

  // Fix round 1 (ruling R22): a line's description is what the customer
  // reads on the proposal, so the estimator can edit it. Same pattern as the
  // header fields: a local draft in JobScreen committed on blur/Enter,
  // applied here in a queued turn, persisted by Save (or Mark Sent).
  const changeJobLineDescription = (lineId, value) => enqueueJobOp("update the line description", async (ctx) => {
    const job = activeJobRef.current;
    if (!job) return false;
    if (isJobFrozen(job)) { alert(SENT_JOB_LOCKED); return false; }
    const res = applyLineDescription(job, lineId, value);
    if (!res.ok) { alert(res.error); return false; }
    commitActiveJob(res.job);
    // The line table reads descriptions from the line details.
    await recomputeActiveJob(ctx);
    return true;
  });

  // Fix round 1 (ruling R22): once a custom line's takeoff is saved, the
  // line takes the takeoff's Description while it still has its default
  // description (syncCustomLineDescription). Final fix wave, item 1: a unit
  // type line also takes the takeoff's "Number of units..." value when it is
  // a valid quantity (syncUnitLineQuantity); an invalid one leaves the line
  // as it is, and computeJobLineDetails shows it incomplete with the reason
  // until the two agree. Queued and scoped to the job that is active when
  // the takeoff is saved; saved to the database only when something
  // changed, with the usual stored-row check first.
  const syncJobLineFromTakeoffTurn = (report, template) => enqueueJobOp("update the job line from its takeoff", async (ctx) => {
    const job = activeJobRef.current;
    if (!job || isJobFrozen(job)) return;
    const synced = syncUnitLineQuantity(syncCustomLineDescription(job, report, template), report, template).job;
    if (synced === job) {
      // Nothing to save, but the line's status (e.g. a quantity mismatch)
      // may have changed with the takeoff.
      await recomputeActiveJob(ctx);
      return;
    }
    if (await refuseIfStoredBlocks(ctx, await storedJobBlockReason(job.id, "save"))) return;
    const { data, error } = await upsertJob(synced, currentProject.id);
    if (!data) { alert("The takeoff was saved, but its line on the job could not be updated to match it. The job shows that line as incomplete until they agree." + (error ? ` (${error.message})` : "")); return; }
    updateJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
    if (!ctx.isCurrent()) return;
    commitActiveJob(data);
    await recomputeActiveJob(ctx);
  });

  // Fix round 4 (controller ruling R9): MarkupInput commits on blur/Enter,
  // not per keystroke. Validation stays synchronous so the inline error
  // (and the Save/Add/quantity guards that read jobMarkupErrorRef) take
  // effect before any later click's turn runs; the in-memory update is a
  // queued, job-scoped turn that applies the parsed number to whatever job
  // the previous turn left behind. Not persisted until Save, like every
  // other header field.
  const changeJobMarkup = (raw) => {
    if (isJobFrozen(activeJobRef.current)) { alert(SENT_JOB_LOCKED); return Promise.resolve(false); }
    const parsed = parseMarkupPercent(raw);
    if (!parsed.ok) {
      setJobMarkupError(parsed.error);
      jobMarkupErrorRef.current = parsed.error;
      return Promise.resolve(false);
    }
    setJobMarkupError(null);
    jobMarkupErrorRef.current = null;
    return enqueueJobOp("change the markup", async (ctx) => {
      const job = activeJobRef.current;
      if (!job) return false;
      if (isJobFrozen(job)) { alert(SENT_JOB_LOCKED); return false; }
      commitActiveJob({ ...job, markupPercent: parsed.value });
      await recomputeActiveJob(ctx);
      return true;
    });
  };

  // Fix round 4 (controller ruling R9): Name/Property/Address/Contact are
  // local drafts in JobScreen, committed on blur/Enter through here and
  // applied to activeJobRef.current inside a queued turn. An in-flight
  // quantity/add/save turn can no longer snap the field back: either it
  // finished before this turn read the job, or it starts after this turn
  // applied the edit and reads it.
  const changeJobField = (key, value) => enqueueJobOp("update the job", async () => {
    const job = activeJobRef.current;
    if (!job) return false;
    if (isJobFrozen(job)) { alert(SENT_JOB_LOCKED); return false; }
    commitActiveJob(applyJobHeaderField(job, key, value));
    return true;
  });

  // "Back to job" from the takeoff editor. The recompute is a queued turn
  // scoped to that job and reads the refs, never render-time closures. If
  // that job somehow isn't the active one any more, reopen it properly.
  const returnToJob = (jobId) => {
    setCameFromJobId(null);
    if (activeJobRef.current?.id !== jobId) {
      const job = jobs.find((j) => j.id === jobId);
      if (job) { openJob(job); } else { setScreen("home"); setHomeTab("jobs"); }
      return;
    }
    // Fix round 2, finding #3: a markup error left from before the trip
    // into the takeoff would otherwise silently block every Save/Add/
    // quantity guard on a screen whose markup input has been reset.
    setJobMarkupError(null);
    jobMarkupErrorRef.current = null;
    setScreen("jobScreen");
    enqueueJobOp("recalculate the job", recomputeActiveJob, jobId);
  };

  const saveReport = async () => {
    if (activeRepLocked) { alert(SENT_TAKEOFF_LOCKED); return; }
    // Check required fields — red cells in the template
    const allSecs = repTpl?.isMultiSheet ? (repTpl.sheets||[]).flatMap(sh=>sh.sections||[]) : (repTpl?.sections||[]);
    const requiredFields = allSecs.flatMap(sec => (sec.fields || []).filter(f => f.required));
    const missingRequired = requiredFields.filter(f => !activeRep[f.id] || activeRep[f.id] === "");
    let reportToSave = { ...activeRep };
    if (missingRequired.length > 0) {
      const proceed = window.confirm(
        `One or more required fields is needed:\n\n${missingRequired.map(f => `• ${f.label}`).join("\n")}\n\nSave anyway?`
      );
      if (!proceed) return;
      reportToSave = { ...reportToSave, _actionNeeded: true };
    } else {
      reportToSave = { ...reportToSave, _actionNeeded: false };
    }
    const { data, error } = await upsertReport(reportToSave, currentProject.id);
    if (!data) { alert("Failed to save report. Please try again." + (error ? ` (${error.message})` : "")); return; }
    setActiveRep(reportToSave);
    // Final fix wave, item 1: the ref too, so the job-line sync turn queued
    // below reads this save without waiting for a render.
    reportsRef.current = reportsRef.current.some(r => r.id === data.id) ? reportsRef.current.map(r => r.id === data.id ? data : r) : [...reportsRef.current, data];
    setReports(prev => prev.some(r => r.id === data.id) ? prev.map(r => r.id === data.id ? data : r) : [...prev, data]);
    doFlash();
    // Task 7 fix round 1 (R22): a custom line on the open draft job picks up
    // this takeoff's Description. Final fix wave, item 1: a unit type line
    // picks up its quantity the same way.
    const jobOnScreen = activeJobRef.current;
    if (jobOnScreen && !isJobFrozen(jobOnScreen) && (jobOnScreen.lines || []).some((l) => l.reportId === data.id)) {
      syncJobLineFromTakeoffTurn(data, repTpl);
    }
  };
  // Fix round 2, finding #3 (minor): now returns the delete's own
  // {error} instead of discarding it — addJobLine's orphan-report cleanup
  // needs to know whether the delete actually succeeded before it decides
  // what to tell the user.
  const deleteReport = async (id) => {
    if (sentTakeoffIds.has(id)) { alert(SENT_TAKEOFF_LOCKED); return { error: new Error(SENT_TAKEOFF_LOCKED) }; }
    const { error } = await dbDeleteReport(id);
    setReports(prev => prev.filter(r => r.id !== id));
    return { error };
  };
  const submitReport = async (r) => {
    // Final fix wave, items 7 and 11: a takeoff behind a sent bid is never
    // submitted (the button is hidden too), and neither is anything while
    // the jobs that would say so are unknown.
    if (refuseIfJobsUnknown()) return;
    if (sentTakeoffIds.has(r.id)) { alert(SENT_TAKEOFF_LOCKED); return; }
    const ok = window.confirm("Clicking okay validates this document. You will not be able to change this document after hitting okay.");
    if (!ok) return;
    const submitted = { ...r, _submitted: true };
    const { data, error } = await upsertReport(submitted, currentProject.id);
    if (!data) { alert("Failed to submit report." + (error ? ` (${error.message})` : "")); return; }
    setReports(prev => prev.map(rep => rep.id === r.id ? { ...rep, _submitted: true } : rep));
  };
  const setRF = (k, v) => setActiveRep(r => ({ ...r, [k]: v }));

  const S = styles;

  // Final fix wave, item 3 (ruling R28): the rates-changed warning on the
  // Jobs list and the Job screen too, where bids are priced and sent.
  const ratesDirtyJobsBanner = ratesDirty ? (
    <div style={{ background: "var(--rf-bg)", border: "1px solid var(--rf-danger)", borderRadius: 7, color: "var(--rf-danger)", fontSize: 13, padding: "8px 14px", marginBottom: 12 }}>
      Rates have changed since the unit types were last updated, so totals here may still use the old rates. A bid cannot be sent or previewed until an admin clicks Update Unit Types on the Rate Catalog tab.
    </div>
  ) : null;

  return (
    <div style={S.root} onClick={() => { setExportMenu(null); setMoveMenu(null); }}>
      <style>{globalCss}</style>

      {/* ── Auth Loading Spinner ───────────────────────────────────────────── */}
      {authLoading && (
        <div style={{ minHeight: "100vh", background: "var(--rf-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: "var(--rf-text-dim)", fontSize: 14 }}>Loading…</div>
        </div>
      )}

      {/* ── Landing Page + Login ───────────────────────────────────────────── */}
      {!authLoading && !currentUser && (
          <div style={{ minHeight: "100vh", background: "var(--rf-bg)", display: "flex", flexDirection: "column" }}>
            {/* Nav */}
            <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 40px", borderBottom: "1px solid var(--rf-surface)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "white" }}>RF</div>
                <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "var(--rf-text-heading-alt)" }}>ReportForge</span>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <a href="/privacy" style={{ fontSize: 13, color: "var(--rf-text-dim)", textDecoration: "none" }}>Privacy Policy</a>
                <button onClick={() => setShowLogin(true)} style={{ padding: "8px 20px", background: "var(--rf-primary)", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Sign In</button>
              </div>
            </nav>

            {showLogin ? (
              /* ── Login form ── */
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <div style={{ background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 16, padding: "40px 48px", width: "100%", maxWidth: 420 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
                    <div style={{ width: 42, height: 42, background: "linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "white" }}>RF</div>
                    <div>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "var(--rf-text-heading-alt)" }}>ReportForge</div>
                      <div style={{ fontSize: 11, color: "var(--rf-text-dim)" }}>Sign in to continue</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Email</div>
                    <input autoFocus style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 8, color: "var(--rf-text-heading)", fontFamily: "'DM Sans',sans-serif", fontSize: 14, padding: "9px 13px", boxSizing: "border-box" }}
                      value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                      onKeyDown={async e => { if (e.key === "Enter") await login(); }} placeholder="Enter email" />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Password</div>
                    <input type="password" style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 8, color: "var(--rf-text-heading)", fontFamily: "'DM Sans',sans-serif", fontSize: 14, padding: "9px 13px", boxSizing: "border-box" }}
                      value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                      onKeyDown={async e => { if (e.key === "Enter") await login(); }} placeholder="Enter password" />
                  </div>
                  {loginError && <div style={{ color: "var(--rf-danger)", fontSize: 13, marginBottom: 14 }}>{loginError}</div>}
                  <button onClick={async () => await login()} disabled={loginLoading} style={{ width: "100%", padding: "10px", background: "var(--rf-primary)", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loginLoading ? "wait" : "pointer", opacity: loginLoading ? 0.7 : 1 }}>{loginLoading ? "Signing in…" : "Sign In"}</button>
                  <button onClick={() => setShowLogin(false)} style={{ width: "100%", marginTop: 12, padding: "8px", background: "transparent", border: "none", color: "var(--rf-text-dim)", fontSize: 13, cursor: "pointer" }}>← Back</button>
                </div>
              </div>
            ) : (
              /* ── Landing page ── */
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "72px 24px 80px" }}>
                {/* Hero */}
                <div style={{ textAlign: "center", maxWidth: 680, marginBottom: 72 }}>
                  <div style={{ display: "inline-block", background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 20, padding: "4px 14px", fontSize: 12, color: "var(--rf-accent)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 24 }}>Report Automation Platform</div>
                  <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(36px,6vw,56px)", color: "var(--rf-text-heading)", lineHeight: 1.15, marginBottom: 20 }}>
                    Build reports once.<br />Fill them forever.
                  </h1>
                  <p style={{ fontSize: 18, color: "var(--rf-muted)", lineHeight: 1.7, marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
                    ReportForge turns your existing PDF and Excel report templates into fast, fillable forms — then lets you export or email completed reports in one click.
                  </p>
                  <button onClick={() => setShowLogin(true)} style={{ padding: "14px 36px", background: "linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))", color: "white", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 24px rgba(99,102,241,0.4)" }}>
                    Sign In to ReportForge →
                  </button>
                </div>

                {/* Feature grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 20, width: "100%", maxWidth: 900, marginBottom: 64 }}>
                  {[
                    { icon: "📄", title: "PDF & Excel Templates", desc: "Import your existing report templates. ReportForge preserves every field, dropdown, and checkbox exactly as-is." },
                    { icon: "✏️", title: "Fast Form Filling", desc: "Fill reports using clean, native form fields. No reformatting, no copy-paste. Just type and save." },
                    { icon: "📧", title: "Send Without Downloading", desc: "Email completed reports as PDF attachments directly from ReportForge via your own Gmail account." },
                    { icon: "👥", title: "Team & Role Management", desc: "Invite inspectors, managers, and admins. Control who can create templates vs. fill reports." },
                    { icon: "🗂", title: "Organised File Library", desc: "Store reference specs, drawings, and sheets alongside your reports — all in one place." },
                    { icon: "🔒", title: "Secure by Design", desc: "Row-level security ensures each team's data is completely isolated. Your reports stay private." },
                  ].map(f => (
                    <div key={f.title} style={{ background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 12, padding: "24px 22px" }}>
                      <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--rf-text)", marginBottom: 8 }}>{f.title}</div>
                      <div style={{ fontSize: 13, color: "var(--rf-text-dim)", lineHeight: 1.6 }}>{f.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Bottom CTA */}
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 14, color: "var(--rf-text-faint)", marginBottom: 16 }}>Already have an account?</p>
                  <button onClick={() => setShowLogin(true)} style={{ padding: "10px 28px", background: "transparent", color: "var(--rf-accent)", border: "1px solid #4f46e5", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Sign In</button>
                </div>

                <div style={{ marginTop: 48, fontSize: 12, color: "var(--rf-surface)" }}>
                  <a href="/privacy" style={{ color: "var(--rf-text-faint)", textDecoration: "none" }}>Privacy Policy</a>
                </div>
              </div>
            )}
          </div>
      )}

      {/* ── Project Selection Screen ───────────────────────────────────────── */}
      {currentUser && !currentProject && !authLoading && (
        <div style={{ minHeight: "100vh", background: "var(--rf-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 16, padding: "40px 48px", width: "100%", maxWidth: 480 }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: "var(--rf-text-heading-alt)", marginBottom: 8 }}>Select a Project</div>
            <div style={{ fontSize: 13, color: "var(--rf-text-dim)", marginBottom: 28 }}>Choose which project to work on.</div>
            {projects.map(p => (
              <button key={p.id} onClick={() => setCurrentProject(p)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 18px", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 10, color: "var(--rf-text)", fontSize: 15, fontWeight: 600, cursor: "pointer", marginBottom: 10 }}>
                {p.name}
                {p.myRole && <span style={{ fontSize: 11, color: "var(--rf-text-dim)", fontWeight: 400, marginLeft: 10 }}>({p.myRole})</span>}
              </button>
            ))}
            {currentUser?.is_super_admin === undefined && <div style={{ color: "var(--rf-muted)", fontSize: 14 }}>Loading…</div>}
            {projects.length === 0 && currentUser?.is_super_admin === false && <div style={{ color: "var(--rf-muted)", fontSize: 14 }}>You have no projects yet. Contact your administrator.</div>}
            {currentUser?.is_super_admin && (
              <div style={{ marginTop: projects.length ? 24 : 0, paddingTop: projects.length ? 16 : 0, borderTop: projects.length ? "1px solid var(--rf-border)" : "none" }}>
                <div style={{ fontSize: 12, color: "var(--rf-primary)", fontWeight: 600, marginBottom: 10 }}>SUPER ADMIN</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={newProjectName}
                    onChange={e => setNewProjectName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && newProjectName.trim() && createProject(newProjectName.trim()).then(({ data }) => { if (data) { setProjects(prev => [...prev, data]); setNewProjectName(""); } })}
                    placeholder="New project name…"
                    style={{ flex: 1, padding: "8px 12px", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 8, color: "var(--rf-text)", fontSize: 13 }}
                  />
                  <button
                    onClick={async () => {
                      if (!newProjectName.trim()) return;
                      const { data } = await createProject(newProjectName.trim());
                      if (data) { setProjects(prev => [...prev, data]); setNewProjectName(""); }
                    }}
                    style={{ padding: "8px 14px", background: "var(--rf-primary)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    Create
                  </button>
                </div>
              </div>
            )}
            <button onClick={async () => await logout()} style={{ marginTop: 20, background: "transparent", border: "none", color: "var(--rf-text-dim)", fontSize: 12, cursor: "pointer" }}>Sign out</button>
          </div>
        </div>
      )}

      {currentUser && currentProject && screen === "home" && (
        <div style={S.homeWrap}>
          <aside style={S.sidebar}>
            <div style={S.logoArea}>
              <div style={S.logoBadge}>RF</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.logoName}>ReportForge</div>
                <div style={S.logoTagline}>{currentProject?.name || "Template-driven reports"}</div>
              </div>
              {projects.length > 1 && (
                <button
                  onClick={() => setCurrentProject(null)}
                  title="Switch project"
                  style={{ flexShrink: 0, background: "transparent", border: "1px solid #1e3a5f", borderRadius: 6, color: "#64748b", fontSize: 11, padding: "3px 7px", cursor: "pointer", lineHeight: 1.4 }}>
                  ← Projects
                </button>
              )}
            </div>
            {/* Sidebar nav */}
            <nav style={{ padding: "12px 10px 4px", display: "flex", flexDirection: "column", gap: 2 }}>
              {MODULE_KEYS.filter(k => isModuleVisible(k)).map(key => {
                const { icon } = MODULE_META[key];
                const active = homeTab === key;
                return (
                  <button key={key} onClick={() => setHomeTab(key)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px", background: active ? "var(--rf-surface)" : "transparent", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "var(--rf-accent)" : "var(--rf-muted)", textAlign: "left" }}>
                    <span style={{ fontSize: 15 }}>{icon}</span>{label(key, 2, vocabulary)}
                  </button>
                );
              })}
            </nav>
            <div style={{ padding: "16px 16px 8px", marginTop: "auto", borderTop: "1px solid var(--rf-surface)" }}>
              <div style={{ fontSize: 11, color: "var(--rf-text-faint)", marginBottom: 10 }}>
                {templates.length} template{templates.length !== 1 ? "s" : ""} · {reports.length} report{reports.length !== 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--rf-bg)", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                <div style={{ width: 28, height: 28, background: "linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white", flexShrink: 0 }}>
                  {(currentUser?.full_name || currentUser?.email || "?")?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--rf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentUser?.full_name || currentUser?.email}</div>
                  <div style={{ fontSize: 10, color: "var(--rf-text-dim)", textTransform: "capitalize" }}>{myRole}</div>
                </div>
              </div>
              {isAdmin && <button onClick={() => { setShowUserMgmt(true); }} style={{ width: "100%", padding: "6px", background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-muted)", fontSize: 11, cursor: "pointer", marginBottom: 6 }}>👥 Manage Users</button>}
              {isAdmin && <button onClick={() => setShowTabManager(true)} style={{ width: "100%", padding: "6px", background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-muted)", fontSize: 11, cursor: "pointer", marginBottom: 6 }}>🗂 Manage Tabs</button>}
              {isSuperAdmin && <button onClick={async () => { const [allP, allU] = await Promise.all([getAllProjects(), getAllUsers()]); setSuperAdminProjects(allP); setSuperAdminUsers(allU); setShowSuperAdmin(true); }} style={{ width: "100%", padding: "6px", background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-accent)", fontSize: 11, cursor: "pointer", marginBottom: 6 }}>⚙ Admin</button>}
              {gmailEmail ? (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 10, color: "#22c55e", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✓ Gmail: {gmailEmail}</div>
                  <button onClick={disconnectGmail} style={{ width: "100%", padding: "5px", background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-muted)", fontSize: 10, cursor: "pointer" }}>Disconnect Gmail</button>
                </div>
              ) : (
                <button onClick={connectGmail} style={{ width: "100%", padding: "6px", background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-soft)", fontSize: 11, cursor: "pointer", marginBottom: 6 }}>📧 Connect Gmail</button>
              )}
              <button onClick={async () => await logout()} style={{ width: "100%", padding: "6px", background: "transparent", border: "1px solid var(--rf-surface)", borderRadius: 7, color: "var(--rf-text-dim)", fontSize: 11, cursor: "pointer" }}>Sign Out</button>
            </div>
          </aside>

          <main style={S.homeMain}>

            {/* Auto-redirect non-admins away from templates tab */}
            {!isAdmin && homeTab === "templates" && (() => { setTimeout(() => setHomeTab("reports"), 0); return null; })()}

            {/* ── Templates Tab ── */}
            {homeTab === "templates" && isAdmin && (
              <section>
                <div style={S.secBar}>
                  <div><div style={S.secTitle}>Templates</div><div style={S.secDesc}>Design your structure once, reuse it forever.</div></div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.xls,.pdf" style={{ display: "none" }} onChange={handleExcelImport} />
                    <button onClick={downloadStarterTemplate} style={{ ...S.btnImport, background: "#0f4c35", borderColor: "#16a34a", color: "#86efac" }} title="Download a starter .xlsx you can use to build new report templates">
                      ⬇ Template Guide
                    </button>
                    <button onClick={() => fileInputRef.current?.click()} disabled={importing} style={{ ...S.btnImport, opacity: importing ? 0.6 : 1 }}>
                      {importing ? "⏳ Reading…" : "📥 Import Template"}
                    </button>
                    <button onClick={startNewTemplate} style={S.btnPrimary}>+ New Template</button>
                  </div>
                </div>
                <div style={S.fileLayout}>
                  {/* Folder sidebar */}
                  <FolderSidebar kind="tpl" folders={tplFolders} items={templates} activeFolder={activeTplFolder} setActiveFolder={setActiveTplFolder} onCreate={createFolder} onRename={renameFolder} onDelete={deleteFolder} canEdit={true} allLabel="All Templates" />
                  {/* Template cards */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {(activeTplFolder === null ? templates : templates.filter(t => getDescendantIds(tplFolders, activeTplFolder).has(t.folderId))).length === 0 && (
                      <Empty icon="🗂" text={activeTplFolder ? "No templates in this folder." : "No templates yet — create one or import an Excel template."} />
                    )}
                    <div style={S.grid}>
                      {(activeTplFolder === null ? templates : templates.filter(t => getDescendantIds(tplFolders, activeTplFolder).has(t.folderId))).map(t => (
                        <div key={t.id} style={S.card}>
                          <div style={{ ...S.cardStripe, background: "linear-gradient(90deg,var(--rf-primary),var(--rf-primary-soft))" }} />
                          <div style={S.cardBody}>
                            <div style={S.cardTitle}>{t.name || "Untitled Template"}</div>
                            {t.description && <div style={S.cardDesc}>{t.description}</div>}
                            <div style={S.cardMeta}>{(() => { const secs = t.isMultiSheet ? (t.sheets||[]).flatMap(sh=>sh.sections||[]) : t.sections; return `${secs.length} section${secs.length!==1?"s":""} · ${secs.reduce((a,s)=>a+s.fields.length,0)} fields`; })()}{t.isMultiSheet && ` · ${(t.sheets||[]).length} sheets`}</div>
                            {t.folderId && <div style={S.cardFolderBadge}>📁 {folderPath(tplFolders, t.folderId)}</div>}
                            <div style={S.cardActions}>
                              <button onClick={() => editTemplate(t)} style={S.btnGhost}>Edit</button>
                              <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
                                <button onClick={() => setMoveMenu(moveMenu === t.id ? null : t.id)} style={S.btnGhost}>📁 Move ▾</button>
                                {moveMenu === t.id && (
                                  <div style={S.moveDropdown}>
                                    <div style={S.moveItem} onClick={async () => await moveToFolder("tpl", t.id, null)}>— No Folder</div>
                                    {flattenFolders(tplFolders).map(f => <div key={f.id} style={{ ...S.moveItem, paddingLeft: 14 + f.depth * 12 }} onClick={async () => await moveToFolder("tpl", t.id, f.id)}>📁 {f.name}</div>)}
                                  </div>
                                )}
                              </div>
                              <button onClick={async () => { if (window.confirm(`Delete template "${t.name || "Untitled Template"}"?`)) await deleteTpl(t.id); }} style={S.btnRed}>Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ── Rate Catalog Tab ── */}
            {homeTab === "catalog" && isAdmin && (
              <section>
                <div style={S.secBar}>
                  <div>
                    {/* "Rate Catalog" isn't a pure module noun (it's "Rate" +
                        catalog's default "Catalog"), so it stays literal
                        until a client vocabulary actually overrides it. */}
                    <div style={S.secTitle}>{vocabulary.catalog ? label("catalog", 2, vocabulary) : "Rate Catalog"}</div>
                    <div style={S.secDesc}>Paint costs, labor rates, and prep-severity pricing every estimate pulls from.</div>
                  </div>
                  <button onClick={updateUnitTypes} disabled={updatingUnitTypes} style={{ ...S.btnPrimary, opacity: updatingUnitTypes ? 0.6 : 1 }}>{updatingUnitTypes ? "Updating…" : "Update unit types"}</button>
                </div>
                {ratesDirty && (
                  <div style={{ background: "var(--rf-bg)", border: "1px solid var(--rf-danger)", borderRadius: 7, color: "var(--rf-danger)", fontSize: 13, padding: "8px 14px", marginBottom: 12 }}>
                    Rates have changed. Unit Types may be out of date. Click Update Unit Types to bake the new rates into the stored templates.
                  </div>
                )}
                <RateCatalog projectId={currentProject.id} isAdmin={isAdmin} onChange={() => markRatesDirty(currentProject.id, true)} vocabulary={vocabulary} />
              </section>
            )}

            {/* ── Jobs Tab ── */}
            {homeTab === "jobs" && jobsEnabled && (
              <>
              {ratesDirtyJobsBanner}
              {/* Final fix wave, item 11: an empty list after a failed load
                  must not read as "no jobs yet". */}
              {jobsLoadState === "error" && (
                <div style={{ background: "var(--rf-bg)", border: "1px solid var(--rf-danger)", borderRadius: 7, color: "var(--rf-danger)", fontSize: 13, padding: "8px 14px", marginBottom: 12 }}>
                  Could not load this project's jobs. Reload the page to try again. Until they load, takeoffs and templates cannot be edited, submitted or deleted.
                </div>
              )}
              <JobsList jobs={jobs} jobTotals={jobTotals} canEdit={isAdmin || myRole === "manager"} onOpen={openJob} onNew={createNewJob} creating={creatingJob} vocabulary={vocabulary} />
              </>
            )}

            {/* ── Reports Tab ── */}
            {homeTab === "reports" && (() => {
              const searchActive = repSearch.text.trim() || repSearch.dateFrom || repSearch.dateTo || repSearch.templateId;
              const visibleReports = (() => {
                let list = searchActive
                  ? reports  // search ignores folders — finds across all
                  : (activeRepFolder === null ? reports : reports.filter(r => getDescendantIds(repFolders, activeRepFolder).has(r.folderId)));
                // Base role: only see submitted reports
                if (isBase) list = list.filter(r => r._submitted);
                if (repSearch.text.trim()) {
                  const q = repSearch.text.trim().toLowerCase();
                  list = list.filter(r =>
                    (r.reportTitle || "").toLowerCase().includes(q) ||
                    (r._templateName || "").toLowerCase().includes(q) ||
                    (r._author || "").toLowerCase().includes(q)
                  );
                }
                if (repSearch.templateId) list = list.filter(r => r._templateId === repSearch.templateId);
                if (repSearch.dateFrom) list = list.filter(r => r._date && r._date >= repSearch.dateFrom);
                if (repSearch.dateTo)   list = list.filter(r => r._date && r._date <= repSearch.dateTo);
                return list;
              })();
              return (
              <section>
                <div style={S.secBar}>
                  <div><div style={S.secTitle}>Reports</div><div style={S.secDesc}>All your filled-out reports.</div></div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {reports.length > 0 && (
                      <button onClick={() => exportAllToExcel(reports, templates)} style={S.btnXlsx}>⬇ Export All to Excel</button>
                    )}
                    {canEditReports && <button onClick={() => setScreen("pickTemplate")} style={S.btnPrimary} disabled={templates.length === 0}>+ New Report</button>}
                  </div>
                </div>

                {/* ── Search / Filter Bar ── */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 0 4px", alignItems: "flex-end" }}>
                  <div style={{ flex: "1 1 180px", minWidth: 140 }}>
                    <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>Search</div>
                    <input
                      placeholder="Title, author, template…"
                      value={repSearch.text}
                      onChange={e => setRepSearch(s => ({ ...s, text: e.target.value }))}
                      style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "6px 10px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ flex: "1 1 140px", minWidth: 120 }}>
                    <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>Template</div>
                    <select
                      value={repSearch.templateId}
                      onChange={e => setRepSearch(s => ({ ...s, templateId: e.target.value }))}
                      style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: repSearch.templateId ? "var(--rf-text-heading)" : "var(--rf-muted)", fontSize: 13, padding: "6px 10px", boxSizing: "border-box", cursor: "pointer" }}
                    >
                      <option value="">All templates</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>From</div>
                    <input
                      type="date"
                      value={repSearch.dateFrom}
                      onChange={e => setRepSearch(s => ({ ...s, dateFrom: e.target.value }))}
                      style={{ background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: repSearch.dateFrom ? "var(--rf-text-heading)" : "var(--rf-muted)", fontSize: 13, padding: "6px 10px", cursor: "pointer" }}
                    />
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>To</div>
                    <input
                      type="date"
                      value={repSearch.dateTo}
                      onChange={e => setRepSearch(s => ({ ...s, dateTo: e.target.value }))}
                      style={{ background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: repSearch.dateTo ? "var(--rf-text-heading)" : "var(--rf-muted)", fontSize: 13, padding: "6px 10px", cursor: "pointer" }}
                    />
                  </div>
                  {searchActive && (
                    <button
                      onClick={() => setRepSearch({ text: "", dateFrom: "", dateTo: "", templateId: "" })}
                      style={{ ...S.btnGhost, alignSelf: "flex-end", fontSize: 12, padding: "6px 12px" }}
                    >✕ Clear</button>
                  )}
                </div>
                {searchActive && (
                  <div style={{ fontSize: 12, color: "var(--rf-primary)", marginBottom: 4, paddingLeft: 2 }}>
                    {visibleReports.length} result{visibleReports.length !== 1 ? "s" : ""} across all folders
                  </div>
                )}

                <div style={S.fileLayout}>
                  {/* Folder sidebar — hidden when search is active */}
                  {!searchActive && <FolderSidebar kind="rep" folders={repFolders} items={reports} activeFolder={activeRepFolder} setActiveFolder={setActiveRepFolder} onCreate={createFolder} onRename={renameFolder} onDelete={deleteFolder} canEdit={canEditReports} allLabel="All Reports" />}
                  {/* Report list */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {visibleReports.length === 0 && (
                      <Empty icon="📄" text={searchActive ? "No reports match your search." : activeRepFolder ? "No reports in this folder." : "No reports yet — click New Report to get started."} />
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {visibleReports.map(r => {
                        const tpl = templates.find(t => t.id === r._templateId);
                        const allFields = (tpl?.sections || []).flatMap(sec =>
                          (sec.fields || []).filter(f => r[f.id] && r[f.id] !== "").map(f => ({ ...f, secName: sec.name }))
                        );
                        return (
                          <div key={r.id} style={S.reportRow}>
                            <div style={S.reportRowAccent} />
                            <div style={S.reportLeft}>
                              <div style={S.reportTitleRow}>
                                <div style={S.reportRowTitle}>{r.reportTitle || "Untitled Report"}</div>
                                {r._submitted && (
                                  <span style={{fontSize:10,fontWeight:700,color:"#86efac",background:"#0f2a1e",border:"1px solid #16a34a",borderRadius:4,padding:"2px 7px",letterSpacing:"0.3px",whiteSpace:"nowrap"}}>
                                    ✓ VALIDATED
                                  </span>
                                )}
                                {r._actionNeeded && !r._submitted && (
                                  <span style={{fontSize:10,fontWeight:700,color:"#fff",background:"var(--rf-danger-strong)",borderRadius:4,padding:"2px 7px",letterSpacing:"0.3px",whiteSpace:"nowrap"}}>
                                    ACTION NEEDED
                                  </span>
                                )}
                                <div style={S.reportInlineMeta}>
                                  {r._author && <span style={S.reportMetaChip}>👤 {r._author}</span>}
                                  {r._date && <span style={S.reportMetaChip}>📅 {r._date}</span>}
                                </div>
                              </div>
                              {r._templateName && <span style={S.reportRowBadge}>{r._templateName}</span>}
                              {r.folderId && <div style={{ ...S.cardFolderBadge, display: "inline-block", marginBottom: 4 }}>📁 {folderPath(repFolders, r.folderId)}</div>}
                              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                                {canEditReports && !r._submitted && !sentTakeoffIds.has(r.id) && <button onClick={() => { if (!refuseIfJobsUnknown()) openReport(r); }} style={S.btnGreen}>Edit</button>}
                                {sentTakeoffIds.has(r.id) && <span title={SENT_TAKEOFF_LOCKED} style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-text-soft)", border: "1px solid var(--rf-border)", borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>Sent bid, read only</span>}
                                <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
                                  <button onClick={() => setExportMenu(exportMenu === r.id ? null : r.id)} style={S.btnGhost}>⬇ Export ▾</button>
                                  {exportMenu === r.id && (
                                    <div style={S.dropdown}>
                                      <div style={S.dropItem} onMouseEnter={e => e.currentTarget.style.background="var(--rf-text-faint)"} onMouseLeave={e => e.currentTarget.style.background="transparent"} onClick={async () => {
                                        if (tpl?.isPdf) {
                                          await exportFilledPdf(await getRawBytesForTemplate(tpl.id), r, tpl);
                                        } else if (tpl?.isProctor) {
                                          generatePDF(r, tpl, await generateProctorChartImage(computeTrials(r, tpl)));
                                        } else if (tpl?.isExcelImport) {
                                          // Route through the Excel exporter, same as the open-report PDF
                                          // button. Going straight to generatePDF here produced a different
                                          // document from the list than from the open report: no Excel
                                          // layout and no charts.
                                          const rawBytes = await getRawBytesForTemplate(tpl.id);
                                          const wb = await getWorkbookForTemplate(tpl.id);
                                          const chartImages = await buildChartImagesForTemplate(tpl, rawBytes, wb);
                                          const ok = rawBytes ? await exportReportAsExcelPDF(r, tpl, rawBytes, jsPDF, chartImages) : false;
                                          if (!ok) generatePDF(r, tpl, null);
                                        } else {
                                          generatePDF(r, tpl, null);
                                        }
                                        setExportMenu(null);
                                      }}>📄 PDF</div>
                                      {!tpl?.isPdf && <div style={S.dropItem} onMouseEnter={e => e.currentTarget.style.background="var(--rf-text-faint)"} onMouseLeave={e => e.currentTarget.style.background="transparent"} onClick={async () => {
                                        if (tpl?.isProctor) { await exportProctorToExcel(r, await getRawBytesForTemplate(tpl.id)); }
                                        else { const wb = tpl?.isExcelImport ? await getWorkbookForTemplate(tpl.id) : null; wb ? exportReportAsExcelClone(r, tpl, wb) : exportToExcel(r, tpl); }
                                        setExportMenu(null);
                                      }}>📊 Excel (.xlsx)</div>}
                                    </div>
                                  )}
                                </div>
                                {canEditReports && !r._submitted && <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
                                  <button onClick={() => setMoveMenu(moveMenu === r.id ? null : r.id)} style={S.btnGhost}>📁 Move ▾</button>
                                  {moveMenu === r.id && (
                                    <div style={S.moveDropdown}>
                                      <div style={S.moveItem} onClick={async () => await moveToFolder("rep", r.id, null)}>— No Folder</div>
                                      {flattenFolders(repFolders).map(f => <div key={f.id} style={{ ...S.moveItem, paddingLeft: 14 + f.depth * 12 }} onClick={async () => await moveToFolder("rep", r.id, f.id)}>📁 {f.name}</div>)}
                                    </div>
                                  )}
                                </div>}
                                {canEditReports && !r._submitted && !sentTakeoffIds.has(r.id) && <button onClick={async () => { if (refuseIfJobsUnknown()) return; if (window.confirm(`Delete report "${r.reportTitle || "Untitled Report"}"?`)) await deleteReport(r.id); }} style={S.btnRed}>Delete</button>}
                                {canEditReports && !r._submitted && !sentTakeoffIds.has(r.id) && (
                                  <button onClick={() => submitReport(r)} style={{ ...S.btnPrimary, background: "#0f4c35", borderColor: "#16a34a", color: "#86efac" }}>
                                    ✓ Submit
                                  </button>
                                )}
                                {r._submitted && (
                                  <span style={{ fontSize: 11, fontWeight: 700, color: "#86efac", background: "#0f2a1e", border: "1px solid #16a34a", borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                                    ✓ Submitted
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={S.reportDivider} />
                            <div style={S.reportRight}>
                              {allFields.length === 0 ? (
                                <div style={{ fontSize: 12, color: "var(--rf-text-dim)", fontStyle: "italic" }}>No data filled in yet — click Edit to add content.</div>
                              ) : (
                                <div style={S.reportFieldsGrid}>
                                  {allFields.map(f => (
                                    <div key={f.id} style={S.inlineField}>
                                      <div style={S.inlineFieldLabel}>{f.label}</div>
                                      <div style={S.inlineFieldValue}>{f.type === "image" ? "Image" : r[f.id]}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
              );
            })()}
            {/* ── Sheets Tab ── */}
            {homeTab === "sheets" && (
              <section>
                <div style={S.secBar}>
                  <div><div style={S.secTitle}>Sheets</div><div style={S.secDesc}>Imported PDF sheets — organized and ready to view.</div></div>
                  {canEditReports && (
                    <div>
                      <input ref={sheetFileRef} type="file" accept="application/pdf" multiple style={{ display: "none" }} onChange={importSheet} />
                      <button onClick={() => sheetFileRef.current?.click()} style={S.btnImport}>📥 Import PDF</button>
                    </div>
                  )}
                </div>
                <div style={S.fileLayout}>
                  {/* Folder sidebar */}
                  <FolderSidebar kind="sheet" folders={sheetFolders} items={sheets} activeFolder={activeSheetFolder} setActiveFolder={setActiveSheetFolder} onCreate={createFolder} onRename={renameFolder} onDelete={deleteFolder} canEdit={canEditReports} allLabel="All Sheets" />

                  {/* Sheet cards */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {(activeSheetFolder === null ? sheets : sheets.filter(s => getDescendantIds(sheetFolders, activeSheetFolder).has(s.folderId))).length === 0 && (
                      <Empty icon="🗂" text={activeSheetFolder ? "No sheets in this folder." : "No sheets yet — import a PDF to get started."} />
                    )}
                    <div style={S.grid}>
                      {(activeSheetFolder === null ? sheets : sheets.filter(s => getDescendantIds(sheetFolders, activeSheetFolder).has(s.folderId))).map(s => (
                        <div key={s.id} style={{ ...S.card, cursor: "pointer" }} onClick={async () => await openSheetViewer(s)}>
                          <div style={{ ...S.cardStripe, background: "linear-gradient(90deg,var(--rf-danger-strong),#b91c1c)" }} />
                          <div style={S.cardBody}>
                            <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                            <div style={S.cardTitle}>{s.name}</div>
                            <div style={S.cardMeta}>
                              {s.uploadedAt ? new Date(s.uploadedAt).toLocaleDateString() : ""}
                              {s.size ? ` · ${(s.size / 1024).toFixed(0)} KB` : ""}
                            </div>
                            {s.folderId && <div style={S.cardFolderBadge}>📁 {folderPath(sheetFolders, s.folderId)}</div>}
                            <div style={S.cardActions} onClick={e => e.stopPropagation()}>
                              <button onClick={async () => await openSheetViewer(s)} style={S.btnGhost}>👁 View</button>
                              {canEditReports && (
                                <div style={{ position: "relative" }}>
                                  <button onClick={() => setMoveMenu(moveMenu === s.id ? null : s.id)} style={S.btnGhost}>📁 Move ▾</button>
                                  {moveMenu === s.id && (
                                    <div style={S.moveDropdown}>
                                      <div style={S.moveItem} onClick={async () => await moveSheetToFolder(s.id, null)}>— No Folder</div>
                                      {flattenFolders(sheetFolders).map(f => <div key={f.id} style={{ ...S.moveItem, paddingLeft: 14 + f.depth * 12 }} onClick={async () => await moveSheetToFolder(s.id, f.id)}>📁 {f.name}</div>)}
                                    </div>
                                  )}
                                </div>
                              )}
                              {canEditReports && <button onClick={async () => { if (window.confirm(`Delete sheet "${s.name}"?`)) await deleteSheet(s.id); }} style={S.btnRed}>Delete</button>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ── Specs Tab ── */}
            {homeTab === "specs" && (
              <section>
                <div style={S.secBar}>
                  <div><div style={S.secTitle}>Specs</div><div style={S.secDesc}>Imported specification PDFs — organized and ready to view.</div></div>
                  {canEditReports && (
                    <div>
                      <input ref={specFileRef} type="file" accept="application/pdf" multiple style={{ display: "none" }} onChange={importSpec} />
                      <button onClick={() => specFileRef.current?.click()} style={S.btnImport}>📥 Import PDF</button>
                    </div>
                  )}
                </div>
                <div style={S.fileLayout}>
                  {/* Folder sidebar */}
                  <FolderSidebar kind="spec" folders={specFolders} items={specs} activeFolder={activeSpecFolder} setActiveFolder={setActiveSpecFolder} onCreate={createFolder} onRename={renameFolder} onDelete={deleteFolder} canEdit={canEditReports} allLabel="All Specs" />

                  {/* Spec cards */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {(activeSpecFolder === null ? specs : specs.filter(s => getDescendantIds(specFolders, activeSpecFolder).has(s.folderId))).length === 0 && (
                      <Empty icon="📑" text={activeSpecFolder ? "No specs in this folder." : "No specs yet — import a PDF to get started."} />
                    )}
                    <div style={S.grid}>
                      {(activeSpecFolder === null ? specs : specs.filter(s => getDescendantIds(specFolders, activeSpecFolder).has(s.folderId))).map(s => (
                        <div key={s.id} style={{ ...S.card, cursor: "pointer" }} onClick={async () => await openSpecViewer(s)}>
                          <div style={{ ...S.cardStripe, background: "linear-gradient(90deg,#0369a1,#0284c7)" }} />
                          <div style={S.cardBody}>
                            <div style={{ fontSize: 28, marginBottom: 8 }}>📑</div>
                            <div style={S.cardTitle}>{s.name}</div>
                            <div style={S.cardMeta}>
                              {s.uploadedAt ? new Date(s.uploadedAt).toLocaleDateString() : ""}
                              {s.size ? ` · ${(s.size / 1024).toFixed(0)} KB` : ""}
                            </div>
                            {s.folderId && <div style={S.cardFolderBadge}>📁 {folderPath(specFolders, s.folderId)}</div>}
                            <div style={S.cardActions} onClick={e => e.stopPropagation()}>
                              <button onClick={async () => await openSpecViewer(s)} style={S.btnGhost}>👁 View</button>
                              {canEditReports && (
                                <div style={{ position: "relative" }}>
                                  <button onClick={() => setMoveMenu(moveMenu === s.id ? null : s.id)} style={S.btnGhost}>📁 Move ▾</button>
                                  {moveMenu === s.id && (
                                    <div style={S.moveDropdown}>
                                      <div style={S.moveItem} onClick={async () => await moveSpecToFolder(s.id, null)}>— No Folder</div>
                                      {flattenFolders(specFolders).map(f => <div key={f.id} style={{ ...S.moveItem, paddingLeft: 14 + f.depth * 12 }} onClick={async () => await moveSpecToFolder(s.id, f.id)}>📁 {f.name}</div>)}
                                    </div>
                                  )}
                                </div>
                              )}
                              {canEditReports && <button onClick={async () => { if (window.confirm(`Delete spec "${s.name}"?`)) await deleteSpec(s.id); }} style={S.btnRed}>Delete</button>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </main>
        </div>
      )}

      {/* ── Spec PDF Viewer ───────────────────────────────────────────────────── */}
      {viewingSpec && specBlobUrl && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 3000, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", background: "var(--rf-sidebar)", borderBottom: "1px solid var(--rf-surface)" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, color: "var(--rf-text-heading)" }}>📑 {viewingSpec.name}</div>
            <button onClick={closeSpecViewer} style={{ background: "transparent", border: "1px solid var(--rf-text-faint)", borderRadius: 7, color: "var(--rf-text-soft)", fontSize: 13, padding: "6px 14px", cursor: "pointer" }}>✕ Close</button>
          </div>
          <iframe src={specBlobUrl} style={{ flex: 1, border: "none", background: "white" }} title={viewingSpec.name} />
        </div>
      )}

      {/* ── Sheet PDF Viewer ──────────────────────────────────────────────────── */}
      {viewingSheet && sheetBlobUrl && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 3000, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", background: "var(--rf-sidebar)", borderBottom: "1px solid var(--rf-surface)" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, color: "var(--rf-text-heading)" }}>📄 {viewingSheet.name}</div>
            <button onClick={closeSheetViewer} style={{ background: "transparent", border: "1px solid var(--rf-text-faint)", borderRadius: 7, color: "var(--rf-text-soft)", fontSize: 13, padding: "6px 14px", cursor: "pointer" }}>✕ Close</button>
          </div>
          <iframe src={sheetBlobUrl} style={{ flex: 1, border: "none", background: "white" }} title={viewingSheet.name} />
        </div>
      )}

      {currentUser && screen === "pickTemplate" && (
        <div style={S.editorWrap}>
          <div style={S.topBar}>
            <button onClick={() => setScreen("home")} style={S.btnBack}>← Back</button>
            <div style={S.topBarTitle}>Choose a Template</div>
            <div style={{ width: 120 }} />
          </div>
          <div style={S.editorScroll}>
            <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px" }}>
              <div style={{ fontSize: 14, color: "var(--rf-muted)", marginBottom: 24 }}>Select a template to base your new report on.</div>
              <div style={S.grid}>
                {templates.map(t => (
                  <div key={t.id} style={{ ...S.card, cursor: "pointer" }} onClick={() => startReport(t)}>
                    <div style={{ ...S.cardStripe, background: "linear-gradient(90deg,#10b981,#059669)" }} />
                    <div style={S.cardBody}>
                      <div style={S.cardTitle}>{t.name || "Untitled Template"}</div>
                      {t.description && <div style={S.cardDesc}>{t.description}</div>}
                      <div style={S.cardMeta}>{(() => { const secs = t.isMultiSheet ? (t.sheets||[]).flatMap(sh=>sh.sections||[]) : t.sections; return `${secs.length} section${secs.length!==1?"s":""} · ${secs.reduce((a,s)=>a+s.fields.length,0)} fields`; })()}{t.isMultiSheet && ` · ${(t.sheets||[]).length} sheets`}</div>
                      <div style={{ marginTop: 12 }}>
                        <button onClick={e => { e.stopPropagation(); startReport(t); }} style={S.btnGreen}>Use This Template →</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {currentUser && screen === "buildTemplate" && activeTpl && (
        <div style={S.editorWrap}>
          <div style={S.topBar}>
            <button onClick={() => { setScreen("home"); setHomeTab("templates"); }} style={S.btnBack}>← Back</button>
            <div style={S.topBarTitle}>Template Builder</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ fontSize: 11, color: "var(--rf-muted)" }}>
                {activeTpl.sections.reduce((a, s) => a + s.fields.length, 0)} fields
                {activeTpl.sections.flatMap(s => s.fields).some(f => f.required) && (
                  <span style={{ color: "var(--rf-danger)", marginLeft: 6 }}>· {activeTpl.sections.flatMap(s => s.fields).filter(f => f.required).length} required</span>
                )}
              </div>
              <button onClick={saveTemplate} style={{ ...S.btnPrimary, background: flash ? "#059669" : "#10b981", minWidth: 130 }}>{flash ? "✓ Saved!" : "Save Template"}</button>
            </div>
          </div>
          <div style={S.editorScroll}>
            <div style={S.editorInner}>
              {/* ── Template metadata ── */}
              <div style={S.metaBox}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Fld label="Template Name"><input style={S.inp} placeholder="e.g. Monthly Status Report" value={activeTpl.name} onChange={e => setTF("name", e.target.value)} /></Fld>
                  <Fld label="Description (optional)"><input style={S.inp} placeholder="What is this template for?" value={activeTpl.description} onChange={e => setTF("description", e.target.value)} /></Fld>
                </div>
                <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <Fld label="Folder" noMb>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select style={{ ...S.inp, width: "auto", minWidth: 180 }} value={activeTpl.folderId || ""} onChange={e => setTF("folderId", e.target.value || null)}>
                        <option value="">— No Folder —</option>
                        {tplFolders.map(f => <option key={f.id} value={f.id}>📁 {f.name}</option>)}
                      </select>
                      <button style={S.btnGhost} onClick={async () => {
                        const name = window.prompt("New folder name:");
                        if (!name?.trim()) return;
                        const { data } = await dbCreateFolder(currentProject.id, "tpl", name.trim());
                        if (data) { setTplFolders(f => [...f, data]); setTF("folderId", data.id); }
                      }}>+ New Folder</button>
                    </div>
                  </Fld>
                </div>
                {/* Legend */}
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--rf-border)", display: "flex", gap: 20, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)" }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#fffbcc", border: "1px solid #a3a300", marginRight: 5, verticalAlign: "middle" }} />
                    Optional field
                  </div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)" }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#fee2e2", border: "1px solid var(--rf-danger)", marginRight: 5, verticalAlign: "middle" }} />
                    Required field — user must fill before saving
                  </div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)" }}>
                    <span style={{ marginRight: 5 }}>🖼</span>Photo / Image — user uploads a photo directly in the report
                  </div>
                </div>
              </div>

              {/* ── Sections ── */}
              {activeTpl.sections.map((sec, si) => (
                <div key={sec.id} style={S.secBox}>
                  <div style={S.secBoxHead}>
                    <div style={S.secPill}>{si + 1}</div>
                    <input style={{ ...S.inp, flex: 1, fontWeight: 600 }} placeholder="Section name…" value={sec.name} onChange={e => updSec(sec.id, "name", e.target.value)} />
                    <div style={{ fontSize: 11, color: "var(--rf-text-dim)", whiteSpace: "nowrap", padding: "0 8px" }}>
                      {sec.fields.length} field{sec.fields.length !== 1 ? "s" : ""}
                    </div>
                    {activeTpl.sections.length > 1 && <button onClick={() => remSec(sec.id)} style={S.iconX}>×</button>}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    {sec.fields.length === 0 && (
                      <div style={{ fontSize: 12, color: "#4b5563", fontStyle: "italic", paddingBottom: 10 }}>
                        No fields yet — click "+ Add Field" below.
                      </div>
                    )}
                    {sec.fields.map((f, fi) => (
                      <div key={f.id} style={{ ...S.fieldBox, background: f.required ? "#1a0f0f" : "var(--rf-bg)", borderColor: f.required ? "#7f1d1d" : "var(--rf-border)" }}>
                        {/* Row 1: number + label + type + required toggle + delete */}
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                          <div style={{ ...S.fieldNum, flexShrink: 0, alignSelf: "center" }}>{fi + 1}</div>
                          <div style={{ flex: "1 1 160px", minWidth: 120 }}>
                            <div style={{ fontSize: 10, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                              Label {f.required && <span style={{ color: "var(--rf-danger)" }}>*</span>}
                            </div>
                            <input style={S.inp} placeholder="e.g. Project Name" value={f.label} onChange={e => updFld(sec.id, f.id, "label", e.target.value)} />
                          </div>
                          <div style={{ flex: "0 0 160px" }}>
                            <div style={{ fontSize: 10, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Type</div>
                            <select style={S.inp} value={f.type} onChange={e => updFld(sec.id, f.id, "type", e.target.value)}>
                              {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.icon} {ft.label}</option>)}
                            </select>
                          </div>
                          {/* Required toggle */}
                          {f.type !== "image" && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                              <div style={{ fontSize: 10, color: f.required ? "var(--rf-danger)" : "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Required</div>
                              <div
                                onClick={() => updFld(sec.id, f.id, "required", !f.required)}
                                style={{ width: 40, height: 22, borderRadius: 11, background: f.required ? "var(--rf-danger-strong)" : "var(--rf-surface)", border: `1px solid ${f.required ? "var(--rf-danger)" : "#374151"}`, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
                              >
                                <div style={{ width: 16, height: 16, borderRadius: 8, background: "#fff", position: "absolute", top: 2, left: f.required ? 20 : 2, transition: "left 0.15s" }} />
                              </div>
                            </div>
                          )}
                          <button onClick={() => remFld(sec.id, f.id)} style={{ ...S.iconX, flexShrink: 0, alignSelf: "center", marginTop: 16 }}>×</button>
                        </div>

                        {/* Row 2: extra config based on type */}
                        {f.type === "select" && (
                          <div style={{ display: "flex", gap: 10, marginTop: 10, paddingLeft: 30, flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 200px" }}>
                              <div style={{ fontSize: 10, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Options (comma-separated)</div>
                              <input style={S.inp} placeholder="Option A, Option B, Option C" value={f.options || ""} onChange={e => updFld(sec.id, f.id, "options", e.target.value)} />
                            </div>
                            <div style={{ flex: "0 0 180px" }}>
                              <div style={{ fontSize: 10, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Default (optional)</div>
                              <select style={S.inp} value={f.defaultValue || ""} onChange={e => updFld(sec.id, f.id, "defaultValue", e.target.value)}>
                                <option value="">— None —</option>
                                {(f.options || "").split(",").map(o => o.trim()).filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                        {f.type !== "select" && f.type !== "image" && (
                          <div style={{ marginTop: 10, paddingLeft: 30 }}>
                            <div style={{ fontSize: 10, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Default Value (optional)</div>
                            <input style={{ ...S.inp, maxWidth: 320 }} placeholder="Pre-filled value shown in the report form" value={f.defaultValue || ""} onChange={e => updFld(sec.id, f.id, "defaultValue", e.target.value)} />
                          </div>
                        )}
                        {f.type === "image" && (
                          <div style={{ marginTop: 8, paddingLeft: 30, fontSize: 12, color: "var(--rf-muted)", fontStyle: "italic" }}>
                            Users will see a photo upload button in the report form. Supports JPG, PNG, GIF, WebP.
                          </div>
                        )}
                      </div>
                    ))}
                    <button onClick={() => addFld(sec.id)} style={S.btnAddField}>+ Add Field</button>
                  </div>
                </div>
              ))}
              <button onClick={addSec} style={S.btnAddSection}>+ Add Section</button>
            </div>
          </div>
        </div>
      )}

      {currentUser && screen === "fillReport" && activeRep && (
        <div style={S.editorWrap}>
          <div style={S.topBar}>
            <button onClick={() => {
              if (cameFromJobId) {
                returnToJob(cameFromJobId);
              } else {
                setScreen("home"); setHomeTab("reports");
              }
            }} style={S.btnBack}>{cameFromJobId ? "← Back to job" : "← Back"}</button>
            <div style={{ display: "flex", gap: 6 }}>
              {(activeRepLocked ? ["preview"] : ["edit", "preview"]).map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ ...S.tabBtn, ...(tab === t ? S.tabBtnOn : {}) }}>{t === "edit" ? "✏️ Edit" : "👁 Preview"}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {activeRepLocked ? (
                <span title={SENT_TAKEOFF_LOCKED} style={{ alignSelf: "center", fontSize: 12, color: "var(--rf-text-soft)", border: "1px solid var(--rf-border)", borderRadius: 6, padding: "6px 10px" }}>Read only. Part of a sent bid.</span>
              ) : (
                <button onClick={async () => { await saveReport(); }} style={{ ...S.btnPrimary, background: flash ? "#059669" : "#10b981", minWidth: 120 }}>{flash ? "✓ Saved!" : "Save Report"}</button>
              )}
              <button onClick={async () => {
                if (repTpl?.isPdf) {
                  await exportFilledPdf(repTplRawBytes, activeRep, repTpl);
                } else if (repTpl?.isProctor) {
                  const chartImg = await generateProctorChartImage(computeTrials(activeRep, repTpl));
                  generatePDF(activeRep, repTpl, chartImg);
                } else if (repTpl?.isExcelImport) {
                  const rawBytes = await getRawBytesForTemplate(repTpl.id);
                  // Render chart configs to data URLs so they can be embedded in the PDF
                  const chartImagesBySheet = {};
                  for (const [sheetName, cfgs] of Object.entries(chartsBySheet)) {
                    chartImagesBySheet[sheetName] = await Promise.all(
                      cfgs.map(cfg => chartConfigToDataUrl(cfg, cfg.options?.plugins?.title?.text || ""))
                    );
                  }
                  const ok = rawBytes ? await exportReportAsExcelPDF(activeRep, repTpl, rawBytes, jsPDF, chartImagesBySheet) : false;
                  if (!ok) generatePDF(activeRep, repTpl, null);
                } else {
                  generatePDF(activeRep, repTpl, null);
                }
              }} style={{ ...S.btnPrimary, background: "var(--rf-primary)" }}>📄 PDF</button>
              <button onClick={async () => {
                if (repTpl?.isProctor) {
                  await exportProctorToExcel(activeRep, await getRawBytesForTemplate(repTpl.id));
                } else if (repTpl?.isExcelImport) {
                  // Try JSZip raw-byte export first (preserves all formatting & formulas)
                  const rawBytes = await getRawBytesForTemplate(repTpl.id);
                  const ok = rawBytes ? await exportReportAsExcelRaw(activeRep, repTpl, rawBytes) : false;
                  // Fall back to SheetJS clone if raw export unavailable
                  if (!ok) {
                    const wb = await getWorkbookForTemplate(repTpl.id);
                    if (wb) exportReportAsExcelClone(activeRep, repTpl, wb);
                    else exportToExcel(activeRep, repTpl);
                  }
                } else {
                  exportToExcel(activeRep, repTpl);
                }
              }} style={S.btnXlsx} hidden={!!repTpl?.isPdf}>📊 Excel</button>
              <button onClick={() => setSendEmailModal({
                to: "", cc: "", subject: activeRep?.name || "Report", message: "", sending: false, error: null,
              })} style={{ ...S.btnGhost, color: "var(--rf-text-soft)" }} title={gmailEmail ? `Send via ${gmailEmail}` : "Connect Gmail to send email"}>📧 Email</button>
            </div>
          </div>

          {activeRepLocked && (
            <div style={{ padding: "8px 20px", background: "var(--rf-sidebar)", borderBottom: "1px solid var(--rf-surface)", color: "var(--rf-text-soft)", fontSize: 12 }}>
              This takeoff is part of a sent bid and is read only. The preview below is calculated with current rates. The job screen shows the figures that were sent.
            </div>
          )}
          <div style={S.editorScroll}>
            {tab === "edit" && !activeRepLocked ? (
              repTpl?.isPdf ? (
                <PdfFormEditor report={activeRep} template={repTpl} rawBytes={repTplRawBytes} onChange={setRF} />
              ) : repTpl?.isExcelImport ? (
                /* ── Excel template: full sheet with embedded inputs ── */
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* Compact meta bar */}
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "10px 20px", background: "var(--rf-sidebar)", borderBottom: "1px solid var(--rf-surface)" }}>
                    <input style={{ ...S.inp, maxWidth: 220, padding: "6px 10px", fontSize: 13 }} placeholder="Report title…" value={activeRep.reportTitle} onChange={e => setRF("reportTitle", e.target.value)} />
                    <input style={{ ...S.inp, maxWidth: 160, padding: "6px 10px", fontSize: 13 }} placeholder="Author…" value={activeRep._author} onChange={e => setRF("_author", e.target.value)} />
                    <input type="date" style={{ ...S.inp, maxWidth: 160, padding: "6px 10px", fontSize: 13 }} value={activeRep._date} onChange={e => setRF("_date", e.target.value)} />
                    <select style={{ ...S.inp, maxWidth: 180, padding: "6px 10px", fontSize: 13 }} value={activeRep.folderId || ""} onChange={e => setRF("folderId", e.target.value || null)}>
                      <option value="">— No Folder —</option>
                      {repFolders.map(f => <option key={f.id} value={f.id}>📁 {f.name}</option>)}
                    </select>
                  </div>
                  {repTpl.isMultiSheet ? (
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                      {/* Sheet tabs */}
                      <div style={{ display: "flex", background: "var(--rf-bg)", borderBottom: "1px solid var(--rf-surface)", flexShrink: 0, overflowX: "auto" }}>
                        {(repTpl.sheets || []).map((sheet, si) => (
                          <button key={sheet.name} onClick={() => setActiveSheetIdx(si)} style={{ padding: "9px 18px", background: "transparent", color: activeSheetIdx === si ? "var(--rf-accent)" : "var(--rf-muted)", border: "none", borderBottom: activeSheetIdx === si ? "2px solid var(--rf-primary)" : "2px solid transparent", cursor: "pointer", fontSize: 12, fontWeight: activeSheetIdx === si ? 700 : 400, whiteSpace: "nowrap", outline: "none" }}>
                            {sheet.name}
                          </button>
                        ))}
                      </div>
                      {/* Active sheet */}
                      {(() => {
                        const sheet = (repTpl.sheets || [])[activeSheetIdx];
                        if (!sheet) return null;
                        return (
                          <div style={{ overflowY: "auto", flex: 1 }}>
                            <ExcelSheetEditor
                              report={activeRep}
                              template={{ ...sheet, isExcelImport: true, id: repTpl.id }}
                              rawBytes={repTplRawBytes}
                              onChange={(fieldId, val) => setRF(fieldId, val)}
                            />
                            {(chartsBySheet[sheet.name] || []).map((cfg, ci) => (
                              <ExcelChartPanel key={ci} config={cfg} title={cfg.options?.plugins?.title?.text || `Chart ${ci + 1}`} />
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      <ExcelSheetEditor
                        report={activeRep}
                        template={repTpl}
                        rawBytes={repTplRawBytes}
                        onChange={(fieldId, val) => setRF(fieldId, val)}
                        cellOverrides={undefined}
                      />
                      {Object.values(chartsBySheet).flat().map((cfg, ci) => (
                        <ExcelChartPanel key={ci} config={cfg} title={cfg.options?.plugins?.title?.text || `Chart ${ci + 1}`} />
                      ))}
                      {repTpl?.isProctor && (() => {
                        const trials = computeTrials(activeRep, repTpl);
                        const valid = trials.filter(Boolean);
                        if (valid.length < 2) return (
                          <div style={{ textAlign: "center", color: "#4b5563", fontSize: 13, fontStyle: "italic", padding: "28px 20px" }}>
                            📈 Proctor curve will appear here once you enter at least 2 trial data points
                          </div>
                        );
                        return <ProctorChart ref={chartRef} trials={trials} />;
                      })()}
                    </div>
                  )}
                </div>
              ) : (
              <div style={S.editorInner}>
                <div style={S.metaBox}>
                  {repTpl?.name && <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-accent)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>{repTpl.name}</div>}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Fld label="Report Title"><input style={{ ...S.inp, fontWeight: 600, fontSize: 16 }} placeholder="Enter report title…" value={activeRep.reportTitle} onChange={e => setRF("reportTitle", e.target.value)} /></Fld>
                    <Fld label="Author"><input style={S.inp} placeholder="Your name" value={activeRep._author} onChange={e => setRF("_author", e.target.value)} /></Fld>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <Fld label="Date" noMb><input type="date" style={{ ...S.inp, maxWidth: 200 }} value={activeRep._date} onChange={e => setRF("_date", e.target.value)} /></Fld>
                    <Fld label="Folder" noMb>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select style={{ ...S.inp, width: "auto", minWidth: 180 }} value={activeRep.folderId || ""} onChange={e => setRF("folderId", e.target.value || null)}>
                          <option value="">— No Folder —</option>
                          {repFolders.map(f => <option key={f.id} value={f.id}>📁 {f.name}</option>)}
                        </select>
                        <button style={S.btnGhost} onClick={async () => { const name = window.prompt("New folder name:"); if (!name?.trim()) return; const { data } = await dbCreateFolder(currentProject.id, "rep", name.trim()); if (data) { setRepFolders(f => [...f, data]); setRF("folderId", data.id); } }}>+ New Folder</button>
                      </div>
                    </Fld>
                  </div>
                </div>
                {(repTpl?.sections || []).map((sec, si) => (
                  <div key={sec.id} style={S.secBox}>
                    <div style={S.secBoxHead}>
                      <div style={S.secPill}>{si + 1}</div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--rf-text-heading)" }}>{sec.name}</div>
                    </div>
                    <div style={{ marginTop: 14 }}>
                      {sec.fields.length === 0 && <div style={{ fontSize: 12, color: "#4b5563", fontStyle: "italic" }}>No fields in this section.</div>}
                      {repTpl?.isProctor && sec.name === "Compaction Data" && (
                        <Fld label="Mold Weight (g) — apply to all trials">
                          <input type="number" style={{ ...S.inp, maxWidth: 160 }} placeholder="e.g. 4255" value={activeRep._sharedMold || ""}
                            onChange={e => { const val = e.target.value; setActiveRep(r => ({ ...r, _sharedMold: val, t1_mold: val, t2_mold: val, t3_mold: val, t4_mold: val })); }} />
                        </Fld>
                      )}
                      {/* Compact 2-column grid for fields */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px 20px" }}>
                        {sec.fields.map(f => (
                          <div key={f.id} style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", gap: "0 10px", borderBottom: "1px solid var(--rf-surface)", paddingBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>{f.label || "Field"}</div>
                            <div>
                              {f.type === "checkbox"
                                ? <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                    <input type="checkbox" checked={activeRep[f.id] === true || activeRep[f.id] === "true"} onChange={e => setRF(f.id, e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                                    <span style={{ fontSize: 12, color: "var(--rf-text-soft)" }}>{activeRep[f.id] === true || activeRep[f.id] === "true" ? "Yes" : "No"}</span>
                                  </label>
                                : f.type === "textarea"
                                ? <textarea rows={2} style={{ ...S.inp, resize: "vertical", padding: "5px 8px", fontSize: 13 }} value={activeRep[f.id] || ""} onChange={e => setRF(f.id, e.target.value)} />
                                : f.type === "select"
                                  ? <select style={{ ...S.inp, padding: "5px 8px", fontSize: 13 }} value={activeRep[f.id] || ""} onChange={e => setRF(f.id, e.target.value)}>
                                      <option value="">— Select —</option>
                                      {(f.options || "").split(",").map(o => o.trim()).filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                  : f.type === "image"
                                    ? <div>
                                        {activeRep[f.id] && (
                                          <div style={{ marginBottom: 6 }}>
                                            <img src={activeRep[f.id]} alt={f.label} style={{ maxWidth: "100%", maxHeight: 120, borderRadius: 4, border: "1px solid var(--rf-text-faint)", objectFit: "contain" }} />
                                            <button onClick={() => setRF(f.id, "")} style={{ display: "block", marginTop: 4, fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Remove</button>
                                          </div>
                                        )}
                                        <input type="file" accept="image/*" style={{ fontSize: 12, color: "var(--rf-text-soft)" }} onChange={e => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const reader = new FileReader();
                                          reader.onload = ev => setRF(f.id, ev.target.result);
                                          reader.readAsDataURL(file);
                                          e.target.value = "";
                                        }} />
                                      </div>
                                    : <input type={f.type} style={{ ...S.inp, padding: "5px 8px", fontSize: 13 }} placeholder={f.defaultValue || ""} value={activeRep[f.id] || ""} onChange={e => setRF(f.id, e.target.value)} />
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                {repTpl?.isProctor && (() => {
                  const trials = computeTrials(activeRep, repTpl);
                  const valid = trials.filter(Boolean);
                  if (valid.length < 2) return (
                    <div style={{ ...S.secBox, textAlign: "center", color: "#4b5563", fontSize: 13, fontStyle: "italic", padding: "28px 20px" }}>
                      📈 Proctor curve will appear here once you enter at least 2 trial data points
                    </div>
                  );
                  return <ProctorChart ref={chartRef} trials={trials} />;
                })()}
              </div>
              )
            ) : (
              <div style={{ maxWidth: repTpl?.isProctor ? 920 : (repTpl?.isExcelImport || repTpl?.isPdf) ? "100%" : 720, margin: "0 auto", padding: (repTpl?.isExcelImport || repTpl?.isPdf) ? "0" : "0 24px 40px" }}>
                {repTpl?.isProctor ? (
                  <ProctorPreview report={activeRep} template={repTpl} rawBytes={repTplRawBytes} />
                ) : repTpl?.isPdf ? (
                  <PdfFormEditor report={activeRep} template={repTpl} rawBytes={repTplRawBytes} onChange={setRF} readOnly={true} />
                ) : repTpl?.isExcelImport ? (
                  repTpl.isMultiSheet ? (
                    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      {/* Sheet tabs */}
                      <div style={{ display: "flex", background: "var(--rf-bg)", borderBottom: "1px solid var(--rf-surface)", flexShrink: 0, overflowX: "auto" }}>
                        {(repTpl.sheets || []).map((sheet, si) => (
                          <button key={sheet.name} onClick={() => setActiveSheetIdx(si)} style={{ padding: "9px 18px", background: "transparent", color: activeSheetIdx === si ? "var(--rf-accent)" : "var(--rf-muted)", border: "none", borderBottom: activeSheetIdx === si ? "2px solid var(--rf-primary)" : "2px solid transparent", cursor: "pointer", fontSize: 12, fontWeight: activeSheetIdx === si ? 700 : 400, whiteSpace: "nowrap", outline: "none" }}>
                            {sheet.name}
                          </button>
                        ))}
                      </div>
                      {/* Active sheet */}
                      {(() => {
                        const sheet = (repTpl.sheets || [])[activeSheetIdx];
                        if (!sheet) return null;
                        return (
                          <div style={{ overflowY: "auto", flex: 1 }}>
                            <ExcelSheetPreview
                              report={activeRep}
                              template={{ ...sheet, isExcelImport: true, id: repTpl.id }}
                              getRawBytes={getRawBytesForTemplate}
                            />
                            {(chartsBySheet[sheet.name] || []).map((cfg, ci) => (
                              <ExcelChartPanel key={ci} config={cfg} title={cfg.options?.plugins?.title?.text || `Chart ${ci + 1}`} />
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div>
                      <ExcelSheetPreview report={activeRep} template={repTpl} getRawBytes={getRawBytesForTemplate} />
                      {Object.values(chartsBySheet).flat().map((cfg, ci) => (
                        <ExcelChartPanel key={ci} config={cfg} title={cfg.options?.plugins?.title?.text || `Chart ${ci + 1}`} />
                      ))}
                    </div>
                  )
                ) : (
                  <div style={{ background: "#fff", maxWidth: 900, margin: "0 auto", fontFamily: "Arial, sans-serif", fontSize: 12, color: "#111", boxShadow: "0 2px 16px rgba(0,0,0,0.18)", borderRadius: 4, overflow: "hidden" }}>
                    {/* Title bar */}
                    <div style={{ background: "#282828", color: "#fff", fontWeight: 700, fontSize: 13, padding: "8px 14px" }}>
                      {activeRep.reportTitle || <span style={{ fontStyle: "italic", fontWeight: 400, opacity: 0.6 }}>Untitled Report</span>}
                    </div>
                    {/* Meta row */}
                    {(repTpl?.name || activeRep._author || activeRep._date) && (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                          <tr>
                            {repTpl?.name && <><td style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "12%" }}>Template:</td><td style={{ background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "38%" }}>{repTpl.name}</td></>}
                            {activeRep._author && <><td style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "12%" }}>By:</td><td style={{ background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "38%" }}>{activeRep._author}</td></>}
                            {activeRep._date && <><td style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "12%" }}>Date:</td><td style={{ background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "38%" }}>{activeRep._date}</td></>}
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {/* Sections */}
                    <div style={{ padding: "0 12px 10px" }}>
                      {(repTpl?.sections || []).map(sec => {
                        const filledFields = sec.fields.filter(f => activeRep[f.id] || activeRep[f.id] === 0);
                        if (!filledFields.length) return null;
                        const imageFields = filledFields.filter(f => f.type === "image");
                        const textFields = filledFields.filter(f => f.type !== "image");
                        // Pair text fields into rows of 2
                        const rows = [];
                        for (let i = 0; i < textFields.length; i += 2) rows.push(textFields.slice(i, i + 2));
                        return (
                          <div key={sec.id} style={{ marginTop: 10 }}>
                            <div style={{ background: "#373737", color: "#fff", fontWeight: 700, fontSize: 11, textAlign: "center", padding: "5px 0", letterSpacing: 0.5 }}>{sec.name.toUpperCase()}</div>
                            {rows.length > 0 && (
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                  {rows.map((pair, ri) => (
                                    <tr key={ri}>
                                      <td style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "20%" }}>{pair[0].label}:</td>
                                      <td style={{ background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "30%", whiteSpace: "pre-wrap" }}>{activeRep[pair[0].id]}</td>
                                      {pair[1] ? <>
                                        <td style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "20%" }}>{pair[1].label}:</td>
                                        <td style={{ background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb", width: "30%", whiteSpace: "pre-wrap" }}>{activeRep[pair[1].id]}</td>
                                      </> : <><td style={{ border: "1px solid #bbb" }} /><td style={{ border: "1px solid #bbb" }} /></>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {imageFields.map(f => (
                              <div key={f.id} style={{ border: "1px solid #bbb", borderTop: "none" }}>
                                <div style={{ background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", borderBottom: "1px solid #bbb" }}>{f.label}:</div>
                                <div style={{ padding: 8 }}><img src={activeRep[f.id]} alt={f.label} style={{ maxWidth: "100%", maxHeight: 260, objectFit: "contain", display: "block" }} /></div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      {!activeRep.reportTitle && !(repTpl?.sections || []).some(s => s.fields.some(f => activeRep[f.id])) && (
                        <div style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 13, textAlign: "center", padding: "24px 0" }}>Fill in the editor to see your report preview here.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {currentUser && jobsEnabled && screen === "jobScreen" && openingJobId && (
        <div style={S.editorWrap}>
          <div style={{ padding: "24px 32px" }}>
            {/* Fix round 5: shown until the queued switch to this job runs, so
                the previous job's header and lines are never on screen. Back
                cancels the pending switch. */}
            <button onClick={() => { setOpeningJobId(null); setScreen("home"); setHomeTab("jobs"); }} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-text-soft)", fontSize: 12, padding: "6px 12px", cursor: "pointer", marginBottom: 16 }}>
              ← Back
            </button>
            <div style={{ fontSize: 14, color: "var(--rf-muted)" }}>Loading job...</div>
          </div>
        </div>
      )}

      {currentUser && jobsEnabled && screen === "jobScreen" && !openingJobId && activeJob && (
        <div style={S.editorWrap}>
          <div style={{ padding: "24px 32px" }}>
            {ratesDirtyJobsBanner}
            <JobScreen
              job={activeJob}
              lineDetails={jobLineDetails}
              unitTypeTemplates={templates.filter(isUnitTypeTemplate)}
              customTemplate={templates.find(isCustomLineTemplate)}
              canEdit={isAdmin || myRole === "manager"}
              markupError={jobMarkupError}
              onChangeField={changeJobField}
              onChangeMarkup={changeJobMarkup}
              onAddLine={addJobLine}
              onOpenLine={openJobLine}
              onChangeQuantity={changeJobLineQuantity}
              onSave={saveJob}
              onBack={() => { setScreen("home"); setHomeTab("jobs"); }}
              onMarkSent={markJobSent}
              onRevise={reviseJob}
              busyAction={jobActionBusy}
              onGenerateProposal={generateJobProposal}
              onDownloadSentProposal={downloadSentProposal}
              onChangeLineDescription={changeJobLineDescription}
              vocabulary={vocabulary}
            />
          </div>
        </div>
      )}

      {/* ── User Management Modal ─────────────────────────────────────────── */}
      {currentUser && showUserMgmt && isAdmin && (
        <div style={S.modalOverlay} onClick={() => setShowUserMgmt(false)}>
          <div style={{ ...S.modalBox, maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>👥 Project Members</div>
                <div style={S.modalSub}>Manage members of {currentProject?.name}</div>
              </div>
              <button onClick={() => setShowUserMgmt(false)} style={S.iconX}>×</button>
            </div>

            {/* Project members */}
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Members</div>
              {projectMembers.map(m => {
                const displayName = m.profiles?.full_name || m.profiles?.email || m.user_id;
                const isMe = m.user_id === currentUser.id;
                return (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--rf-bg)", borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, background: m.role === "admin" ? "linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))" : m.role === "manager" ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,var(--rf-muted),var(--rf-text-dim))", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "white", flexShrink: 0 }}>
                      {(displayName?.[0] || "?").toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--rf-text-heading)" }}>{displayName}</div>
                      {isMe && <div style={{ fontSize: 10, color: "var(--rf-primary)" }}>You</div>}
                    </div>
                    <select
                      style={{ background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-text)", fontSize: 12, padding: "4px 8px", cursor: "pointer" }}
                      value={m.role}
                      onChange={async e => await changeRoleHandler(m.user_id, e.target.value)}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="base">Base</option>
                    </select>
                    {!isMe && <button onClick={async () => await deleteUserHandler(m.user_id)} style={{ ...S.btnRed, padding: "4px 10px", fontSize: 12 }}>Remove</button>}
                  </div>
                );
              })}
            </div>

            {/* Add new user to project */}
            <div style={{ borderTop: "1px solid var(--rf-surface)", paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Add New User</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)", marginBottom: 4 }}>Email</div>
                  <input style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "7px 10px", boxSizing: "border-box" }} placeholder="user@example.com" value={newUserForm.email} onChange={e => setNewUserForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)", marginBottom: 4 }}>Full Name</div>
                  <input style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "7px 10px", boxSizing: "border-box" }} placeholder="Full name" value={newUserForm.fullName} onChange={e => setNewUserForm(f => ({ ...f, fullName: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)", marginBottom: 4 }}>Password</div>
                  <input type="password" style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "7px 10px", boxSizing: "border-box" }} placeholder="password" value={newUserForm.password} onChange={e => setNewUserForm(f => ({ ...f, password: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--rf-muted)", marginBottom: 4 }}>Role</div>
                  <select style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "7px 10px", cursor: "pointer", boxSizing: "border-box" }} value={newUserForm.role} onChange={e => setNewUserForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="base">Base</option>
                  </select>
                </div>
              </div>
              {userMgmtError && <div style={{ color: "var(--rf-danger)", fontSize: 12, marginTop: 8 }}>{userMgmtError}</div>}
              <button onClick={async () => await createUserHandler()} disabled={creatingUser} style={{ ...S.btnPrimary, marginTop: 12, opacity: creatingUser ? 0.6 : 1 }}>{creatingUser ? "Creating…" : "+ Add User"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Super Admin Panel ─────────────────────────────────────────────── */}
      {currentUser && showSuperAdmin && isSuperAdmin && (
        <div style={S.modalOverlay} onClick={() => setShowSuperAdmin(false)}>
          <div style={{ ...S.modalBox, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>⚙ Super Admin Panel</div>
                <div style={S.modalSub}>Manage all projects and users</div>
              </div>
              <button onClick={() => setShowSuperAdmin(false)} style={S.iconX}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* Projects */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Projects</div>
                <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
                  {superAdminProjects.map(p => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--rf-bg)", borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ flex: 1, fontSize: 13, color: "var(--rf-text)", fontWeight: 600 }}>{p.name}</div>
                      <button onClick={async () => { await deleteProject(p.id); setSuperAdminProjects(prev => prev.filter(x => x.id !== p.id)); }} style={{ ...S.btnRed, padding: "3px 8px", fontSize: 11 }}>Delete</button>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ flex: 1, background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text-heading)", fontSize: 13, padding: "6px 10px" }} placeholder="New project name…" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} />
                  <button onClick={async () => { if (!newProjectName.trim()) return; const { data } = await createProject(newProjectName.trim()); if (data) setSuperAdminProjects(prev => [...prev, data]); setNewProjectName(""); }} style={{ ...S.btnPrimary, padding: "6px 12px" }}>+ Create</button>
                </div>
              </div>
              {/* Users */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rf-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>All Users</div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {superAdminUsers.map(u => (
                    <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--rf-bg)", borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: "var(--rf-text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.full_name || u.email}</div>
                        <div style={{ fontSize: 10, color: "var(--rf-text-dim)" }}>{u.email}</div>
                      </div>
                      <button onClick={async () => { await setSuperAdmin(u.id, !u.is_super_admin); setSuperAdminUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_super_admin: !x.is_super_admin } : x)); }} style={{ padding: "3px 8px", background: u.is_super_admin ? "var(--rf-primary)" : "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 5, color: "var(--rf-text)", fontSize: 10, cursor: "pointer" }}>
                        {u.is_super_admin ? "Super Admin" : "Make SA"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Manager Modal ────────────────────────────────────────────── */}
      {currentUser && showTabManager && isAdmin && (
        <TabManagerModal
          currentProject={currentProject}
          vocabulary={vocabulary}
          jobsEnabled={jobsEnabled}
          onClose={() => setShowTabManager(false)}
          onSave={async (enabledModules) => {
            const newConfig = { ...(currentProject.config || {}), enabledModules };
            const { error } = await updateProjectConfig(currentProject.id, newConfig);
            if (error) { alert("Could not save tab settings: " + error.message); return; }
            const updated = { ...currentProject, config: newConfig };
            setCurrentProject(updated);
            setProjects(prev => prev.map(p => p.id === currentProject.id ? updated : p));
            setShowTabManager(false);
          }}
        />
      )}

      {/* ── Excel Import Modal ───────────────────────────────────────────── */}
      {currentUser && importModal && (
        <div style={S.modalOverlay} onClick={() => setImportModal(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>📥 Import Excel Template</div>
                <div style={S.modalSub}>{importModal.fileName} — {(importModal.visibleNames || importModal.templates).length} sheet{(importModal.visibleNames || importModal.templates).length !== 1 ? "s" : ""} detected</div>
              </div>
              <button onClick={() => setImportModal(null)} style={S.iconX}>×</button>
            </div>
            <div style={{ overflowY: "auto", maxHeight: "55vh" }}>
              {importModal.templates.map(tpl => (
                <div key={tpl.id} style={S.importCard}>
                  <div style={S.importCardLeft}>
                    <div style={S.importCardTitle}>{tpl.name}</div>
                    <div style={S.importCardMeta}>
                      {tpl.sections.length} section{tpl.sections.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
                      {tpl.sections.reduce((a, s) => a + s.fields.length, 0)} fields detected
                    </div>
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                      {tpl.sections.map(sec => (
                        <div key={sec.id} style={S.importSecPreview}>
                          <span style={S.importSecName}>{sec.name}</span>
                          <span style={S.importSecFields}>
                            {sec.fields.slice(0, 5).map(f => f.label).join(" · ")}
                            {sec.fields.length > 5 ? ` +${sec.fields.length - 5} more` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={async () => await confirmImport(tpl)} style={{ ...S.btnGreen, alignSelf: "flex-start", whiteSpace: "nowrap" }}>
                    Import This Sheet
                  </button>
                </div>
              ))}
            </div>
            {importModal.templates.length > 1 && (
              <div style={{ padding: "16px 0 0", borderTop: "1px solid var(--rf-surface)", marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={confirmImportMultiSheet} style={{ ...S.btnGreen, width: "100%" }}>
                  📋 Import All Sheets as One Multi-Sheet Template
                </button>
                <button onClick={confirmImportAll} style={{ ...S.btnPrimary, width: "100%" }}>
                  Import All Sheets as Separate Templates
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Send Email Modal ─────────────────────────────────────────────── */}
      {sendEmailModal && (
        <div style={S.modalOverlay} onClick={() => !sendEmailModal.sending && setSendEmailModal(null)}>
          <div style={{ ...S.modalBox, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>📧 Send Report</div>
                <div style={S.modalSub}>{gmailEmail ? `Sending from ${gmailEmail}` : "Connect Gmail in the sidebar to send emails"}</div>
              </div>
              <button onClick={() => setSendEmailModal(null)} style={S.iconX} disabled={sendEmailModal.sending}>×</button>
            </div>

            {!gmailEmail ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ fontSize: 13, color: "var(--rf-text-soft)", marginBottom: 16 }}>You need to connect your Gmail account first.</div>
                <button onClick={connectGmail} style={S.btnPrimary}>Connect Gmail</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Fld label="To *" noMb>
                  <input value={sendEmailModal.to} onChange={e => setSendEmailModal(m => ({ ...m, to: e.target.value }))}
                    placeholder="recipient@example.com (comma-separate multiple)"
                    style={{ ...S.inp, width: "100%" }} disabled={sendEmailModal.sending} />
                </Fld>
                <Fld label="CC" noMb>
                  <input value={sendEmailModal.cc} onChange={e => setSendEmailModal(m => ({ ...m, cc: e.target.value }))}
                    placeholder="optional"
                    style={{ ...S.inp, width: "100%" }} disabled={sendEmailModal.sending} />
                </Fld>
                <Fld label="Subject *" noMb>
                  <input value={sendEmailModal.subject} onChange={e => setSendEmailModal(m => ({ ...m, subject: e.target.value }))}
                    style={{ ...S.inp, width: "100%" }} disabled={sendEmailModal.sending} />
                </Fld>
                <Fld label="Message" noMb>
                  <textarea value={sendEmailModal.message} onChange={e => setSendEmailModal(m => ({ ...m, message: e.target.value }))}
                    rows={4} placeholder="Optional message body…"
                    style={{ ...S.inp, width: "100%", resize: "vertical" }} disabled={sendEmailModal.sending} />
                </Fld>
                {repTpl?.isPdf && <div style={{ fontSize: 11, color: "var(--rf-muted)" }}>📎 The filled PDF will be attached automatically.</div>}
                {sendEmailModal.error && <div style={{ color: "var(--rf-danger)", fontSize: 13 }}>{sendEmailModal.error}</div>}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                  <button onClick={() => setSendEmailModal(null)} style={S.btnGhost} disabled={sendEmailModal.sending}>Cancel</button>
                  <button
                    disabled={sendEmailModal.sending || !sendEmailModal.to.trim() || !sendEmailModal.subject.trim()}
                    onClick={async () => {
                      setSendEmailModal(m => ({ ...m, sending: true, error: null }));
                      try {
                        await sendEmailReport({
                          to:      sendEmailModal.to,
                          cc:      sendEmailModal.cc,
                          subject: sendEmailModal.subject,
                          message: sendEmailModal.message,
                        });
                        setSendEmailModal(null);
                        doFlash();
                      } catch (err) {
                        setSendEmailModal(m => ({ ...m, sending: false, error: err.message }));
                      }
                    }}
                    style={{ ...S.btnPrimary, opacity: (sendEmailModal.sending || !sendEmailModal.to.trim() || !sendEmailModal.subject.trim()) ? 0.5 : 1 }}>
                    {sendEmailModal.sending ? "Sending…" : "Send Email"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, children, noMb }) {
  return (
    <div style={{ marginBottom: noMb ? 0 : 16 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
function Empty({ icon, text }) {
  return (
    <div style={{ textAlign: "center", padding: "36px 20px", color: "#4b5563", fontSize: 14, background: "var(--rf-surface)", borderRadius: 12, border: "1.5px dashed var(--rf-border)", marginBottom: 16 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>{text}
    </div>
  );
}

const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:var(--rf-bg, #0f172a);font-family:'DM Sans',sans-serif}
  input:focus,textarea:focus,select:focus{outline:none;border-color:var(--rf-primary, #6366f1)!important}
  ::-webkit-scrollbar{width:6px;height:6px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--rf-border, #1e3a5f);border-radius:3px}
  button{font-family:'DM Sans',sans-serif;transition:opacity .15s}button:hover{opacity:.88}
`;

const styles = {
  root:{minHeight:"100vh",background:"var(--rf-bg)",color:"var(--rf-text)"},
  homeWrap:{display:"flex",height:"100vh",overflow:"hidden"},
  sidebar:{width:230,background:"var(--rf-sidebar)",borderRight:"1px solid var(--rf-surface)",padding:"24px 0 16px",position:"sticky",top:0,height:"100vh",display:"flex",flexDirection:"column"},
  logoArea:{display:"flex",alignItems:"center",gap:10,padding:"0 16px 20px",borderBottom:"1px solid var(--rf-surface)",marginBottom:8},
  logoBadge:{width:36,height:36,background:"linear-gradient(135deg,var(--rf-primary),var(--rf-primary-soft))",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"white",flexShrink:0},
  logoName:{fontFamily:"'Playfair Display',serif",fontSize:16,color:"var(--rf-text-heading-alt)"},
  logoTagline:{fontSize:10,color:"var(--rf-text-faint)",marginTop:1},
  navLink:{padding:"10px 20px",fontSize:13,color:"var(--rf-muted)",cursor:"pointer"},
  homeMain:{flex:1,padding:"36px 40px",overflowY:"auto"},
  secBar:{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20},
  secTitle:{fontFamily:"'Playfair Display',serif",fontSize:22,color:"var(--rf-text-heading)",marginBottom:4},
  secDesc:{fontSize:13,color:"var(--rf-text-dim)"},
  grid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14},
  card:{background:"var(--rf-surface)",borderRadius:12,border:"1px solid var(--rf-border)",overflow:"visible"},
  cardStripe:{height:4,borderRadius:"12px 12px 0 0"},
  cardBody:{padding:"16px 18px"},
  cardTitle:{fontSize:15,fontWeight:600,color:"var(--rf-text-heading)",marginBottom:4},
  cardDesc:{fontSize:12,color:"var(--rf-muted)",marginBottom:4},
  cardMeta:{fontSize:11,color:"var(--rf-text-faint)"},
  cardActions:{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"},
  editorWrap:{display:"flex",flexDirection:"column",height:"100vh"},
  topBar:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 24px",background:"var(--rf-sidebar)",borderBottom:"1px solid var(--rf-surface)",gap:12},
  topBarTitle:{fontFamily:"'Playfair Display',serif",fontSize:17,color:"var(--rf-text-heading)"},
  editorScroll:{flex:1,overflowY:"auto",padding:"28px 24px"},
  editorInner:{maxWidth:760,margin:"0 auto"},
  metaBox:{background:"var(--rf-surface)",border:"1px solid var(--rf-border)",borderRadius:12,padding:22,marginBottom:20},
  secBox:{background:"var(--rf-surface)",border:"1px solid var(--rf-border)",borderRadius:12,padding:20,marginBottom:14},
  secBoxHead:{display:"flex",alignItems:"center",gap:10},
  secPill:{width:24,height:24,background:"var(--rf-primary)",borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"white",flexShrink:0},
  fieldBox:{background:"var(--rf-bg)",border:"1px solid var(--rf-surface)",borderRadius:8,padding:"12px 14px",marginBottom:10,display:"flex",gap:10,alignItems:"flex-start",flexWrap:"wrap"},
  fieldNum:{width:20,height:20,background:"var(--rf-surface)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,color:"var(--rf-text-dim)",marginTop:22,flexShrink:0},
  inp:{width:"100%",background:"var(--rf-bg)",border:"1px solid var(--rf-border)",borderRadius:8,color:"var(--rf-text-heading)",fontFamily:"'DM Sans',sans-serif",fontSize:14,padding:"9px 13px"},
  iconX:{width:30,height:30,background:"transparent",border:"none",color:"#374151",cursor:"pointer",fontSize:20,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0},
  btnPrimary:{padding:"8px 20px",background:"var(--rf-primary)",color:"white",border:"none",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer"},
  btnGreen:{padding:"7px 14px",background:"#10b981",color:"white",border:"none",borderRadius:7,fontSize:12,fontWeight:500,cursor:"pointer"},
  btnGhost:{padding:"7px 14px",background:"transparent",color:"var(--rf-text-soft)",border:"1px solid var(--rf-border)",borderRadius:7,fontSize:12,fontWeight:500,cursor:"pointer"},
  btnRed:{padding:"7px 14px",background:"transparent",color:"var(--rf-danger)",border:"1px solid var(--rf-surface)",borderRadius:7,fontSize:12,fontWeight:500,cursor:"pointer"},
  btnBack:{padding:"7px 14px",background:"transparent",color:"var(--rf-muted)",border:"1px solid var(--rf-border)",borderRadius:7,fontSize:13,cursor:"pointer"},
  btnXlsx:{padding:"7px 16px",background:"#166534",color:"#86efac",border:"1px solid #14532d",borderRadius:7,fontSize:13,fontWeight:500,cursor:"pointer"},
  btnAddField:{padding:"7px 14px",background:"transparent",border:"1px dashed var(--rf-border)",borderRadius:7,color:"var(--rf-primary)",fontSize:12,fontWeight:500,cursor:"pointer",marginTop:8},
  btnAddSection:{width:"100%",padding:12,background:"transparent",border:"1.5px dashed var(--rf-border)",borderRadius:10,color:"var(--rf-text-faint)",fontSize:13,fontWeight:500,cursor:"pointer",marginTop:4},
  tabBtn:{padding:"7px 16px",borderRadius:6,fontSize:13,fontWeight:500,cursor:"pointer",border:"none",background:"transparent",color:"var(--rf-muted)"},
  tabBtnOn:{background:"var(--rf-bg)",color:"var(--rf-text-heading)"},
  dropdown:{position:"absolute",bottom:"calc(100% + 4px)",left:0,background:"var(--rf-surface)",border:"1px solid var(--rf-text-faint)",borderRadius:8,zIndex:1000,minWidth:160,boxShadow:"0 -4px 24px rgba(0,0,0,.5)"},
  dropItem:{padding:"10px 14px",fontSize:13,color:"var(--rf-text)",cursor:"pointer",borderRadius:4},
  prevCard:{background:"white",borderRadius:"0 0 12px 12px",padding:"48px 52px",color:"#111827",boxShadow:"0 20px 60px rgba(0,0,0,.5)"},
  prevTitle:{fontFamily:"'Playfair Display',serif",fontSize:30,color:"#111827",lineHeight:1.2,marginBottom:8},
  prevSecHead:{display:"flex",alignItems:"center",gap:10,background:"#f3f4f6",padding:"8px 14px",borderRadius:8,marginBottom:14,borderLeft:"3px solid #6366f1"},
  prevSecNum:{width:22,height:22,background:"#111827",borderRadius:4,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"white",flexShrink:0},
  prevSecTitle:{fontSize:14,fontWeight:700,color:"#111827"},
  prevFldLabel:{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,color:"#9ca3af",marginBottom:4},
  prevFldVal:{fontSize:14,color:"#374151",lineHeight:1.6,whiteSpace:"pre-wrap"},
  reportRow:{display:"flex",gap:0,background:"var(--rf-surface)",border:"1px solid var(--rf-border)",borderRadius:12,overflow:"visible",position:"relative",minHeight:140},
  reportRowAccent:{width:4,background:"linear-gradient(180deg,#10b981,#059669)",borderRadius:"12px 0 0 12px",flexShrink:0},
  reportLeft:{width:260,flexShrink:0,padding:"14px 16px"},
  reportRowTitle:{fontSize:15,fontWeight:700,color:"var(--rf-text-heading)",lineHeight:1.3,marginRight:10},
  reportRowBadge:{display:"inline-block",background:"var(--rf-border)",color:"var(--rf-accent)",padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:600,marginBottom:8,marginTop:6},
  reportTitleRow:{display:"flex",alignItems:"baseline",flexWrap:"wrap",gap:6,marginBottom:2},
  reportInlineMeta:{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"},
  reportMetaChip:{fontSize:11,color:"var(--rf-muted)",whiteSpace:"nowrap"},
  reportMetaItem:{display:"flex",flexDirection:"column",marginBottom:6},
  reportMetaLabel:{fontSize:10,fontWeight:700,color:"var(--rf-text-dim)",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:1},
  reportMetaVal:{fontSize:13,color:"var(--rf-text-soft)"},
  reportRowMeta:{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",fontSize:12,color:"var(--rf-muted)"},
  reportDivider:{width:1,background:"var(--rf-bg)",flexShrink:0,margin:"14px 0"},
  reportRight:{flex:1,padding:"18px 20px",minWidth:0},
  reportFieldsGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:"12px 20px"},
  inlineSection:{padding:"10px 18px 14px",borderTop:"1px solid var(--rf-bg)"},
  inlineSectionLabel:{display:"flex",alignItems:"center",gap:8,fontSize:11,fontWeight:700,color:"var(--rf-muted)",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:10},
  inlineSectionNum:{width:18,height:18,background:"#10b981",borderRadius:3,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"white",flexShrink:0},
  inlineFieldGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"10px 20px"},
  inlineField:{minWidth:0},
  inlineFieldLabel:{fontSize:10,fontWeight:700,color:"var(--rf-text-dim)",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:3},
  inlineFieldValue:{fontSize:13,color:"var(--rf-text-softer)",lineHeight:1.5,wordBreak:"break-word",whiteSpace:"pre-wrap"},
  // Import button
  btnImport:{padding:"8px 14px",background:"var(--rf-border)",color:"#93c5fd",border:"1px solid #1e4a7f",borderRadius:8,fontSize:12,fontWeight:500,cursor:"pointer",textAlign:"center"},
  // Filing system
  fileLayout:{display:"flex",gap:20,alignItems:"flex-start"},
  folderPanel:{width:200,flexShrink:0,background:"var(--rf-sidebar)",border:"1px solid var(--rf-surface)",borderRadius:10,padding:"12px 8px",display:"flex",flexDirection:"column",gap:2,maxHeight:"calc(100vh - 180px)",overflowY:"auto"},
  folderPanelTitle:{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,color:"var(--rf-text-faint)",padding:"0 8px 8px"},
  folderItem:{display:"flex",alignItems:"center",gap:6,padding:"7px 8px",borderRadius:6,cursor:"pointer",fontSize:13,color:"var(--rf-muted)",userSelect:"none"},
  folderItemActive:{background:"var(--rf-surface)",color:"var(--rf-accent)"},
  folderIcon:{fontSize:14,flexShrink:0},
  folderCount:{marginLeft:"auto",fontSize:10,color:"var(--rf-text-faint)",background:"var(--rf-surface)",borderRadius:10,padding:"1px 6px",flexShrink:0},
  folderMenuBtn:{fontSize:11,cursor:"pointer",opacity:0.6,padding:"0 2px",flexShrink:0},
  folderEditInput:{width:"100%",background:"var(--rf-bg)",border:"1px solid var(--rf-primary)",borderRadius:5,color:"var(--rf-text-heading)",fontSize:12,padding:"4px 7px",outline:"none"},
  folderAddBtn:{margin:"6px 8px 2px",background:"transparent",border:"1px dashed var(--rf-border)",borderRadius:6,color:"var(--rf-primary)",fontSize:12,cursor:"pointer",padding:"5px 10px",textAlign:"left"},
  cardFolderBadge:{fontSize:10,color:"var(--rf-muted)",marginTop:4,marginBottom:2},
  moveDropdown:{position:"absolute",top:"calc(100% + 4px)",left:0,background:"var(--rf-surface)",border:"1px solid var(--rf-text-faint)",borderRadius:8,zIndex:1000,minWidth:160,boxShadow:"0 4px 24px rgba(0,0,0,.5)"},
  moveItem:{padding:"8px 14px",fontSize:13,color:"var(--rf-text)",cursor:"pointer",borderRadius:4},
  // Modal
  modalOverlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:24},
  modalBox:{background:"var(--rf-surface)",border:"1px solid var(--rf-border)",borderRadius:16,padding:24,width:"100%",maxWidth:640,maxHeight:"90vh",display:"flex",flexDirection:"column",gap:0},
  modalHeader:{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,marginBottom:20},
  modalTitle:{fontSize:18,fontWeight:700,color:"var(--rf-text-heading)",fontFamily:"'Playfair Display',serif"},
  modalSub:{fontSize:12,color:"var(--rf-text-dim)",marginTop:3},
  importCard:{display:"flex",gap:16,alignItems:"flex-start",background:"var(--rf-bg)",border:"1px solid var(--rf-surface)",borderRadius:10,padding:"14px 16px",marginBottom:10},
  importCardLeft:{flex:1,minWidth:0},
  importCardTitle:{fontSize:15,fontWeight:700,color:"var(--rf-text-heading)",marginBottom:4},
  importCardMeta:{fontSize:11,color:"var(--rf-text-dim)"},
  importSecPreview:{background:"var(--rf-surface)",borderRadius:6,padding:"6px 10px"},
  importSecName:{fontSize:11,fontWeight:700,color:"var(--rf-accent)",textTransform:"uppercase",letterSpacing:"0.6px",display:"block",marginBottom:2},
  importSecFields:{fontSize:11,color:"var(--rf-text-dim)",lineHeight:1.5},
};

// ── Tab Manager Modal ──────────────────────────────────────────────────────────
// Lets project admins enable or disable modules for this project.
// Disabled modules are hidden from every user (including admins) in the sidebar.
// The data for a disabled module is never deleted — re-enabling it restores it.
function TabManagerModal({ currentProject, vocabulary, jobsEnabled, onClose, onSave }) {
  const stored = currentProject?.config?.enabledModules ?? null;
  const [selected, setSelected] = useState(() =>
    stored != null ? new Set(stored) : new Set(MODULE_KEYS)
  );
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow disabling the last visible tab.
        const wouldRemain = MODULE_KEYS.filter(k => next.has(k) && k !== key);
        if (wouldRemain.length === 0) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    // If all modules are selected, save null (= default all-enabled).
    const allOn = MODULE_KEYS.every(k => selected.has(k));
    await onSave(allOn ? null : [...MODULE_KEYS.filter(k => selected.has(k))]);
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: "var(--rf-surface)", border: "1px solid var(--rf-border)", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--rf-text-heading)", fontFamily: "'Playfair Display',serif" }}>🗂 Manage Tabs</div>
            <div style={{ fontSize: 12, color: "var(--rf-text-dim)", marginTop: 3 }}>Show or hide tabs for everyone in this project.</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--rf-muted)", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          {MODULE_KEYS.map(key => {
            const meta = MODULE_META[key];
            const on = selected.has(key);
            const isLastOn = on && MODULE_KEYS.filter(k => selected.has(k)).length === 1;
            const note = key === "jobs" && !jobsEnabled
              ? "Requires estimating templates in this project"
              : meta.adminOnly ? "Visible to admins only" : null;
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--rf-bg)", borderRadius: 8, padding: "10px 14px", opacity: isLastOn ? 0.5 : 1 }}>
                <button
                  onClick={() => toggle(key)}
                  disabled={isLastOn}
                  title={isLastOn ? "At least one tab must remain enabled" : (on ? "Disable" : "Enable")}
                  style={{
                    flexShrink: 0, width: 36, height: 20, borderRadius: 10, border: "none", cursor: isLastOn ? "not-allowed" : "pointer",
                    background: on ? "var(--rf-primary)" : "var(--rf-surface)",
                    position: "relative", transition: "background 0.15s",
                  }}
                >
                  <span style={{
                    position: "absolute", top: 2, left: on ? 18 : 2, width: 16, height: 16,
                    borderRadius: "50%", background: "white", transition: "left 0.15s",
                  }} />
                </button>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{meta.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: on ? "var(--rf-text)" : "var(--rf-muted)" }}>
                    {label(key, 2, vocabulary)}
                  </div>
                  {note && <div style={{ fontSize: 10, color: "var(--rf-text-dim)", marginTop: 1 }}>{note}</div>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-muted)", fontSize: 13, padding: "8px 16px", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ background: "var(--rf-primary)", border: "none", borderRadius: 7, color: "white", fontSize: 13, fontWeight: 600, padding: "8px 16px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
